import { normalizeLoamifyBrowserMediaUrl } from './media-url';
import { resolveLoamifyClientAppHeader } from './api-client-app';
import { resolveApiBaseUrl } from './resolve-api-base-url';

export function getPublicApiBaseUrl() {
  return resolveApiBaseUrl();
}

export type OtpResendStatus = {
  nextResendAt: string | null;
  resendCount: number;
  maxResends: number;
  cooldownSeconds: number;
  lockedUntil: string | null;
  canResend: boolean;
};

export class ApiRequestError extends Error {
  status: number;
  resendStatus?: OtpResendStatus;
  code?: string;

  constructor(message: string, status: number, resendStatus?: OtpResendStatus, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.resendStatus = resendStatus;
    this.code = code;
  }
}

export function getOtpResendStatusFromError(err: unknown): OtpResendStatus | undefined {
  if (err instanceof ApiRequestError) return err.resendStatus;
  return undefined;
}

export type PrivacyPrefs = {
  isDiscoverable: boolean;
  showFollowers: boolean;
  showFollowing: boolean;
  showActivity: boolean;
  showBookmarks: boolean;
  allowMessagesFrom: 'everyone' | 'followers' | 'none';
};

export type RecommendedFollow = {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  isCreator: boolean;
  creatorId?: string;
  matchedTopics: string[];
  topics: string[];
  headline?: string | null;
  followersCount: number;
};

export type FeaturedCreator = {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  headline?: string | null;
  bio?: string | null;
  topics: string[];
  badge?: string | null;
  followersCount: number;
  featuredOrder?: number | null;
};

export type DiscoveryAdminMetrics = {
  windowDays: 7 | 30;
  generatedAt: string;
  search: {
    total: number;
    previousPeriodTotal: number;
    zeroResultCount: number;
    zeroResultRate: number;
    uniqueQueries: number;
    topQueries: Array<{ query: string; count: number; zeroResultCount: number }>;
    byType: Array<{ type: string; count: number }>;
  };
  reports: {
    created: number;
    open: number;
    byTargetType: Array<{ targetType: string; count: number }>;
    byReason: Array<{ reason: string; count: number }>;
    byStatus: Array<{ status: string; count: number }>;
  };
};

export type SearchPerson = {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  isCreator: boolean;
  creatorId?: string;
  matchedTopics: string[];
};

export type SearchArticleHit = {
  id: string;
  postId: string;
  title: string;
  slug: string | null;
  excerpt: string;
  coverUrl: string | null;
  readTimeMinutes: number;
  href: string | null;
  createdAt: string;
  author: {
    id: string;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
    isCreator: boolean;
  };
  _count: { likes: number; comments: number };
};

export type SearchResults = {
  creators: unknown[];
  members: unknown[];
  people: SearchPerson[];
  posts: unknown[];
  articles: SearchArticleHit[];
  topics: { name: string }[];
};

export type ContentPrefs = {
  mutedWords: string[];
  showArticlesTab: boolean;
  defaultArticleComments: 'everyone' | 'off';
  warnArticleCoverMissing: boolean;
};

export type ArticleRecord = {
  id: string;
  postId: string;
  title: string;
  subtitle: string | null;
  slug: string | null;
  excerpt: string;
  coverUrl: string | null;
  coverKey: string | null;
  bodyJson: unknown;
  bodyText: string;
  seoTitle: string | null;
  seoDescription: string | null;
  commentPolicy: 'everyone' | 'off';
  readTimeMinutes: number;
  topics: string[] | null;
  createdAt: string;
  updatedAt: string;
  post: {
    id: string;
    status: string;
    type: string;
    createdAt: string;
    author: unknown;
    _count?: { likes: number; comments: number; bookmarks: number; reposts: number };
  };
};

export type CommunityRecord = {
  id: string;
  creatorId: string;
  slug: string | null;
  title: string;
  about: string | null;
  rules: string | null;
  coverUrl: string | null;
  coverKey: string | null;
  logoUrl: string | null;
  logoKey: string | null;
  status: 'draft' | 'published' | 'archived';
  joinMode?: 'open' | 'invite_only';
  listingVisibility?: 'listed' | 'unlisted' | 'secret';
  inviteToken?: string | null;
  accessType?: 'free' | 'paid';
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
  currency?: string;
  membershipPolicyText?: string | null;
  membershipRefundPolicyText?: string | null;
  membershipCancellationPolicyText?: string | null;
  membershipSellerOfRecordPolicyText?: string | null;
  membershipPolicies?: CommunityMembershipPolicies;
  adultContentEnabled?: boolean;
  minimumAgeYears?: number;
  publishedAt: string | null;
  publishedById: string | null;
  createdAt: string;
  updatedAt: string;
  topic?: string | null;
  featuredAt?: string | null;
  featuredOrder?: number | null;
  acquisitionPaused?: boolean;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoCanonicalPath?: string | null;
  seoIndexable?: boolean | null;
  brandPrimaryColor?: string | null;
  brandAccentColor?: string | null;
  brandSecondaryColor?: string | null;
  memberMessagingMode?: 'owner_mod_only' | 'members_allowed';
  digestSchedule?: 'inherit' | 'daily' | 'weekly' | 'off';
  branding?: CommunityBrandTheme;
  creator?: {
    id: string;
    userId?: string;
    followersCount?: number;
    user: {
      profile: {
        username: string | null;
        displayName: string | null;
        avatarUrl: string | null;
        headline?: string | null;
        topics?: string[] | null;
      } | null;
    };
  };
  seo?: {
    title: string;
    description: string;
    imageUrl: string | null;
    canonicalPath: string | null;
    indexable?: boolean;
    structuredData?: Record<string, unknown>;
  };
  landing?: {
    pricingTeaser: {
      kind: 'free' | 'paid' | 'coming_soon';
      label: string;
      detail?: string | null;
      membershipPolicies?: CommunityMembershipPolicies | null;
      monthlyPriceCents?: number | null;
      annualPriceCents?: number | null;
      currency?: string;
      tiers?: Array<{
        id: string;
        name: string;
        billingType: 'free' | 'subscription' | 'lifetime';
        monthlyPriceCents?: number | null;
        annualPriceCents?: number | null;
        lifetimePriceCents?: number | null;
        trialDays?: number | null;
        atCapacity?: boolean;
        isDefault?: boolean;
      }>;
    };
    joinCta: {
      label: string;
      enabled: boolean;
      requiresCheckout?: boolean;
      hint?: string | null;
      acquisitionPaused?: boolean;
    };
    lockedPreview: {
      title: string;
      description: string;
      items: string[];
      stats?: {
        memberCount: number;
        postCount: number;
      };
      memberAvatars?: Array<{
        displayName: string;
        username: string | null;
        avatarUrl: string | null;
      }>;
      categorySamples?: Array<{ id: string; name: string; slug: string }>;
      spaces?: Array<{ id: string; label: string; detail?: string | null }>;
      postTeasers?: Array<{
        id: string;
        title: string;
        excerpt: string;
        locked: boolean;
        announcement?: boolean;
        pinned?: boolean;
      }>;
      pinnedTeasers?: Array<{
        id: string;
        title: string;
        excerpt: string;
        locked: boolean;
        announcement?: boolean;
        pinned?: boolean;
      }>;
      announcementTeasers?: Array<{
        id: string;
        title: string;
        excerpt: string;
        locked: boolean;
        announcement?: boolean;
        pinned?: boolean;
      }>;
    };
    adultContent?: {
      enabled: boolean;
      minimumAgeYears: number;
    };
  };
  viewer?: {
    isMember: boolean;
    role: string | null;
    application?: {
      id: string;
      status: 'pending' | 'approved' | 'rejected' | 'promoted';
      tierId: string;
      tierName: string;
      billingType: string;
      reviewedAt: string | null;
    } | null;
  } | null;
};

export type CommunityDirectoryTopic = {
  slug: string;
  name: string;
  description: string;
  count: number;
};

export type PaginatedCommunitiesResponse = {
  items: CommunityRecord[];
  nextCursor: string | null;
};

export type CommunityBadgeAwardType = 'action_count' | 'level_reached' | 'streak_days' | 'manual';

export type CommunityBadgeRecord = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  iconEmoji: string;
  awardType: CommunityBadgeAwardType;
  awardConfig: Record<string, unknown>;
  enabled: boolean;
  sortOrder: number;
};

export type CommunityMemberBadgeRecord = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  iconEmoji: string;
  awardedAt: string;
};

export type CommunityMemberStreakRecord = {
  type: string;
  currentDays: number;
  longestDays: number;
  lastActivityDate: string | null;
  streakFreezeUntil?: string | null;
};

export type CommunityChallengeRecord = {
  id: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  goalActionKey: string;
  goalTarget: number;
  enabled: boolean;
  sortOrder: number;
  active?: boolean;
  progressCount?: number;
  completedAt?: string | null;
};

export type CommunityWelcomeChecklistItem = {
  key: 'intro_post' | 'profile_complete' | 'first_comment';
  label: string;
  description: string;
  done: boolean;
  href?: string | null;
};

export type CommunityImportJobRecord = {
  id: string;
  kind: 'members' | 'posts';
  status: string;
  dryRun: boolean;
  sourceFilename: string | null;
  rowCount: number;
  successCount: number;
  errorCount: number;
  errors: unknown;
  preview: unknown;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
};

export type CommunityBrandTheme = {
  primaryColor: string | null;
  accentColor: string | null;
  secondaryColor?: string | null;
  cssVariables: Record<string, string>;
};

export type CommunityOwnershipTransferRecord = {
  id: string;
  communityId: string;
  communityTitle: string;
  communitySlug: string | null;
  communityAccessType?: string;
  status: 'pending' | 'accepted' | 'cancelled';
  confirmNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
  stripeReconnectCompletedAt?: string | null;
  stripeReconnectRequired?: boolean;
  incomingOwner: {
    userId: string;
    username: string | null;
    displayName: string | null;
  };
  outgoingOwner: {
    username: string | null;
    displayName: string | null;
  };
};

export type LegalComplianceRequestRecord = {
  id: string;
  type: 'dmca_takedown' | 'dmca_counter_notice';
  status:
    | 'received'
    | 'reviewing'
    | 'action_taken'
    | 'rejected'
    | 'counter_received'
    | 'restored'
    | 'closed';
  claimantName: string;
  claimantEmail: string;
  claimantAddress: string | null;
  claimantPhone: string | null;
  targetType: string | null;
  targetId: string | null;
  infringingUrl: string;
  communityId: string | null;
  communityTitle: string | null;
  communitySlug: string | null;
  copyrightedWorkDescription: string;
  counterStatement: string | null;
  parentRequestId: string | null;
  adminNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PlatformSupportRequestRecord = {
  id: string;
  referenceCode: string;
  userId: string;
  submitterEmail: string;
  submitterName: string;
  category:
    | 'account_access'
    | 'creator_plan_billing'
    | 'purchase_refund'
    | 'circles_communities'
    | 'courses_learning'
    | 'payments_payouts'
    | 'bug_report'
    | 'feature_request'
    | 'other';
  subject: string;
  message: string;
  screenshotAssetId: string | null;
  screenshot: {
    assetId: string;
    status: string;
    scanStatus: string;
    mimeType: string;
    url: string | null;
  } | null;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  adminNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    email: string;
    username: string | null;
    displayName: string | null;
  };
};

export type PlatformModerationAppealRecord = {
  id: string;
  userId: string;
  appealType: 'account_ban' | 'creator_disabled';
  message: string;
  status: 'pending' | 'approved' | 'denied';
  resolutionNote: string | null;
  resolvedAt: string | null;
  createdAt: string;
  user: {
    email: string;
    username: string | null;
    displayName: string | null;
  };
};

export type CreatorSiteSectionType =
  | 'hero'
  | 'navbar'
  | 'footer'
  | 'about'
  | 'rich_text'
  | 'faq'
  | 'testimonials'
  | 'case_study'
  | 'social_links'
  | 'link_row'
  | 'follow_loamify'
  | 'featured_offer'
  | 'offer_grid'
  | 'course_showcase'
  | 'membership_card'
  | 'event_list'
  | 'article_list'
  | 'featured_article'
  | 'newsletter_signup'
  | 'contact_form'
  | 'embed'
  | 'image_gallery'
  | 'logo_strip'
  | 'stats'
  | 'spacer';

export type CreatorSiteSectionVisibility = {
  device: 'all' | 'mobile' | 'desktop';
};

export type CreatorSiteSectionRecord = {
  id: string;
  type: CreatorSiteSectionType;
  variant: string;
  sortOrder: number;
  visible: boolean;
  visibility: CreatorSiteSectionVisibility;
  content: Record<string, unknown>;
  bindings: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
};

export type CreatorSiteHomepageRecord = {
  id: string;
  slug: string;
  title: string | null;
  status: 'draft' | 'published';
  seoTitle: string | null;
  seoDescription: string | null;
  isHomepage?: boolean;
  sortOrder?: number;
  sections: CreatorSiteSectionRecord[];
};

export type CreatorSiteRecord = {
  id: string;
  status: 'draft' | 'published';
  themePreset: string;
  themeTokens: Record<string, string> | null;
  resolvedThemeTokens: Record<string, string>;
  siteTitle: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageAssetId: string | null;
  hidePlatformAttribution: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  pages: CreatorSiteHomepageRecord[];
  homepage: CreatorSiteHomepageRecord | null;
};

export type CreatorSiteResponse = {
  site: CreatorSiteRecord;
  capabilities?: {
    canHidePlatformAttribution: boolean;
  };
};

export type PublicCreatorSiteSection = {
  id: string;
  type: CreatorSiteSectionType;
  variant: string;
  sortOrder: number;
  visible: boolean;
  visibility: CreatorSiteSectionVisibility;
  content: Record<string, unknown>;
  bindings: Record<string, unknown> | null;
  resolved: {
    offers?: Array<{
      id: string;
      slug: string;
      title: string;
      shortDescription: string | null;
      priceCents: number;
      compareAtCents: number | null;
      currency: string;
      type: string;
      typeLabel: string;
      metaLabel: string | null;
      ctaLabel: string | null;
      thumbnailUrl: string | null;
      href: string;
      acquisitionPaused: boolean;
    }>;
    courses?: Array<{
      id: string;
      slug: string;
      title: string;
      subtitle: string | null;
      coverUrl: string | null;
      href: string;
      priceType: string;
      priceCents: number;
      compareAtCents: number | null;
      currency: string;
      ctaLabel: string;
      level: string | null;
      estimatedDurationMinutes: number | null;
      acquisitionPaused: boolean;
    }>;
    articles?: Array<{
      id: string;
      slug: string;
      title: string;
      subtitle: string | null;
      excerpt: string;
      coverUrl: string | null;
      href: string;
      readTimeMinutes: number;
      readTimeLabel: string | null;
      publishedAt: string;
      topics: string[];
    }>;
    membership?: {
      id: string;
      slug: string;
      title: string;
      description: string | null;
      heroImageUrl: string | null;
      href: string;
      priceCents: number;
      currency: string;
      ctaLabel: string;
      acquisitionPaused: boolean;
    } | null;
  } | null;
};

export type PublicCreatorSiteNavPage = {
  slug: string;
  title: string | null;
  href: string;
};

export type PublicCreatorSitePage = {
  id: string;
  slug: string;
  title: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  sections: PublicCreatorSiteSection[];
};

export type PublicCreatorSitePublished = {
  published: true;
  username: string;
  site: {
    id: string;
    status: 'published';
    themePreset: string;
    themeTokens: Record<string, string> | null;
    siteTitle: string | null;
    seoTitle: string | null;
    seoDescription: string | null;
    publishedAt: string | null;
    showPlatformAttribution?: boolean;
    navPages: PublicCreatorSiteNavPage[];
    page: PublicCreatorSitePage;
    /** @deprecated Use `page` — kept for backward compatibility */
    homepage: PublicCreatorSitePage;
  };
};

export type PublicCreatorSitePageResponse = PublicCreatorSitePublished | { published: false };
export type PublicCreatorSiteResponse = PublicCreatorSitePageResponse;

export type HoneypotTripRecord = {
  id: string;
  ip: string;
  path: string;
  userAgent: string | null;
  source: 'web-public' | 'api';
  blockedUntilSeconds: number;
  timestamp: string;
  isBlocked: boolean;
};

export type CommunityMemberDirectoryRecord = {
  id: string;
  membershipId: string;
  userId: string;
  role: string;
  status: string;
  joinedAt: string;
  createdAt: string;
  profile?: {
    username?: string | null;
    displayName?: string | null;
    avatarUrl?: string | null;
    headline?: string | null;
  } | null;
  viewerMuted?: boolean;
  viewerBlocked?: boolean;
  earnedBadges?: Array<{ name: string; iconEmoji: string }>;
  lastActiveAt: string | null;
  isOnline: boolean;
  lastActiveLabel: string | null;
};

export type CommunityWelcomeSettings = {
  welcomeChecklistEnabled: boolean;
  autoWelcomeEnabled: boolean;
  autoWelcomeMode: 'notification' | 'post';
  autoWelcomeMessage: string | null;
};

export type CommunityMembershipRecord = {
  id: string;
  communityId: string;
  userId: string;
  role: 'owner' | 'moderator' | 'member';
  status: 'active' | 'banned' | 'left';
  guidelinesAcceptedAt: string;
  joinedAt: string;
};

export type CommunityMemberFieldRecord = {
  id: string;
  prompt: string;
  sortOrder: number;
  required?: boolean;
  enabled?: boolean;
  fieldType?: 'text' | 'select' | 'multiselect' | 'checkbox' | 'url';
  options?: string[];
  conditionalRules?: { showWhenFieldId?: string; showWhenEquals?: string } | null;
};

export type CommunityModerationAppealRecord = {
  id: string;
  appealType: 'ban' | 'content_removal';
  status: 'pending' | 'approved' | 'denied';
  message: string;
  resolutionNote?: string | null;
  createdAt: string;
  resolvedAt?: string | null;
};

export type CommunityNotificationPrefsView = {
  muted: boolean;
  emailMode: 'inherit' | 'realtime' | 'digest';
  pushEnabled: boolean;
  digestSchedule: 'inherit' | 'daily' | 'weekly' | 'off';
  events: {
    replies: boolean;
    mentions: boolean;
    pins: boolean;
    modActions: boolean;
    welcome: boolean;
    billing: boolean;
    applicationReviewed: boolean;
  };
};

export type CommunityMembershipStatusResponse = {
  communityId: string;
  isMember: boolean;
  role: string | null;
  status: string | null;
  isBanned?: boolean;
  requiresGuidelinesReaccept?: boolean;
  rulesVersion?: number;
  guidelinesAcceptedVersion?: number;
  guidelinesText?: string | null;
  consentCopy?: string | null;
  minAgePolicyText?: string | null;
  canSubmitBanAppeal?: boolean;
  canSubmitContentRemovalAppeal?: boolean;
  billingStatus?: string | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  subscriptionPaused?: boolean;
  subscriptionPausedAt?: string | null;
  subscriptionPauseResumesAt?: string | null;
  canPauseSubscription?: boolean;
  canResumeSubscription?: boolean;
  notificationsMuted?: boolean;
  hideMembershipFromProfile?: boolean;
  profileFieldAnswers?: Record<string, string> | null;
  showLocationOnMap?: boolean;
  locationCity?: string | null;
  locationRegion?: string | null;
  locationLatitude?: number | null;
  locationLongitude?: number | null;
  adultContent?: {
    enabled: boolean;
    minimumAgeYears: number;
  };
  application?: {
    id: string;
    status: 'pending' | 'approved' | 'rejected' | 'promoted';
    tierId: string;
    tierName: string;
    billingType: 'free' | 'subscription' | 'lifetime';
    reviewedAt?: string | null;
  } | null;
  appeal?: CommunityModerationAppealRecord | null;
  billingEvents?: Array<{
    id: string;
    eventType: 'pause' | 'resume';
    note?: string | null;
    createdAt: string;
  }>;
};

export type CommunityModeratorRoleRecord = {
  id: string;
  communityId: string;
  name: string;
  permissions: string[];
  memberCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CommunityJoinQuestionRecord = {
  id: string;
  prompt: string;
  sortOrder: number;
  required?: boolean;
  enabled?: boolean;
};

export type CircleMembershipWaitlistEntryRecord = {
  id: string;
  communityId: string;
  tierId: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected' | 'promoted';
  message?: string | null;
  answers?: Record<string, string> | null;
  profileFieldAnswers?: Record<string, string> | null;
  rejectionReason?: string | null;
  staffNotes?: string | null;
  reviewedAt?: string | null;
  createdAt: string;
  tier: {
    id: string;
    name: string;
    slug: string;
    billingType: 'free' | 'subscription' | 'lifetime';
  };
  user: {
    id: string;
    profile: {
      username: string | null;
      displayName: string | null;
      avatarUrl: string | null;
    } | null;
  };
};

export type ConnectStatusResponse = {
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  testMode: boolean;
  accountId: string | null;
  ready?: boolean;
  message?: string;
  platformFeePercent?: number;
  commerceRestricted?: boolean;
};

export type PayoutSummaryResponse = {
  currency: string;
  stripeAvailableCents: number;
  stripePendingCents: number;
  activeHoldsCents: number;
  withdrawableCents: number;
  ledgerBalanceCents: number;
  holdsAutoPayoutWarning: boolean;
  lifetimeNetCents: number;
  isNegative: boolean;
  withdrawNowEnabled: boolean;
  payoutSchedule: {
    interval: string;
    label: string;
    manualPayoutsEnabled: boolean;
    delayDays: number | null;
  } | null;
  connect: {
    accountId: string | null;
    ready: boolean;
    chargesEnabled: boolean;
    payoutsEnabled: boolean;
    detailsSubmitted: boolean;
    testMode: boolean;
    platformFeePercent: number;
  };
  holds: Array<{
    id: string;
    sourceType: string;
    sourceId: string;
    orderId: string | null;
    amountCents: number;
    currency: string;
    createdAt: string;
  }>;
  streamBreakdown: Array<{
    stream: string;
    netCents: number;
    saleCount: number;
  }>;
};

export type PayoutLedgerEntry = {
  id: string;
  eventType: string;
  stream: string;
  grossCents: number;
  platformFeeCents: number;
  netCents: number;
  balanceAfterCents: number;
  currency: string;
  description: string;
  sourceType: string | null;
  sourceId: string | null;
  buyerId: string | null;
  buyerDisplayName: string | null;
  buyerUsername: string | null;
  buyerEmail: string | null;
  occurredAt: string;
  metadata: unknown;
};

export type PayoutLedgerQuery = {
  limit?: number;
  eventType?: string;
  stream?: string;
  search?: string;
};

export type PayoutLedgerResponse = {
  items: PayoutLedgerEntry[];
};

export type PayoutWithdrawalRecord = {
  payoutId: string;
  amountCents: number;
  currency: string;
  status: string;
  arrivalDate: string | null;
  createdAt: string;
  method: string | null;
};

export type PayoutWithdrawalsResponse = {
  items: PayoutWithdrawalRecord[];
};

export type PayoutWithdrawResponse = {
  ok: boolean;
  payoutId: string;
  amountCents: number;
  currency: string;
  status: string;
  arrivalDate: string | null;
  withdrawableCentsAfter: number;
};

export type PayoutLedgerExportResponse = {
  filename: string;
  csv: string;
  rowCount: number;
  windowMonths: number;
};

export type CircleAnalyticsDailyPoint = {
  date: string;
  value: number;
};

export type CircleAnalyticsContentDailyPoint = {
  date: string;
  posts: number;
  comments: number;
};

export type CircleAnalyticsFunnel = {
  landingViews: number;
  joins: number;
  conversionRate: number;
};

export type CircleAnalyticsRetentionCohortMonth = {
  monthOffset: number;
  label: string;
  activeCount: number;
  rate: number;
};

export type CircleAnalyticsRetentionCohort = {
  cohortMonth: string;
  joined: number;
  months: CircleAnalyticsRetentionCohortMonth[];
};

export type CircleAnalyticsTopContributor = {
  userId: string;
  displayName: string | null;
  username: string | null;
  activityCount: number;
  lastActivityAt: string | null;
};

export type CircleAnalyticsTopPost = {
  postId: string;
  preview: string;
  commentCount: number;
  reactionCount: number;
  createdAt: string;
};

export type CircleAnalyticsRevenueDailyPoint = {
  date: string;
  mrrCents: number;
  arrCents: number;
  paidSubscribers: number;
  churnCount: number;
};

export type CircleAnalyticsRevenueCohort = {
  cohortMonth: string;
  startingPaid: number;
  retainedPaid: number;
  retentionRate: number;
};

export type CircleAnalyticsInsights = {
  memberJoinsDaily: CircleAnalyticsDailyPoint[];
  contentActivityDaily: CircleAnalyticsContentDailyPoint[];
  funnel30d: CircleAnalyticsFunnel;
  retentionCohorts: CircleAnalyticsRetentionCohort[];
  topContributors: CircleAnalyticsTopContributor[];
  topPosts: CircleAnalyticsTopPost[];
  revenueDaily30d: CircleAnalyticsRevenueDailyPoint[];
  revenueDaily90d: CircleAnalyticsRevenueDailyPoint[];
  revenueCohorts: CircleAnalyticsRevenueCohort[];
};

export type CircleAnalyticsResponse = {
  communityId: string;
  status: string;
  accessType: string;
  currency: string;
  activeMemberCount: number;
  newMembersLast7Days: number;
  newMembersLast30Days: number;
  mrrCents: number;
  arrCents: number;
  paidSubscriberCount: number;
  pastDueCount: number;
  cancelAtPeriodEndCount: number;
  bannedCount: number;
  postCount: number;
  dauCount: number;
  wauCount: number;
  churnLast30Days: number;
  postsLast7Days: number;
  commentsLast7Days: number;
  churnRate30d: number;
  insights: CircleAnalyticsInsights;
};

export type CreatorAnalyticsRange = '7d' | '30d' | '90d';
export type CreatorAnalyticsMetric = {
  current: number;
  previous: number;
  delta: { absolute: number; percent: number | null };
};
export type CreatorAnalyticsModuleSummary = {
  key:
    | 'commerce'
    | 'services'
    | 'courses'
    | 'memberships'
    | 'events'
    | 'audience'
    | 'communities'
    | 'newsletters';
  status: 'available' | 'disabled' | 'unavailable';
  catalogCount: number;
  previousCatalogCount?: number;
  currentCount: number;
  previousCount: number;
  attributedGrossCents?: number;
  attributedNetCents?: number;
  drillDownRoute: string;
  message?: string;
};
export type CreatorAnalyticsOverview = {
  range: CreatorAnalyticsRange;
  generatedAt: string;
  boundaries: {
    timezone: 'UTC';
    currentStart: string;
    currentEndExclusive: string;
    previousStart: string;
    previousEndExclusive: string;
  };
  currency: string | null;
  revenueState: 'single' | 'mixed';
  currencyGroups: Array<{
    currency: string;
    currentGrossCents: number;
    previousGrossCents: number;
    currentNetCents: number;
    previousNetCents: number;
    grossRevenueCents: CreatorAnalyticsMetric;
    netRevenueCents: CreatorAnalyticsMetric;
    daily: Array<{ date: string; grossCents: number; netCents: number; sales: number }>;
  }>;
  headline: {
    grossRevenueCents: CreatorAnalyticsMetric | null;
    netRevenueCents: CreatorAnalyticsMetric | null;
    sales: CreatorAnalyticsMetric;
    customers: CreatorAnalyticsMetric;
    followers: CreatorAnalyticsMetric;
    engagement: CreatorAnalyticsMetric;
    catalog: CreatorAnalyticsMetric;
  };
  dailyRevenue: Array<{ date: string; grossCents: number; netCents: number; sales: number }>;
  modules: CreatorAnalyticsModuleSummary[];
  instrumentation: { durationMs: number; warningThresholdMs: number };
};

export type AudienceCrmContactRecord = {
  id: string;
  userId: string | null;
  email?: string | null;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  lastActivityAt?: string | null;
  ltvCents: number;
  consentProductUpdates?: boolean;
  consentPromotional?: boolean;
  relationships: Array<{
    type: string;
    sourceModule: string;
    status?: string | null;
    metadata?: unknown;
  }>;
  tags: string[];
  badges: string[];
};

export type CreatorDomainRecord = {
  id: string;
  hostname: string;
  status: string;
  redirectMode: string;
  pairKey: string | null;
  tlsStatus: string;
  tlsExpiresAt?: string | null;
  healthErrorCode: string | null;
  healthHint: string | null;
  verifiedAt: string | null;
  lastCheckedAt: string | null;
  lastHealthyAt?: string | null;
  certProvisionedAt?: string | null;
  adminDisabled: boolean;
  disabledReason?: string | null;
  isApex: boolean;
  isWww: boolean;
  binding: {
    targetType: string;
    targetId: string | null;
    isPrimary: boolean;
  } | null;
  instructions: {
    txtHost: string;
    txtValue: string;
    cnameHost: string | null;
    cnameTarget: string | null;
    apexIps: string[];
    apexAliasTarget: string | null;
    cloudflareNote: string;
    note: string;
  };
  marketingDns: {
    suggestedFromDomain: string;
    records: Array<{ type: string; host: string; value: string; purpose: string }>;
    statusNote: string;
    fromState?: 'platform_default' | 'host_pending' | 'dns_pending' | 'verified';
    auth?: {
      spfOk: boolean;
      dkimOk: boolean;
      dmarcOk: boolean;
      status: 'green' | 'partial' | 'missing' | null;
    } | null;
  };
  canonicalUrl: string | null;
};

export type AdminDomainsHealthOverview = {
  total: number;
  adminDisabled: number;
  statusCounts: Record<string, number>;
  tlsCounts: Record<string, number>;
  incidents: {
    dnsOrVerificationErrors: number;
    tlsErrors: number;
    staleHealthChecks: number;
    tlsExpiringWithin14Days: number;
    activeUnbound: number;
  };
  openIncidentCount: number;
};

export type AdminCreatorDomainRecord = CreatorDomainRecord & {
  creatorId?: string;
  username?: string | null;
  email?: string | null;
};

export type AdminAudienceContactRecord = {
  id: string;
  creatorId: string;
  creatorUsername: string | null;
  creatorEmail: string | null;
  creatorDisplayName: string | null;
  userId: string | null;
  email: string | null;
  displayName: string | null;
  username: string | null;
  lastActivityAt: string | null;
  ltvCents: number;
  consentProductUpdates: boolean;
  consentPromotional: boolean;
  relationships: Array<{
    type: string;
    sourceModule: string;
    status?: string | null;
  }>;
  tags: string[];
  badges: string[];
};

export type AdminAudienceContactDetail = Omit<AdminAudienceContactRecord, 'relationships'> & {
  avatarUrl: string | null;
  relationships: Array<{
    id: string;
    type: string;
    sourceModule: string;
    sourceId: string;
    status: string | null;
    metadata: unknown;
    updatedAt: string;
  }>;
  timeline: Array<{
    id: string;
    eventType: string;
    sourceModule: string;
    payload: unknown;
    occurredAt: string;
  }>;
};

export type CreatorDomainTargetOptions = {
  presets: Array<{ targetType: string; suggestedSubdomain: string }>;
  circles: Array<{ id: string; title: string; slug: string | null; status: string }>;
  courses: Array<{ id: string; title: string; slug: string | null }>;
  membership: Array<{ id: string; title: string }>;
  events: Array<{ id: string; title: string; slug: string | null }>;
  offers: Array<{ id: string; title: string; slug: string | null }>;
  publications: Array<{ id: string; name: string; slug: string | null; status: string }>;
  profile: { targetType: 'creator_profile' };
  site: { targetType: 'creator_site' };
  eventsCalendar: { targetType: 'events_calendar' };
};

export type AudienceCrmContactDetail = Omit<AudienceCrmContactRecord, 'relationships'> & {
  ltvBreakdown: {
    totalCents: number;
    servicesCents: number;
    coursesCents: number;
    membershipsCents: number;
  };
  relationships: Array<{
    id: string;
    type: string;
    sourceModule: string;
    sourceId: string;
    status?: string | null;
    metadata?: unknown;
    deepLink?: string | null;
  }>;
  timeline: Array<{
    id: string;
    eventType: string;
    sourceModule: string;
    payload: unknown;
    occurredAt: string;
  }>;
  recentOrders: Array<{
    kind: 'service' | 'course';
    id: string;
    title: string;
    amountCents: number;
    currency: string;
    at: string | null;
    deepLink: string;
  }>;
};

export type AudienceSegmentRecord = {
  id: string;
  creatorId: string;
  name: string;
  filter: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type OwnedCommunityMemberRecord = {
  id: string;
  userId: string;
  role: string;
  status: string;
  joinedAt: string;
  intakeAnswers?: Record<string, string> | null;
  profileFieldAnswers?: Record<string, string> | null;
  moderatorPermissions?: string[] | null;
  profile?: {
    username?: string | null;
    displayName?: string | null;
    avatarUrl?: string | null;
    headline?: string | null;
  } | null;
  billingInterval?: string | null;
  stripeBillingStatus?: string | null;
  stripeSubscriptionId?: string | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  subscriptionPausedAt?: string | null;
  subscriptionPauseResumesAt?: string | null;
  pastDueAt?: string | null;
  contactSource?: string | null;
  lastActivityAt?: string | null;
  activityCount?: number;
};

export type MembershipRecord = {
  id: string;
  creatorId: string;
  slug: string;
  title: string;
  description?: string | null;
  visibility: string;
  hidden: boolean;
  publicButtonLabel: string;
  heroHeadline?: string | null;
  heroSubtitle?: string | null;
  heroImageUrl?: string | null;
  carouselImages?: unknown[];
  services?: Array<{ title: string; description: string; icon?: string }>;
  seoTitle?: string | null;
  seoDescription?: string | null;
  ogImageUrl?: string | null;
  refundPolicyText?: string | null;
  publishedAt?: string | null;
  adminDisabledAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type MembershipTierRecord = {
  id: string;
  membershipId: string;
  name: string;
  slug: string;
  description?: string | null;
  sortOrder: number;
  billingType: 'free' | 'paid';
  monthlyPriceCents?: number | null;
  currency: string;
  perks?: Array<{ title: string; description?: string; manualLink?: string }>;
  status: string;
  activeMemberCount?: number;
  feePreview?: {
    priceCents: number;
    platformFeePercent: number;
    platformFeeCents: number;
    youKeepCents: number;
  } | null;
};

export type MembershipPostRecord = {
  id: string;
  membershipId: string;
  title: string;
  body?: unknown;
  status: string;
  visibility: string;
  minTierId?: string | null;
  teaser?: string | null;
  attachments?: unknown[];
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type MembershipPublicView = MembershipRecord & {
  creator: { username?: string | null; displayName?: string | null; avatarUrl?: string | null };
  tiers: MembershipTierRecord[];
  posts: Array<{
    id: string;
    title: string;
    visibility: string;
    teaser?: string | null;
    minTierId?: string | null;
    minTierName?: string | null;
    publishedAt?: string | null;
    locked: boolean;
    body?: unknown;
    attachments?: unknown[];
    attachmentCount?: number;
  }>;
};

export type MembershipFulfillmentRecord = {
  id: string;
  perkIndex: number;
  perkTitle: string;
  manualLink: string | null;
  status: string;
  deliveredAt: string | null;
  failureReason: string | null;
};

export type MembershipViewerSubscription = {
  id: string;
  status: string;
  tier?: { id: string; name: string; sortOrder?: number; billingType: string };
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
  pastDueAt?: string | null;
  graceEndsAt?: string | null;
  stripeBillingStatus?: string | null;
  canManageBilling?: boolean;
};

export type MembershipPublicPageResponse = {
  membership: MembershipPublicView;
  acquisitionPaused?: boolean;
  viewer: {
    isOwner: boolean;
    entitled: boolean;
    tierId?: string | null;
    subscription?: MembershipViewerSubscription | null;
    fulfillments?: MembershipFulfillmentRecord[];
  };
};

export type MembershipMemberRecord = {
  id: string;
  userId: string;
  email: string;
  username?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  status: string;
  stripeBillingStatus?: string | null;
  tier: { id: string; name: string; billingType: string; monthlyPriceCents?: number | null };
  joinedAt: string;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  graceEndsAt?: string | null;
  canRefund?: boolean;
  fulfillments?: MembershipFulfillmentRecord[];
};

export type MembershipOverviewMetrics = {
  mrrCents: number;
  activeMembers: number;
  paidActiveMembers?: number;
  publishedTierCount: number;
  currency?: string;
  tierBreakdown?: Array<{
    tierId: string;
    tierName: string;
    billingType: string;
    activeMembers: number;
    mrrCents: number;
  }>;
  connectReady?: boolean;
};

export type MembershipMineResponse = {
  membership: MembershipRecord | null;
  overview: MembershipOverviewMetrics | null;
};

export type MembershipStorefront = {
  item: {
    id: string;
    slug: string;
    title: string;
    description?: string | null;
    publicButtonLabel: string;
    heroSubtitle?: string | null;
    heroImageUrl?: string | null;
    url: string;
  } | null;
  acquisitionPaused?: boolean;
  teasers: Array<{
    id: string;
    title: string;
    teaser?: string | null;
    visibility?: string;
    minTierName?: string | null;
    publishedAt?: string | null;
    attachmentCount?: number;
    url?: string;
  }>;
};

export type ServiceDigitalContentRecord = {
  id?: string;
  fileKey?: string | null;
  fileName?: string | null;
  fileMimeType?: string | null;
  externalUrl?: string | null;
  buyerInstructions?: string | null;
};

export type ServiceWebinarConfigRecord = {
  durationMinutes: number;
  timezone: string;
  startsAt: string;
  capacity: number;
  showSeatsLeft: boolean;
  meetingProvider: 'zoom' | 'google_meet' | 'microsoft_teams' | 'manual';
  manualMeetingUrl?: string | null;
  sharedMeetingUrl?: string | null;
  joinLinkReleaseMinutes: number;
  replayEnabled: boolean;
  recordingUrl?: string | null;
  replayPublishedAt?: string | null;
  cancelPolicyText?: string | null;
};

export type ServiceWebinarPublicMeta = {
  durationMinutes: number;
  timezone: string;
  startsAt: string;
  capacity: number;
  seatsRemaining: number;
  showSeatsLeft: boolean;
  joinLinkReleaseMinutes: number;
  replayEnabled: boolean;
  replayPublished: boolean;
  cancelPolicyText?: string | null;
};

export type ServiceCohortConfigRecord = {
  capacity: number;
  timezone: string;
  enrollmentOpensAt?: string | null;
  enrollmentClosesAt?: string | null;
  circlePerkUrl?: string | null;
  showSeatsLeft: boolean;
  cancelPolicyText?: string | null;
};

export type ServiceCohortSessionRecord = {
  id: string;
  sessionIndex: number;
  title?: string | null;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  timezone: string;
  meetingProvider: 'zoom' | 'google_meet' | 'microsoft_teams' | 'manual';
  manualMeetingUrl?: string | null;
  sharedMeetingUrl?: string | null;
  recordingUrl?: string | null;
  replayPublishedAt?: string | null;
};

export type ServiceCohortPublicMeta = {
  timezone: string;
  capacity: number;
  seatsRemaining: number;
  showSeatsLeft: boolean;
  enrollmentOpensAt?: string | null;
  enrollmentClosesAt?: string | null;
  circlePerkUrl?: string | null;
  cancelPolicyText?: string | null;
  joinLinkReleaseMinutes: number;
  sessions: Array<{
    id: string;
    sessionIndex: number;
    title?: string | null;
    startsAt: string;
    endsAt: string;
    durationMinutes: number;
    timezone: string;
    hasReplay: boolean;
    isUpcoming: boolean;
  }>;
};

export type ServicePackageLineItemRecord = {
  id: string;
  includedOfferId?: string | null;
  includedCourseId?: string | null;
  includedMembershipTierId?: string | null;
  includedEventId?: string | null;
  quantity: number;
  sortOrder: number;
  includedOffer?: {
    id: string;
    title: string;
    type: string;
    slug: string;
    priceCents?: number;
    shortDescription?: string | null;
  } | null;
  includedCourse?: {
    id: string;
    title: string;
    slug: string | null;
    subtitle?: string | null;
    priceAmountCents?: number | null;
    priceType?: string;
  } | null;
  includedMembershipTier?: {
    id: string;
    name: string;
    slug: string;
    monthlyPriceCents?: number | null;
    billingType?: string;
    membership?: { title: string; slug: string };
  } | null;
  includedEvent?: {
    id: string;
    title: string;
    slug: string | null;
    description?: string | null;
    startsAt?: string | null;
  } | null;
};

export type PackageCandidateItem = {
  kind: 'service_offer' | 'course' | 'membership_tier' | 'event';
  id: string;
  title: string;
  subtitle?: string;
  typeLabel: string;
  priceCents: number;
};

export type ServicePackagePublicMeta = {
  lineItems: ServicePackageLineItemRecord[];
};

export type ServiceOfferRecord = {
  id: string;
  creatorId: string;
  type: string;
  slug: string;
  title: string;
  shortDescription?: string | null;
  description?: unknown;
  priceCents: number;
  compareAtCents?: number | null;
  currency: string;
  status: string;
  visibility: string;
  refundPolicyText?: string | null;
  coverMediaId?: string | null;
  thumbnailMediaId?: string | null;
  coverUrl?: string | null;
  thumbnailUrl?: string | null;
  ctaLabel?: string | null;
  serviceLabel?: string | null;
  confirmationMessage?: string | null;
  redirectUrl?: string | null;
  intakePrompt?: string | null;
  intakeQuestions?: Array<{ id: string; question: string; required: boolean }>;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  digitalContent?: ServiceDigitalContentRecord | null;
  oneToOneConfig?: ServiceOneToOneConfigRecord | null;
  priorityDmConfig?: ServicePriorityDmConfigRecord | null;
  webinarConfig?: ServiceWebinarConfigRecord | null;
  cohortConfig?: ServiceCohortConfigRecord | null;
  cohortSessions?: ServiceCohortSessionRecord[];
  packageConfig?: { offerId: string } | null;
  packageLineItems?: ServicePackageLineItemRecord[];
};

export type ServiceOneToOneConfigRecord = {
  durationMinutes: number;
  timezone: string;
  minNoticeHours: number;
  maxBookingsPerDay: number;
  bufferMinutes: number;
  meetingProvider: 'zoom' | 'google_meet' | 'microsoft_teams' | 'manual';
  manualMeetingUrl?: string | null;
  scheduleSource: 'inherit' | 'override';
  scheduleId?: string | null;
  reschedulePolicyText?: string | null;
  cancelMinNoticeHours?: number | null;
};

export type CreatorSchedulingProfileRecord = {
  timezone: string;
  minNoticeMinutes: number;
  maxAdvanceBookingDays: number;
  reschedulePolicyText?: string | null;
  defaultScheduleId?: string | null;
  calendarConnected?: boolean;
};

export type CreatorAvailabilityScheduleRecord = {
  id: string;
  name: string;
  isDefault: boolean;
  windowCount?: number;
  blockCount?: number;
};

export type CreatorAvailabilityBlockRecord = {
  id: string;
  date: string;
  label?: string | null;
};

export type GoogleCalendarListItem = {
  id: string;
  summary: string;
  primary?: boolean;
  accessRole?: string;
};

export type ServicePriorityDmConfigRecord = {
  slaHours: number;
  maxOpenRequests?: number | null;
  intakePrompt?: string | null;
  intakeQuestions?: Array<{ id: string; question: string; required: boolean }>;
  attachmentsPolicy: 'text_only' | 'image_file_lite';
  slaPublicCopy?: string | null;
};

export type ServicePriorityDmRequestRecord = {
  id: string;
  status: string;
  message?: string | null;
  slaDueAt?: string | null;
  submittedAt?: string | null;
  fulfilledAt?: string | null;
  conversationId?: string | null;
  isOverdue?: boolean;
  createdAt: string;
  offer: {
    id: string;
    slug: string;
    title: string;
    creatorUsername?: string | null;
    creatorDisplayName?: string | null;
    creatorAvatarUrl?: string | null;
    priorityDmConfig?: ServicePriorityDmConfigRecord | null;
  };
};

export type ServiceAvailabilityWindowRecord = {
  id: string;
  creatorId: string;
  offerId?: string | null;
  dayOfWeek: number;
  startMinute: number;
  endMinute: number;
  timezone: string;
};

export type IntegrationStatusRecord = {
  provider: string;
  label: string;
  status: string;
  connected: boolean;
  externalAccountId?: string | null;
  writeCalendarId?: string | null;
  busyCalendarIds: string[];
  lastError?: string | null;
  connectedAt?: string | null;
  updatedAt?: string | null;
  testMode: boolean;
};

export type ServiceOfferCard = {
  id: string;
  type: string;
  slug: string;
  title: string;
  shortDescription?: string | null;
  priceCents: number;
  compareAtCents?: number | null;
  currency: string;
  ctaLabel?: string | null;
  serviceLabel?: string | null;
  thumbnailUrl?: string | null;
  metaLabel?: string | null;
};

export type PublicServiceOfferRecord = ServiceOfferRecord & {
  creatorUsername?: string | null;
  creatorDisplayName?: string | null;
  creatorAvatarUrl?: string | null;
  webinarMeta?: ServiceWebinarPublicMeta | null;
  cohortMeta?: ServiceCohortPublicMeta | null;
  packageMeta?: ServicePackagePublicMeta | null;
  acquisitionPaused?: boolean;
};

export type ServiceCheckoutSession = {
  sessionId: string;
  clientSecret: string;
  publishableKey: string;
  stripeAccountId: string;
  devStub: boolean;
};

export type ServiceCouponRecord = {
  id: string;
  offerId: string;
  code: string;
  percentOff?: number | null;
  amountOffCents?: number | null;
  maxRedemptions?: number | null;
  redemptionCount: number;
  expiresAt?: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type ServiceCreatorOrderRecord = {
  id: string;
  offerId: string;
  buyerId: string;
  amountCents: number;
  discountCents: number;
  couponCode?: string | null;
  currency: string;
  status: string;
  receiptUrl?: string | null;
  paidAt?: string | null;
  refundedAt?: string | null;
  createdAt: string;
  offer: { title: string; slug: string; type: string };
  buyer: { email: string; displayName?: string | null; username?: string | null };
};

export type ServiceCatalogOverview = {
  days: number;
  publishedOfferCount: number;
  publishedCatalogCount?: number;
  grossCents: number;
  netEstimateCents: number;
  platformFeeCents: number;
  refundedCents: number;
  discountCents: number;
  orderCount: number;
  refundCount: number;
  views: number;
  paid: number;
  conversionRate: number;
  courses?: {
    publishedCount: number;
    enrollments30d: number;
    revenueGrossCents30d: number;
  } | null;
  memberships?: {
    activeMembers: number;
    mrrCents: number;
  } | null;
  events?: {
    publishedCount: number;
    rsvps30d: number;
  } | null;
};

export type StudioCatalogItem = {
  kind: 'service' | 'course' | 'membership' | 'event';
  id: string;
  title: string;
  slug: string;
  status: string;
  typeLabel: string;
  priceLabel: string;
  priceCents: number;
  currency: string;
  shortDescription?: string | null;
  thumbnailUrl?: string | null;
  metaLabel?: string | null;
  editHref: string;
  publicHref?: string | null;
  updatedAt: string;
};

export type PublicCatalogItem = {
  kind: 'service' | 'course' | 'membership' | 'event';
  id: string;
  title: string;
  slug: string;
  href: string;
  typeLabel: string;
  priceCents: number;
  compareAtCents?: number | null;
  currency: string;
  ctaLabel?: string | null;
  thumbnailUrl?: string | null;
  shortDescription?: string | null;
  metaLabel?: string | null;
  acquisitionPaused?: boolean;
};

export type AccountSubscriptionItem = {
  kind: 'circle' | 'membership';
  id: string;
  subscriptionId?: string | null;
  status: string;
  stripeBillingStatus?: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd?: string | null;
  subscriptionPausedAt?: string | null;
  subscriptionPauseResumesAt?: string | null;
  pastDueAt?: string | null;
  graceEndsAt?: string | null;
  joinedAt: string;
  title: string;
  slug: string;
  tierName?: string | null;
  href: string;
  coverUrl?: string | null;
  creatorUsername?: string | null;
  creatorDisplayName?: string | null;
};

export type ServiceAuditRecord = {
  id: string;
  actionType: string;
  targetId: string;
  metadata?: unknown;
  createdAt: string;
  actor?: {
    id: string;
    profile?: { username?: string | null; displayName?: string | null };
  };
};

export type ServiceBookingSlot = {
  startsAt: string;
  endsAt: string;
};

export type ServiceBookingHold = {
  bookingId: string;
  startsAt: string;
  endsAt: string;
  holdExpiresAt: string;
};

export type AccountBookingItem = {
  id: string;
  kind?: 'one_to_one' | 'webinar' | 'cohort_session';
  sessionIndex?: number;
  sessionTitle?: string | null;
  enrollmentId?: string;
  sessionId?: string;
  status: string;
  startsAt: string;
  endsAt?: string;
  buyerTimezone?: string | null;
  meetingUrl?: string | null;
  joinAvailableAt?: string | null;
  replayUrl?: string | null;
  isUpcoming: boolean;
  offer: {
    id: string;
    slug: string;
    title: string;
    durationMinutes?: number | null;
    reschedulePolicyText?: string | null;
    creatorUsername?: string | null;
    creatorDisplayName?: string | null;
    creatorAvatarUrl?: string | null;
  };
};

export type CreatorServiceBookingItem = {
  id: string;
  status: string;
  startsAt: string;
  endsAt: string;
  meetingUrl?: string | null;
  offer: { id: string; slug: string; title: string };
  buyer: {
    email: string;
    username?: string | null;
    displayName?: string | null;
  };
};

export type ServiceDownloadAccess = {
  type: 'file' | 'external';
  url: string;
  fileName?: string | null;
  buyerInstructions?: string | null;
};

export type AccountLibraryItem = {
  entitlementId: string;
  grantedAt: string;
  offer: {
    id: string;
    slug: string;
    title: string;
    type: string;
    shortDescription?: string | null;
    buyerInstructions?: string | null;
    fileName?: string | null;
    hasExternalUrl?: boolean;
    creatorUsername?: string | null;
    creatorDisplayName?: string | null;
    creatorAvatarUrl?: string | null;
  };
};

export type AccountOrderItem = {
  id: string;
  stream: string;
  amountCents: number;
  currency: string;
  status: string;
  receiptUrl?: string | null;
  paidAt?: string | null;
  refundedAt?: string | null;
  createdAt: string;
  description: string;
  offerType: string;
  offerSlug: string;
  creatorUsername?: string | null;
  creatorDisplayName?: string | null;
  billingKind?: string | null;
};

export type AccountOverview = {
  recentOrders: Array<{
    id: string;
    amountCents: number;
    currency: string;
    paidAt?: string | null;
    createdAt: string;
    offer: {
      id: string;
      slug: string;
      title: string;
      type: string;
      creatorUsername?: string | null;
      creatorDisplayName?: string | null;
    };
  }>;
  libraryCount: number;
  upcomingBookingsCount: number;
};

export type CourseRecord = {
  id: string;
  creatorId: string;
  slug: string | null;
  title: string;
  description?: string | null;
  subtitle?: string | null;
  coverUrl?: string | null;
  coverKey?: string | null;
  promoVideoUrl?: string | null;
  promoVideoKey?: string | null;
  fullDescription?: unknown;
  learningOutcomes?: string[];
  learningObjectives?: string[];
  skills?: string[];
  targetAudience?: string[];
  prerequisites?: unknown;
  level?: string;
  language?: string;
  estimatedDurationMinutes?: number;
  estimatedWeeks?: number | null;
  hoursPerWeek?: number | null;
  instructorName?: string | null;
  instructorBio?: unknown;
  instructorAvatarUrl?: string | null;
  faq?: Array<{ q: string; a: string }>;
  certificateEnabled?: boolean;
  primaryTopic?: string | null;
  visibility?: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
  priceType?: string;
  priceAmountCents?: number | null;
  compareAtPriceCents?: number | null;
  priceCurrency?: string;
  refundPolicyText?: string | null;
  termsAcknowledgment?: boolean;
  status: string;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  modules?: CourseModuleRecord[];
  instructor?: {
    name: string;
    bio?: unknown;
    avatarUrl?: string | null;
    username?: string | null;
  };
  lessonCount?: number;
  isFree?: boolean;
  href?: string | null;
  learnHref?: string | null;
  entitled?: boolean;
  enrolled?: boolean;
  accessSource?: string | null;
  storefrontSortOrder?: number;
  inviteOnlyEnrollment?: boolean;
  enrollmentToken?: string | null;
  rotateEnrollmentToken?: boolean;
  acquisitionPaused?: boolean;
};

export type CoursePurchaseRecord = {
  id: string;
  userId: string;
  amountCents: number;
  discountCents?: number;
  currency: string;
  status: string;
  couponCode?: string | null;
  receiptUrl?: string | null;
  refundedAt?: string | null;
  createdAt: string;
  buyerName: string;
  buyerUsername?: string | null;
};

export type CourseStudentRecord = {
  id: string;
  userId: string;
  email: string;
  username: string | null;
  displayName: string | null;
  accessSource: string;
  enrolledAt: string;
  completedAt: string | null;
  progressPercent: number;
  completedLessons: number;
  totalLessons: number;
  hasActivePurchase: boolean;
};

export type CourseStudentProgressRecord = {
  student: CourseStudentRecord;
  modules: Array<{
    id: string;
    title: string;
    lessons: Array<{
      id: string;
      title: string;
      completed: boolean;
      completedAt: string | null;
      lastPositionSeconds: number | null;
    }>;
  }>;
};

export type EventTicketCheckoutResponse = {
  sessionId: string;
  clientSecret: string;
  publishableKey: string | null;
  stripeAccountId: string;
  devStub: boolean;
  amountCents: number;
  currency: string;
};

export type EventRegistrationQuestionRecord = {
  id: string;
  prompt: string;
  required: boolean;
  sortOrder: number;
};

export type DiscoverEventListItem = {
  id: string;
  slug: string | null;
  title: string;
  description?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  location?: string | null;
  eventType?: string;
  coverUrl?: string | null;
  access?: string;
  creator?: {
    username: string | null;
    displayName: string | null;
    avatarUrl?: string | null;
  };
};

export type CourseCheckoutResponse = {
  sessionId: string;
  clientSecret: string;
  publishableKey: string | null;
  stripeAccountId: string;
  devStub: boolean;
  couponApplied?: string | null;
};

export type CourseCouponRecord = {
  id: string;
  courseId: string;
  code: string;
  percentOff?: number | null;
  amountOffCents?: number | null;
  maxRedemptions?: number | null;
  redemptionCount: number;
  expiresAt?: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CourseModuleRecord = {
  id: string;
  title: string;
  description?: string | null;
  learningObjectives?: string[];
  sortOrder: number;
  status: string;
  dripRule?: string;
  dripDaysAfterEnroll?: number | null;
  dripUnlockAt?: string | null;
  lessons?: CourseLessonRecord[];
};

export type CourseLessonRecord = {
  id: string;
  title: string;
  type: string;
  estimatedDurationMinutes?: number;
  freePreview?: boolean;
  lockUntilPreviousComplete?: boolean;
  prerequisiteLessonIds?: string[];
  commentsEnabled?: boolean;
  status: string;
  sortOrder: number;
  bodyJson?: unknown;
  bodyText?: string | null;
  mediaUrl?: string | null;
  mediaKey?: string | null;
  resources?: unknown;
  accessible?: boolean;
  locked?: boolean;
  dripLocked?: boolean;
  missingPrerequisiteLessonIds?: string[];
  unlockAt?: string | null;
  completed?: boolean;
  lastPositionSeconds?: number | null;
};

export type CourseCatalogItem = {
  id: string;
  slug: string | null;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  coverUrl?: string | null;
  level?: string;
  language?: string;
  estimatedDurationMinutes?: number;
  skills?: string[];
  primaryTopic?: string | null;
  priceType?: string;
  priceAmountCents?: number | null;
  priceCurrency?: string;
  certificateEnabled?: boolean;
  publishedAt?: string | null;
  href?: string | null;
  acquisitionPaused?: boolean;
  creator: {
    id: string;
    displayName: string;
    username?: string | null;
    avatarUrl?: string | null;
  };
};

export type CourseMediaAsset = {
  id: string;
  key: string;
  mimeType: string;
  sizeBytes: number;
  publicUrl?: string | null;
  createdAt: string;
};

export type CourseMediaLibrary = {
  items: CourseMediaAsset[];
  usage: {
    usedBytes: number;
    pendingBytes: number;
  };
};

export type CoursePublishChecklist = {
  ready: boolean;
  items: Array<{ key: string; label: string; passed: boolean; message?: string }>;
};

export type CirclePublishChecklist = {
  ready: boolean;
  items: Array<{
    key: string;
    label: string;
    passed: boolean;
    blocking: boolean;
    message?: string;
  }>;
};

export type CourseQuizChoiceRecord = {
  id: string;
  label: string;
  isCorrect?: boolean;
  sortOrder?: number;
};

export type CourseQuizQuestionRecord = {
  id: string;
  prompt: string;
  type: 'multiple_choice' | 'true_false';
  sortOrder?: number;
  explanation?: string | null;
  choices: CourseQuizChoiceRecord[];
};

export type CourseQuizRecord = {
  id?: string;
  lessonId: string;
  lessonTitle?: string;
  passScorePercent: number;
  attemptLimit?: number | null;
  showAnswersPolicy: 'after_pass' | 'after_fail' | 'after_both' | 'never';
  questions: CourseQuizQuestionRecord[];
};

export type CourseQuizTakeView = {
  lessonId: string;
  lessonTitle: string;
  courseSlug: string | null;
  passScorePercent: number;
  attemptLimit?: number | null;
  attemptCount: number;
  attemptsRemaining?: number | null;
  canSubmit: boolean;
  bestScorePercent?: number | null;
  passed: boolean;
  questions: Array<{
    id: string;
    prompt: string;
    type: 'multiple_choice' | 'true_false';
    sortOrder: number;
    choices: Array<{ id: string; label: string; sortOrder: number }>;
  }>;
  recentAttempts: Array<{
    id: string;
    scorePercent: number;
    passed: boolean;
    submittedAt: string;
  }>;
};

export type CourseQuizSubmitResult = {
  attemptId: string;
  scorePercent: number;
  passed: boolean;
  correctCount: number;
  totalCount: number;
  passScorePercent: number;
  attemptCount: number;
  attemptsRemaining?: number | null;
  showCorrectAnswers: boolean;
  questionResults: Array<{
    questionId: string;
    correct: boolean;
    selectedChoiceId: string | null;
    correctChoiceId: string | null;
    explanation?: string | null;
  }>;
};

export type CourseCertificateRecord = {
  id: string;
  code: string;
  status: 'pending' | 'generating' | 'ready' | 'failed';
  issuedAt?: string | null;
  ready?: boolean;
};

export type CourseCertificateView = {
  enabled: boolean;
  completed: boolean;
  certificate: CourseCertificateRecord | null;
};

export type CourseCertificateDownload = {
  certificateId: string;
  code: string;
  courseTitle: string;
  downloadUrl: string;
  expiresInSeconds: number;
};

export type CourseAnalyticsResponse = {
  courseId: string;
  courseTitle: string;
  priceType: string;
  currency: string;
  totalEnrollments: number;
  activeLearners7d: number;
  activeLearners30d: number;
  completionRate: number;
  completedEnrollments: number;
  grossRevenueCents: number;
  netRevenueCents: number;
  refundedRevenueCents: number;
  purchaseCount: number;
  totalLessons: number;
  enrollmentsDaily30d: Array<{ date: string; value: number }>;
  revenueDaily30d: Array<{ date: string; value: number }>;
  completionsDaily30d: Array<{ date: string; value: number }>;
  completionFunnel: Array<{
    stage: 'enrolled' | 'started' | 'halfway' | 'completed';
    label: string;
    count: number;
    rate: number;
  }>;
};

export type CourseLessonCommentRecord = {
  id: string;
  lessonId: string;
  authorId: string;
  parentCommentId: string | null;
  content: string;
  depth: number;
  createdAt: string;
  author: {
    id: string;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  };
};

export type CourseQuizAnalyticsResponse = {
  courseId: string;
  courseTitle: string;
  quizCount: number;
  totalAttempts: number;
  passedAttempts: number;
  passRate: number;
  avgScorePercent: number;
  uniqueLearners: number;
  attemptsDaily30d: Array<{ date: string; value: number; passed: number }>;
  perQuiz: Array<{
    lessonId: string;
    lessonTitle: string;
    questionCount: number;
    passScorePercent: number;
    attemptLimit?: number | null;
    attemptCount: number;
    passRate: number;
    avgScorePercent: number;
  }>;
  recentAttempts: Array<{
    id: string;
    lessonTitle: string;
    scorePercent: number;
    passed: boolean;
    submittedAt: string;
    learnerName: string;
    learnerUsername?: string | null;
  }>;
};

export type MyCourseEnrollment = {
  id: string;
  enrolledAt: string;
  completedAt?: string | null;
  accessSource: string;
  progressPercent?: number;
  continueLessonId?: string | null;
  course: {
    id: string;
    slug: string | null;
    title: string;
    subtitle?: string | null;
    coverUrl?: string | null;
    estimatedDurationMinutes?: number;
    href?: string;
    learnHref?: string;
    creatorName: string;
    creatorAvatarUrl?: string | null;
  };
};

export type CircleCourseRefRecord = {
  id: string;
  slug: string | null;
  title: string;
  description?: string | null;
  href: string;
  entitled?: boolean;
  accessSource?: string | null;
  perkMode?: 'free' | 'member_price' | 'public_price';
  requiresPurchase?: boolean;
  checkoutAmountCents?: number | null;
  publicPriceAmountCents?: number | null;
  memberPriceAmountCents?: number | null;
  priceCurrency?: string | null;
  communitySlug?: string | null;
};

export type CircleCourseClassroomResponse = {
  visible: boolean;
  label: string;
  entitled: boolean;
  courses: CircleCourseRefRecord[];
  message?: string | null;
};

export type CircleCourseClassroomPreview = {
  available: boolean;
  courseCount: number;
  courses: Array<{
    id: string;
    title: string;
    description: string | null;
    modules: Array<{ id: string; title: string }>;
  }>;
};

export type CircleCoursePerkRecord = {
  id: string;
  courseId: string;
  minTierId?: string | null;
  perkMode?: 'free' | 'member_price' | 'public_price';
  memberPriceAmountCents?: number | null;
  memberPriceCurrency?: string;
  enabled: boolean;
  sortOrder: number;
  course: Pick<
    CourseRecord,
    | 'id'
    | 'slug'
    | 'title'
    | 'description'
    | 'status'
    | 'priceType'
    | 'priceAmountCents'
    | 'priceCurrency'
  >;
  minTier?: { id: string; name: string; slug: string } | null;
};

export type NewsletterSectionRecord = {
  id: string;
  publicationId: string;
  name: string;
  slug: string;
  description?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export type MarketingDeveloperApiKeyRecord = {
  id: string;
  creatorId: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
};

export type MarketingWebhookEndpointRecord = {
  id: string;
  label: string;
  url: string;
  enabled: boolean;
  events: string[];
  createdAt: string;
  updatedAt: string;
};

export type MarketingSubjectAbReport = {
  enabled: boolean;
  status: string;
  samplePercent: number;
  windowEndsAt: string | null;
  subjectA: string;
  subjectB: string | null;
  winnerVariant: string | null;
  winnerSubject: string | null;
  variantA: { delivered: number; rawOpens: number; verifiedOpens: number };
  variantB: { delivered: number; rawOpens: number; verifiedOpens: number };
};

export type NewsletterEditionRecord = {
  id: string;
  publicationId: string;
  creatorId: string;
  title: string;
  subject: string;
  subjectVariantB?: string | null;
  previewText?: string | null;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  blocks: unknown[];
  htmlCache?: string | null;
  plaintextCache?: string | null;
  autosavedAt?: string | null;
  scheduledAt?: string | null;
  timezone?: string | null;
  materializedAt?: string | null;
  cancelledAt?: string | null;
  archiveSlug?: string | null;
  webPublishedAt?: string | null;
  contentArticleId?: string | null;
  sectionId?: string | null;
  section?: { id: string; name: string; slug: string } | null;
  audioAssetId?: string | null;
  audioMimeType?: string | null;
  audioSizeBytes?: number | null;
  audioDurationSeconds?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type MarketingAnalyticsReport = {
  range: CreatorAnalyticsRange;
  honesty: string;
  sends: {
    delivered: number;
    bounced: number;
    failed: number;
    complained: number;
    unsubscribed: number;
    held: number;
    deferred: number;
  };
  engagement: {
    rawOpens: number;
    verifiedOpens: number;
    rawClicks: number;
    verifiedClicks: number;
  };
  links: Array<{ url: string; rawClicks: number; verifiedClicks: number }>;
  growth: {
    confirmedSubscribes: number;
    churned: number;
    confirmedReferrals: number;
    sources: Array<{ source: string; count: number }>;
  };
  revenue: {
    paidJoins: number;
    estimatedCents: number;
    currency: string;
  };
  cohorts: Array<{ week: string; signedUp: number; stillConfirmed: number }>;
  sequences: Array<{
    id: string;
    name: string;
    enrolled: number;
    completed: number;
    exited: number;
    active: number;
  }>;
  automations: Array<{ id: string; name: string; runs: number }>;
  recentSends: Array<{
    id: string;
    subject: string;
    title: string;
    status: string;
    deliveredCount: number;
    failedCount: number;
    skippedCount: number;
    createdAt: string;
  }>;
};

export type GrowthSponsorshipOfferRecord = {
  id: string;
  name: string;
  advertiserName: string;
  headline: string;
  body: string | null;
  imageUrl: string | null;
  ctaLabel: string;
  ctaUrl: string;
  disclosure: string;
  affiliateCode: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type NewsletterPublicationRecord = {
  id: string;
  creatorId: string;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  logoKey?: string | null;
  brandColor?: string | null;
  fromName?: string | null;
  requireDoubleOptIn?: boolean;
  physicalAddress?: string | null;
  leadMagnetUrl?: string | null;
  leadMagnetLabel?: string | null;
  accessMode?: 'free' | 'paid';
  paidInterval?: 'monthly' | 'annual' | null;
  paidAmountCents?: number | null;
  paidCurrency?: string | null;
  freePreviewBlocks?: number;
  linkedCommunityId?: string | null;
  linkedCommunity?: { id: string; title: string; slug: string | null } | null;
  status: 'draft' | 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
};

export type CreatorEventRecord = {
  id: string;
  creatorId: string;
  slug: string | null;
  title: string;
  description?: string | null;
  status: string;
  eventType: 'in_person' | 'virtual' | 'hybrid';
  access: 'public' | 'registered' | 'circle_members' | 'circle_tier' | 'ticket_holders';
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string;
  meetingLink?: string | null;
  meetingLinkReveal?: 'always' | 'going' | 'start_window' | 'ticket';
  location?: string | null;
  capacity?: number | null;
  coverUrl?: string | null;
  coverKey?: string | null;
  registrationOpen: boolean;
  hideGuestList?: boolean;
  requiresApproval?: boolean;
  waitlistEnabled?: boolean;
  organizerMessage?: string | null;
  publishedAt?: string | null;
  cancelledAt?: string | null;
  seriesParentId?: string | null;
  rruleFreq?: string | null;
  rruleInterval?: number;
  rruleCount?: number | null;
  rruleUntil?: string | null;
  ticketPriceCents?: number | null;
  ticketCurrency?: string;
  refundPolicyText?: string | null;
  checkInCode?: string | null;
  recordingUrl?: string | null;
  recordingKey?: string | null;
  announceToCircleFeed?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreatorEventAnalytics = {
  going: number;
  notGoing: number;
  maybe: number;
  pending: number;
  waitlisted: number;
  checkedIn: number;
  totalResponses: number;
  capacity: number | null;
  capacityUsedPercent: number | null;
  ticketed: number;
  grossRevenueCents: number;
  currency: string;
};

export type CreatorEventRsvpRecord = {
  id: string;
  userId: string;
  email: string;
  username: string | null;
  displayName: string | null;
  status:
    | 'going'
    | 'maybe'
    | 'not_going'
    | 'pending_approval'
    | 'waitlisted'
    | 'cancelled';
  createdAt: string;
  updatedAt: string;
};

export type CircleEventRefRecord = {
  id: string;
  slug: string | null;
  title: string;
  description?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone?: string | null;
  meetingLink?: string | null;
  location?: string | null;
  href: string;
  entitled?: boolean;
  accessSource?: string | null;
  rsvpStatus?: string | null;
  rsvpCount?: number;
};

export type CircleEventCalendarResponse = {
  visible: boolean;
  label: string;
  entitled: boolean;
  events: CircleEventRefRecord[];
  message?: string | null;
};

export type CircleEventPerkRecord = {
  id: string;
  eventId: string;
  minTierId?: string | null;
  enabled: boolean;
  sortOrder: number;
  event: Pick<
    CreatorEventRecord,
    'id' | 'slug' | 'title' | 'description' | 'status' | 'startsAt' | 'endsAt' | 'timezone'
  >;
  minTier?: { id: string; name: string; slug: string } | null;
};

export type AdminCommunityBillingHealth = {
  activePaidCount: number;
  pastDueCount: number;
  cancelAtPeriodEndCount: number;
  conflicts: string[];
};

export type AdminCommunityConnectSummary = {
  connected: boolean;
  accountId: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  ready: boolean;
  conflicts: string[];
};

export type CursorPage<T> = {
  items: T[];
  pageInfo: {
    nextCursor: string | null;
    hasMore: boolean;
  };
};

export type NumberedPage<T> = {
  items: T[];
  pageInfo: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
};

export type AdminCommunityRecord = {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  accessType: string;
  listingVisibility?: string | null;
  featuredAt?: string | null;
  featuredOrder?: number | null;
  featuredUntil?: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    stripeConnectAccountId: string | null;
    connectChargesEnabled: boolean;
    connectPayoutsEnabled: boolean;
    connectDetailsSubmitted: boolean;
    user: {
      email: string;
      profile: { username: string | null; displayName: string | null } | null;
    };
  };
  connect: AdminCommunityConnectSummary;
  billingHealth: AdminCommunityBillingHealth;
};

export type AdminCreatorSiteRecord = {
  id: string;
  status: 'draft' | 'published';
  siteTitle: string | null;
  publishedAt: string | null;
  username: string | null;
  displayName: string | null;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    email: string;
  } | null;
};

export type AdminEventRecord = {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  eventType: string;
  startsAt: string | null;
  publishedAt: string | null;
  adminDisabledAt: string | null;
  adminDisabledReason: string | null;
  rsvpCount: number;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    email: string;
    username: string | null;
    displayName: string | null;
  };
};

export type AdminMarketingSendHealth = {
  sentLastHour: number;
  sentToday: number;
  config: Record<string, unknown>;
  warmupActive: boolean;
  withinCaps: boolean;
  killSwitch: boolean;
};

export type AdminMarketingKillSwitchState = {
  enabled: boolean;
  health: AdminMarketingSendHealth;
  updatedAt?: string;
};

export type AdminMarketingDeliverabilityCounts = {
  delivered: number;
  hardFail: number;
  softFail: number;
  complained: number;
  held: number;
  deferred: number;
};

export type AdminMarketingDeliverabilityOverview = {
  last24Hours: AdminMarketingDeliverabilityCounts;
  last7Days: AdminMarketingDeliverabilityCounts;
  suppressionCount: number;
  suspendedCreatorCount: number;
};

export type AdminCreatorDeliverabilityRecord = {
  creatorId: string;
  email: string | null;
  username: string | null;
  displayName: string | null;
  marketingSuspendedAt: string | null;
  marketingSuspendedReason: string | null;
  counts: AdminMarketingDeliverabilityCounts;
  bounceRatePct: number;
  complaintRatePct: number;
};

export type AdminCreatorMarketingSuspensionState = {
  creatorId: string;
  marketingSuspendedAt: string | null;
  marketingSuspendedReason: string | null;
};

export type PlatformPublicSettings = {
  maintenanceMode: boolean;
  maintenanceMessage: string | null;
  signupEnabled: boolean;
  /** Semver floor for member Expo app; null = use client local floor only. */
  mobileConsumerMinVersion?: string | null;
};

export type PlatformAdminSettings = PlatformPublicSettings & {
  updatedAt: string;
  updatedById: string | null;
};

export type AdminCourseRecord = {
  id: string;
  slug: string | null;
  title: string;
  status: string;
  visibility: string;
  priceType: string;
  publishedAt: string | null;
  enrollmentCount: number;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    email: string;
    username: string | null;
    displayName: string | null;
  };
};

export type AdminOfferTypeCapabilityRow = {
  type: string;
  label: string;
  defaultState: boolean;
  effectiveEnabled: boolean;
  platformOverride: boolean | null;
  envOverride: boolean | null;
  envVarName: string;
  storageKey: string;
};

export type AdminServiceOfferRecord = {
  id: string;
  slug: string;
  title: string;
  type: string;
  status: string;
  priceCents: number;
  currency: string;
  adminDisabledAt: string | null;
  publishedAt: string | null;
  orderCount: number;
  openReportCount: number;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    email: string;
    username: string | null;
    displayName: string | null;
  };
};

export type AdminOpenReportRecord = {
  id: string;
  reason: string;
  targetType: string;
  targetId: string;
  status: string;
  targetLabel?: string;
  targetHref?: string | null;
  communityId?: string | null;
  communityTitle?: string | null;
  creatorSiteId?: string;
  creatorUsername?: string | null;
  serviceOfferId?: string;
  serviceOfferSlug?: string | null;
  serviceOfferDisabled?: boolean;
  postId?: string | null;
  articleSlug?: string | null;
};

export type AdminSubscriptionRefundRecord = {
  id: string;
  stripeEventId: string;
  type: string;
  amountCents: number | null;
  currency: string | null;
  status: string | null;
  chargeId: string | null;
  refundId: string | null;
  createdAt: string;
};

export type AdminCommerceDisputeRecord = {
  disputeId: string;
  amountCents: number | null;
  currency: string | null;
  status: string | null;
  reason: string | null;
  chargeId: string | null;
  paymentIntentId: string | null;
  connectAccountId: string | null;
  evidenceDueBy: string | null;
  stripeCreatedAt: string | null;
  lastEventType: string;
  lastEventAt: string;
  lastWebhookEventId: string;
  creatorId: string | null;
  creatorUsername: string | null;
  creatorDisplayName: string | null;
  creatorEmail: string | null;
  serviceOrderId: string | null;
  serviceOrderStatus: string | null;
  offerId: string | null;
  offerTitle: string | null;
  ledgerDescription: string | null;
  ledgerSourceType: string | null;
  ledgerSourceId: string | null;
};

export type AdminCommerceDisputeDetail = AdminCommerceDisputeRecord & {
  timeline: Array<{
    id: string;
    type: string;
    status: string | null;
    createdAt: string;
  }>;
};

export type AdminCommerceDisputesOverview = {
  totalCases: number;
  openCount: number;
  lostCount: number;
  wonCount: number;
  createdLast7d: number;
  mirrorEventCount: number;
};

export type AdminMembershipRecord = {
  id: string;
  slug: string;
  title: string;
  visibility: string;
  adminDisabledAt: string | null;
  publishedAt: string | null;
  activeMemberCount: number;
  updatedAt: string;
  creator: {
    id: string;
    userId: string;
    disabled: boolean;
    email: string;
    username: string | null;
    displayName: string | null;
  };
  connect: AdminCommunityConnectSummary;
  billingHealth: AdminCommunityBillingHealth;
};

export type AdminMembershipConnectHealthResponse = {
  membershipId: string;
  title: string;
  slug: string;
  visibility: string;
  adminDisabledAt: string | null;
  creator: {
    id: string;
    userId: string;
    email: string;
    username: string | null;
    displayName: string | null;
  };
  connect: AdminCommunityConnectSummary;
  billingHealth: AdminCommunityBillingHealth;
};

export type AdminCommunityConnectHealthResponse = {
  communityId: string;
  title: string;
  slug: string | null;
  status: string;
  accessType: string;
  creator: {
    id: string;
    userId: string;
    email: string;
    username: string | null;
    displayName: string | null;
  };
  connect: AdminCommunityConnectSummary;
  billingHealth: AdminCommunityBillingHealth;
};

export type CircleMembershipTierRecord = {
  id: string;
  communityId: string;
  name: string;
  slug: string;
  description?: string | null;
  sortOrder: number;
  accessRank: number;
  billingType: 'free' | 'subscription' | 'lifetime';
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
  lifetimePriceCents?: number | null;
  trialDays?: number | null;
  memberCap?: number | null;
  requiresApproval?: boolean;
  waitlistWhenFull?: boolean;
  enabled?: boolean;
  isDefault?: boolean;
  activeMemberCount?: number;
  atCapacity?: boolean;
};

export type CommunityMembershipPolicies = {
  refundPolicyText: string | null;
  cancellationPolicyText: string | null;
  sellerOfRecordPolicyText: string | null;
  sellerOfRecordDisplayText: string;
};

export type CommunityResourceKind = 'folder' | 'file' | 'link';

export type CommunityResourceStudioRecord = {
  id: string;
  parentId: string | null;
  kind: CommunityResourceKind;
  title: string;
  description: string | null;
  sortOrder: number;
  isPinned: boolean;
  minLevel: number | null;
  minTierId: string | null;
  minTierName: string | null;
  fileUrl: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  linkUrl: string | null;
  status: 'active' | 'archived';
  version: number;
  childCount: number;
  createdAt: string;
  updatedAt: string;
};

export type CommunityResourceMemberRecord = {
  id: string;
  parentId: string | null;
  kind: CommunityResourceKind;
  title: string;
  description: string | null;
  isPinned: boolean;
  locked: boolean;
  canOpen: boolean;
  unlockLevelName?: string | null;
  unlockTierName?: string | null;
  fileUrl: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  linkUrl: string | null;
  childCount: number;
};

export type CommunityResourcesMemberResponse = {
  parentId: string | null;
  breadcrumbs: Array<{ id: string; title: string }>;
  pinned: CommunityResourceMemberRecord[];
  items: CommunityResourceMemberRecord[];
  hasResources: boolean;
};

export type CreateCommunityResourceFolderPayload = {
  title: string;
  description?: string;
  parentId?: string;
  isPinned?: boolean;
  minLevel?: number | null;
  minTierId?: string | null;
};

export type CreateCommunityResourceLinkPayload = CreateCommunityResourceFolderPayload & {
  linkUrl: string;
};

export type CreateCommunityResourceFilePayload = CreateCommunityResourceFolderPayload & {
  fileKey: string;
  mimeType: string;
  sizeBytes: number;
};

export type UpdateCommunityResourcePayload = {
  title?: string;
  description?: string | null;
  parentId?: string | null;
  isPinned?: boolean;
  sortOrder?: number;
  minLevel?: number | null;
  minTierId?: string | null;
  linkUrl?: string;
};

export type CommunityResourceVersionRecord = {
  id: string;
  version: number;
  title: string;
  description: string | null;
  fileUrl: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  linkUrl: string | null;
  createdAt: string;
  createdByName: string;
};

export type ReplaceCircleMembershipPoliciesPayload = {
  membershipRefundPolicyText?: string | null;
  membershipCancellationPolicyText?: string | null;
  membershipSellerOfRecordPolicyText?: string | null;
};

export type CircleMembershipCouponRecord = {
  id: string;
  communityId: string;
  code: string;
  percentOff?: number | null;
  amountOffCents?: number | null;
  maxRedemptions?: number | null;
  redemptionCount?: number;
  expiresAt?: string | null;
  tierIds?: string[] | null;
  enabled?: boolean;
};

export type CircleMembershipCheckoutResponse = {
  sessionId: string;
  clientSecret: string;
  publishableKey: string | null;
  stripeAccountId: string;
  devStub: boolean;
  tierId?: string;
  couponApplied?: string | null;
};

export type NotificationPrefsLegacy = {
  likes: boolean;
  comments: boolean;
  follows: boolean;
  creatorPosts: boolean;
  mentions: boolean;
  directMessages: boolean;
  pushDirectMessages: boolean;
};

export type NotificationPrefs = {
  version: 2;
  preset: 'recommended' | 'in_app_only' | 'all_email' | 'custom';
  digest: {
    enabled: boolean;
    frequency: 'daily' | 'weekly';
    hourUtc: number;
  };
  categories: Record<
    string,
    {
      inApp: boolean;
      email: 'off' | 'missed_only' | 'immediate' | 'daily_digest' | 'weekly_digest';
      push?: boolean;
      granular?: {
        likes?: boolean;
        follows?: boolean;
        reposts?: boolean;
        saves?: boolean;
        likesPush?: boolean;
        followsPush?: boolean;
        repostsPush?: boolean;
        savesPush?: boolean;
      };
    }
  >;
};

export type NotificationListPage = {
  items: EnrichedNotification[];
  nextCursor: string | null;
};

export type ProfileCompletionState = {
  score: number;
  completed: string[];
  pending: string[];
};

export type WelcomeWizardStatus = 'not_started' | 'dismissed' | 'completed';

export type WelcomeWizardState = {
  status: WelcomeWizardStatus;
  step: string | null;
  resumeSnoozedAt: string | null;
};

export type OnboardingState = {
  completed: boolean;
  topicCount: number;
  creatorFollowCount: number;
  hasUsername: boolean;
  hasDisplayName: boolean;
  welcomeWizard: WelcomeWizardState;
};

export type UserSessionInfo = {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  lastActiveAt: string;
  createdAt: string;
  expiresAt: string;
  current: boolean;
};

export type FullMemberSettings = {
  email: string;
  emailVerifiedAt: string | null;
  accountStatus: string;
  twoFactorEnabled: boolean;
  twoFactorBackupCodesRemaining?: number;
  notificationPrefs: NotificationPrefs;
  privacyPrefs: PrivacyPrefs;
  contentPrefs: ContentPrefs;
  socialLinks: {
    id: string;
    label: string;
    url: string;
    platform?: string | null;
    sortOrder: number;
  }[];
  topics: string[];
  profileCompletion: ProfileCompletionState;
};

export type ProfilePrompts = {
  workingOn?: string | null;
  learning?: string | null;
  askMeAbout?: string | null;
};

export type PublicSocialLink = {
  label: string;
  url: string;
  platform?: string | null;
  sortOrder?: number;
};

export type PublicProfile = {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  bannerUrl?: string | null;
  bio: string | null;
  headline?: string | null;
  topics?: string[];
  profilePrompts?: ProfilePrompts;
  location: string | null;
  websiteUrl: string | null;
  pinnedPostId?: string | null;
  createdAt?: string;
  creator: {
    id: string;
    followersCount?: number;
    links?: string | null;
    branding?: string | null;
    featuredLinks?: Array<{ label: string; url: string }> | null;
    badge?: string | null;
    isActive?: boolean;
  } | null;
  socialLinks?: PublicSocialLink[];
  privacyPrefs?: PrivacyPrefs;
  trust?: {
    emailVerified: boolean;
    memberSince: string;
  };
  followedBy?: {
    userId: string;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  }[];
  visibility?: {
    activityHidden: boolean;
    bookmarksHidden: boolean;
    followersHidden: boolean;
    followingHidden: boolean;
    articlesHidden?: boolean;
  };
  seo?: {
    title: string;
    description: string;
    imageUrl: string | null;
    canonicalUrl: string;
  };
  stats: {
    postsCount: number;
    followersCount: number;
    followingCount: number;
    followersHidden?: boolean;
    followingHidden?: boolean;
  };
};

export type PublicProfileReply = {
  id: string;
  content: string;
  mediaUrl?: string | null;
  createdAt: string;
  author: {
    id: string;
    profile: {
      username: string | null;
      displayName: string | null;
      avatarUrl?: string | null;
    } | null;
    creator?: { id: string } | null;
  };
  post: unknown;
};

export type PublicListResponse<T> = {
  items: T[];
  hidden: boolean;
  nextCursor?: string | null;
};

export type PublicPaginatedListResponse<T> = {
  items: T[];
  nextCursor: string | null;
  hidden?: boolean;
};

export type PublicProfileActivityItem =
  | { kind: 'like'; id: string; createdAt: string; post: unknown }
  | {
      kind: 'reply';
      id: string;
      createdAt: string;
      content: string;
      mediaUrl: string | null;
      author: PublicProfileReply['author'];
      post: unknown;
    };

export type PublicProfileLike = {
  id: string;
  createdAt: string;
  post: unknown;
};

export type FollowListResponse = {
  members: unknown[];
  creators: unknown[];
  hidden?: boolean;
};

export type PaginatedFeedResponse<T = unknown> = {
  items: T[];
  nextCursor: string | null;
};

export type CommentThreadMeta = {
  maxReplyDepth: number;
};

export type PaginatedCommentsResponse<T = unknown> = PaginatedFeedResponse<T> & {
  threading?: CommentThreadMeta | null;
};

export type MemberInsightsPeriod = '7d' | '30d';

export type InsightDailyPoint = {
  date: string;
  value: number;
};

export type InsightMetric = {
  key: string;
  label: string;
  total: number;
  series: InsightDailyPoint[];
};

export type MemberInsightsResponse = {
  period: MemberInsightsPeriod;
  periodLabel: string;
  from: string;
  to: string;
  metrics: InsightMetric[];
};

export type EnrichedNotification = {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
  actor?: {
    id: string;
    username: string | null;
    displayName: string | null;
    avatarUrl: string | null;
  };
  target?: {
    postId?: string;
    conversationId?: string;
  };
};

export type MediaAssetUploadStatus = {
  id: string;
  key: string;
  status: 'pending' | 'confirmed' | 'rejected';
  scanStatus: 'pending' | 'queued' | 'scanning' | 'clean' | 'infected' | 'error' | 'skipped';
  error: string | null;
  publicUrl: string | null;
};

function normalizeMediaAssetStatus(status: MediaAssetUploadStatus): MediaAssetUploadStatus {
  return {
    ...status,
    publicUrl: normalizeLoamifyBrowserMediaUrl(status.publicUrl) ?? null,
  };
}

export type ApiAuthTransport = 'cookie' | 'bearer';

export class ApiClient {
  private sessionActive = false;
  private csrfToken: string | null = null;
  private csrfTokenRequest: Promise<string> | null = null;
  /** Browser apps use cookie + CSRF; native apps use bearer (D-MOB-008). */
  private authTransport: ApiAuthTransport = 'cookie';
  private accessToken: string | null = null;

  /**
   * Configure how authenticated requests send credentials.
   * Call once at app boot (`cookie` default for web; `bearer` for Expo).
   */
  configureAuthTransport(transport: ApiAuthTransport) {
    this.authTransport = transport;
    if (transport === 'cookie') {
      this.accessToken = null;
    }
  }

  getAuthTransport(): ApiAuthTransport {
    return this.authTransport;
  }

  /**
   * Sets the bearer access token for native clients.
   * No-op for cookie transport aside from sessionActive bookkeeping.
   */
  setAccessToken(token: string | null) {
    this.accessToken = token;
    this.sessionActive = !!token;
    if (!token) {
      this.csrfToken = null;
      this.csrfTokenRequest = null;
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** @deprecated Prefer cookie session (web) or `setAccessToken` (native bearer). */
  setToken(token: string | null) {
    if (this.authTransport === 'bearer') {
      this.setAccessToken(token);
      return;
    }
    this.sessionActive = !!token;
  }

  /** Returns true when client believes a session may exist (cookie or bearer). */
  hasSession() {
    return this.sessionActive;
  }

  markSessionActive() {
    this.sessionActive = true;
    this.csrfToken = null;
    this.csrfTokenRequest = null;
  }

  markSessionInactive() {
    this.sessionActive = false;
    if (this.authTransport === 'bearer') {
      this.accessToken = null;
    }
  }

  private isUnsafeBrowserRequest(options: RequestInit): boolean {
    if (this.authTransport === 'bearer') return false;
    if (typeof window === 'undefined') return false;
    const method = (options.method ?? 'GET').toUpperCase();
    return !['GET', 'HEAD', 'OPTIONS'].includes(method);
  }

  private async getCsrfToken(forceRefresh = false): Promise<string> {
    if (forceRefresh) {
      this.csrfToken = null;
      this.csrfTokenRequest = null;
    }
    if (this.csrfToken) return this.csrfToken;
    if (this.csrfTokenRequest) return this.csrfTokenRequest;

    this.csrfTokenRequest = (async () => {
      const res = await fetch(`${resolveApiBaseUrl()}/api/security/csrf-token`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      const payload = (await res.json().catch(() => ({}))) as {
        token?: string;
        message?: string | string[];
        code?: string;
      };
      if (!res.ok || !payload.token) {
        const message = Array.isArray(payload.message)
          ? payload.message.join(', ')
          : payload.message || 'Unable to initialize request protection';
        throw new ApiRequestError(message, res.status, undefined, payload.code);
      }
      this.csrfToken = payload.token;
      return payload.token;
    })();

    try {
      return await this.csrfTokenRequest;
    } finally {
      this.csrfTokenRequest = null;
    }
  }

  async request<T>(path: string, options: RequestInit = {}, csrfRetry = false): Promise<T> {
    const headers = new Headers(options.headers);
    // Only set JSON content-type when we send a body. Putting it on GET/HEAD
    // forces CORS preflights on cross-subdomain API calls and can break
    // credentialed session cookie flows (staging/production).
    const hasBody = options.body !== undefined && options.body !== null;
    if (hasBody && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (this.isUnsafeBrowserRequest(options)) {
      headers.set('x-csrf-token', await this.getCsrfToken(csrfRetry));
    }
    const clientApp = resolveLoamifyClientAppHeader();
    if (clientApp) {
      headers.set('x-loamify-client', clientApp);
    }
    if (this.authTransport === 'bearer' && this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    const res = await fetch(`${resolveApiBaseUrl()}/api${path}`, {
      ...options,
      headers,
      // Cookie sessions need credentials; bearer tokens must not rely on cookies.
      credentials: this.authTransport === 'cookie' ? 'include' : 'omit',
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => ({ message: res.statusText }))) as {
        message?: string | string[];
        resendStatus?: OtpResendStatus;
        code?: string;
      };
      if (err.code === 'CSRF_TOKEN_EXPIRED' && !csrfRetry && this.isUnsafeBrowserRequest(options)) {
        return this.request<T>(path, options, true);
      }
      const message = Array.isArray(err.message)
        ? err.message.join(', ')
        : err.message || 'Request failed';
      throw new ApiRequestError(message, res.status, err.resendStatus, err.code);
    }
    if (res.status === 204) {
      return undefined as T;
    }
    const text = await res.text();
    if (!text) {
      return undefined as T;
    }
    return JSON.parse(text) as T;
  }

  signup(data: {
    email: string;
    password: string;
    username: string;
    displayName: string;
    intent?: 'member' | 'creator';
  }) {
    return this.request<{
      requiresEmailVerification: true;
      pendingToken: string;
      email: string;
      username: string;
      emailVerification?: {
        expiresIn: number;
        resendStatus?: OtpResendStatus;
        devCode?: string;
        devOtp?: string;
        devToken?: string;
        devLink?: string;
      };
    }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  verifyEmailOtp(code: string) {
    return this.request<{
      ok: boolean;
      alreadyVerified?: boolean;
      firstVerification?: boolean;
    }>('/auth/email-verification/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  verifyEmail(token: string) {
    return this.request<{ ok: boolean }>(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  }

  resendEmailVerification() {
    return this.request<{
      ok: boolean;
      alreadyVerified?: boolean;
      expiresIn?: number;
      resendStatus?: OtpResendStatus;
      devCode?: string;
      devOtp?: string;
      devToken?: string;
      devLink?: string;
    }>('/auth/email-verification/resend', { method: 'POST' });
  }

  getEmailVerificationResendStatus() {
    return this.request<OtpResendStatus>('/auth/email-verification/resend-status');
  }

  forgotPassword(email: string) {
    return this.request<{
      ok: boolean;
      message: string;
      pendingToken?: string;
      email?: string;
      passwordReset?: {
        expiresIn: number;
        resendStatus?: OtpResendStatus;
        devCode?: string;
        devOtp?: string;
      };
    }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  verifyForgotPasswordOtp(pendingToken: string, code: string) {
    return this.request<{ ok: boolean; resetPendingToken: string }>(
      '/auth/forgot-password/verify',
      {
        method: 'POST',
        body: JSON.stringify({ pendingToken, code }),
      },
    );
  }

  resendForgotPasswordOtp(pendingToken: string) {
    return this.request<{
      ok: boolean;
      email: string;
      expiresIn?: number;
      resendStatus?: OtpResendStatus;
      devCode?: string;
      devOtp?: string;
    }>('/auth/forgot-password/resend', {
      method: 'POST',
      body: JSON.stringify({ pendingToken }),
    });
  }

  getForgotPasswordResendStatus(pendingToken: string) {
    return this.request<OtpResendStatus>('/auth/forgot-password/resend-status', {
      method: 'POST',
      body: JSON.stringify({ pendingToken }),
    });
  }

  checkUsername(username: string) {
    return this.request<{
      available: boolean;
      username: string;
      reason: 'invalid_format' | 'reserved' | 'taken' | null;
    }>(`/users/check-username/${encodeURIComponent(username)}`);
  }

  login(email: string, password: string) {
    return this.request<{
      user?: unknown;
      username?: string | null;
      requiresTwoFactor?: boolean;
      requiresEmailVerification?: boolean;
      pendingToken?: string;
      email?: string;
      postAuthRedirect?: 'feed' | 'profile-welcome';
      isFirstTimeUser?: boolean;
      /** Present for native clients (`x-loamify-client: mobile_*`). */
      accessToken?: string;
      expiresIn?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }).then((result) => {
      if (result.accessToken && this.authTransport === 'bearer') {
        this.setAccessToken(result.accessToken);
      } else if (result.user && !result.requiresTwoFactor && !result.requiresEmailVerification) {
        this.markSessionActive();
      }
      return result;
    });
  }

  loginVerifyEmail(pendingToken: string, code: string) {
    return this.request<{
      user?: unknown;
      username?: string | null;
      requiresTwoFactor?: boolean;
      pendingToken?: string;
      postAuthRedirect?: 'feed' | 'profile-welcome';
      isFirstTimeUser?: boolean;
      accessToken?: string;
      expiresIn?: string;
    }>('/auth/login/verify-email', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    }).then((result) => {
      if (result.accessToken && this.authTransport === 'bearer') {
        this.setAccessToken(result.accessToken);
      } else if (result.user && !result.requiresTwoFactor) {
        this.markSessionActive();
      }
      return result;
    });
  }

  resendLoginEmailVerification(pendingToken: string) {
    return this.request<{
      ok: boolean;
      alreadyVerified?: boolean;
      email?: string;
      expiresIn?: number;
      resendStatus?: OtpResendStatus;
      devCode?: string;
      devOtp?: string;
    }>('/auth/login/resend-email-verification', {
      method: 'POST',
      body: JSON.stringify({ pendingToken }),
    });
  }

  getLoginEmailVerificationResendStatus(pendingToken: string) {
    return this.request<OtpResendStatus>('/auth/login/email-verification/resend-status', {
      method: 'POST',
      body: JSON.stringify({ pendingToken }),
    });
  }

  logout() {
    return this.request<{ ok: boolean }>('/auth/logout', { method: 'POST' });
  }

  me() {
    return this.request<{ user: unknown }>('/auth/me');
  }

  private buildFeedQuery(
    cursor?: string,
    limit?: number,
    extra?: Record<string, string | undefined>,
  ) {
    const params = new URLSearchParams();
    if (extra) {
      for (const [key, value] of Object.entries(extra)) {
        if (value !== undefined) params.set(key, value);
      }
    }
    if (cursor) params.set('cursor', cursor);
    if (limit !== undefined) params.set('limit', String(limit));
    const q = params.toString();
    return q ? `?${q}` : '';
  }

  getFeedPaginated(endpoint: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PaginatedFeedResponse>(`${endpoint}${q}`);
  }

  getFeed(cursor?: string, limit?: number) {
    if (cursor !== undefined || limit !== undefined) {
      return this.getFeedPaginated('/content/feed', cursor, limit);
    }
    return this.request<unknown[]>('/content/feed');
  }

  getFollowingFeed(cursor?: string, limit?: number) {
    if (cursor !== undefined || limit !== undefined) {
      return this.getFeedPaginated('/content/feed/following', cursor, limit);
    }
    return this.request<unknown[]>('/content/feed/following');
  }

  getHomeFeed(cursor?: string, limit?: number) {
    if (cursor !== undefined || limit !== undefined) {
      return this.getFeedPaginated('/content/feed/home', cursor, limit);
    }
    return this.request<unknown[]>('/content/feed/home');
  }

  getTrendingFeed(cursor?: string, limit?: number) {
    if (cursor !== undefined || limit !== undefined) {
      return this.getFeedPaginated('/content/feed/trending', cursor, limit);
    }
    return this.request<unknown[]>('/content/feed/trending');
  }

  getPost(id: string) {
    return this.request<unknown>(`/content/posts/${id}`);
  }

  requestMediaUploadUrl(data: {
    mimeType: string;
    sizeBytes: number;
    purpose?:
      | 'post'
      | 'article'
      | 'community-file'
      | 'community-audio'
      | 'service-digital'
      | 'service-image'
      | 'course-image'
      | 'event-image'
      | 'support-screenshot';
    durationSeconds?: number;
  }) {
    return this.request<{ assetId: string; uploadUrl: string; key: string; expiresIn: number }>(
      '/media/upload-url',
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  async uploadMediaFile(
    file: File,
    onProgress?: (pct: number) => void,
    purpose?:
      | 'post'
      | 'article'
      | 'community-file'
      | 'community-audio'
      | 'service-digital'
      | 'service-image'
      | 'course-image'
      | 'event-image'
      | 'support-screenshot',
    durationSeconds?: number,
    onStatus?: (status: MediaAssetUploadStatus) => void,
  ) {
    const { assetId, uploadUrl } = await this.requestMediaUploadUrl({
      mimeType: file.type,
      sizeBytes: file.size,
      purpose,
      durationSeconds,
    });

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('Upload failed'));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    const queued = await this.confirmMediaUpload(assetId);
    onStatus?.(queued);
    return this.pollMediaAssetUntilReady(assetId, { onStatus });
  }

  confirmMediaUpload(assetId: string) {
    return this.request<MediaAssetUploadStatus>('/media/confirm', {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    }).then(normalizeMediaAssetStatus);
  }

  getMediaAssetStatus(assetId: string) {
    return this.request<MediaAssetUploadStatus>(`/media/${encodeURIComponent(assetId)}/status`).then(
      normalizeMediaAssetStatus,
    );
  }

  async pollMediaAssetUntilReady(
    assetId: string,
    options: {
      intervalMs?: number;
      timeoutMs?: number;
      onStatus?: (status: MediaAssetUploadStatus) => void;
    } = {},
  ): Promise<MediaAssetUploadStatus> {
    const intervalMs = options.intervalMs ?? 1_000;
    const deadline = Date.now() + (options.timeoutMs ?? 10 * 60_000);
    while (Date.now() < deadline) {
      const asset = await this.getMediaAssetStatus(assetId);
      options.onStatus?.(asset);
      if (asset.status === 'confirmed' && asset.publicUrl) return asset;
      if (
        asset.status === 'rejected' ||
        asset.scanStatus === 'infected' ||
        asset.scanStatus === 'error'
      ) {
        throw new Error(asset.error || 'Upload failed security inspection');
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error('Upload security scan timed out');
  }

  searchGifs(query: string, limit = 20) {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      provider: 'tenor' | 'dev';
      items: Array<{
        id: string;
        title: string;
        previewUrl: string;
        url: string;
        width: number;
        height: number;
      }>;
    }>(`/integrations/gifs/search${suffix}`);
  }

  getCreators(search?: string) {
    const q = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<unknown[]>(`/creators${q}`);
  }

  getCreator(id: string) {
    return this.request<unknown>(`/creators/${id}`);
  }

  follow(creatorId: string) {
    return this.request('/social/follow', {
      method: 'POST',
      body: JSON.stringify({ creatorId }),
    });
  }

  unfollow(creatorId: string) {
    return this.request(`/social/follow/${creatorId}`, { method: 'DELETE' });
  }

  isFollowing(creatorId: string) {
    return this.request<{ following: boolean }>(`/social/following/${creatorId}`);
  }

  followUser(userId: string) {
    return this.request(`/social/follow/user/${userId}`, { method: 'POST' });
  }

  unfollowUser(userId: string) {
    return this.request(`/social/follow/user/${userId}`, { method: 'DELETE' });
  }

  isFollowingUser(userId: string) {
    return this.request<{ following: boolean }>(`/social/follow/user/${userId}`);
  }

  getFollowing() {
    return this.request<unknown>('/social/following');
  }

  likePost(postId: string) {
    return this.request(`/engagement/posts/${postId}/like`, { method: 'POST' });
  }

  unlikePost(postId: string) {
    return this.request(`/engagement/posts/${postId}/like`, { method: 'DELETE' });
  }

  isPostLiked(postId: string) {
    return this.request<{ liked: boolean }>(`/engagement/posts/${postId}/liked`);
  }

  getComments(postId: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PaginatedCommentsResponse>(`/engagement/posts/${postId}/comments${q}`);
  }

  createComment(
    postId: string,
    content: string | { content: string; mediaUrl?: string; parentCommentId?: string },
  ) {
    const body = typeof content === 'string' ? { content } : content;
    return this.request(`/engagement/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteComment(commentId: string) {
    return this.request(`/engagement/comments/${commentId}`, { method: 'DELETE' });
  }

  updateComment(commentId: string, payload: { content: string; mediaUrl?: string }) {
    return this.request(`/engagement/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  likeComment(commentId: string) {
    return this.request(`/engagement/comments/${commentId}/like`, { method: 'POST' });
  }

  unlikeComment(commentId: string) {
    return this.request(`/engagement/comments/${commentId}/like`, { method: 'DELETE' });
  }

  isCommentLiked(commentId: string) {
    return this.request<{ liked: boolean }>(`/engagement/comments/${commentId}/liked`);
  }

  bookmarkPost(postId: string) {
    return this.request(`/engagement/posts/${postId}/bookmark`, { method: 'POST' });
  }

  unbookmarkPost(postId: string) {
    return this.request(`/engagement/posts/${postId}/bookmark`, { method: 'DELETE' });
  }

  isPostBookmarked(postId: string) {
    return this.request<{ bookmarked: boolean }>(`/engagement/posts/${postId}/bookmarked`);
  }

  getBookmarks(cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PaginatedFeedResponse>(`/engagement/bookmarks${q}`);
  }

  getOnboardingState() {
    return this.request<OnboardingState>('/onboarding/state');
  }

  getTopicSuggestions() {
    return this.request<{ id: string; slug: string; name: string; description: string | null }[]>(
      '/onboarding/topic-suggestions',
    );
  }

  getRecommendedCreators() {
    return this.request<unknown[]>('/onboarding/recommended-creators');
  }

  completeOnboarding(options?: { skipCreatorFollows?: boolean }) {
    return this.request('/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(options ?? {}),
    });
  }

  dismissWelcomeWizard() {
    return this.request<OnboardingState>('/onboarding/welcome-wizard/dismiss', { method: 'POST' });
  }

  saveWelcomeWizardProgress(step: string) {
    return this.request<OnboardingState>('/onboarding/welcome-wizard/progress', {
      method: 'PATCH',
      body: JSON.stringify({ step }),
    });
  }

  snoozeWelcomeResume() {
    return this.request<OnboardingState>('/onboarding/welcome-wizard/snooze', { method: 'POST' });
  }

  reopenWelcomeWizard() {
    return this.request<OnboardingState>('/onboarding/welcome-wizard/reopen', { method: 'POST' });
  }

  search(
    q: string,
    type: 'all' | 'creators' | 'members' | 'people' | 'posts' | 'articles' | 'topics' = 'all',
    filters?: { topic?: string },
  ) {
    const params = new URLSearchParams({ q, type });
    if (filters?.topic) params.set('topic', filters.topic);
    return this.request<SearchResults>(`/search?${params.toString()}`);
  }

  getProfile() {
    return this.request('/users/profile');
  }

  updateProfile(data: Record<string, unknown>) {
    const body = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));
    return this.request('/users/profile', { method: 'PATCH', body: JSON.stringify(body) });
  }

  getSettings() {
    return this.request<FullMemberSettings>('/users/me/settings');
  }

  getProfileCompletion() {
    return this.request<ProfileCompletionState>('/users/me/completion');
  }

  getMemberInsights(period: MemberInsightsPeriod = '7d') {
    return this.request<MemberInsightsResponse>(`/users/me/insights?period=${period}`);
  }

  recordProfileView(username: string) {
    return this.request<{ ok: boolean; skipped?: boolean }>(`/users/views/profile/${username}`, {
      method: 'POST',
    });
  }

  recordPostView(postId: string) {
    return this.request<{ ok: boolean; skipped?: boolean }>(`/content/posts/${postId}/view`, {
      method: 'POST',
    });
  }

  updatePrivacyPrefs(prefs: Partial<PrivacyPrefs>) {
    return this.request('/users/me/privacy', { method: 'PATCH', body: JSON.stringify(prefs) });
  }

  updateContentPrefs(prefs: Partial<ContentPrefs>) {
    return this.request('/users/me/content-preferences', {
      method: 'PATCH',
      body: JSON.stringify(prefs),
    });
  }

  updateSocialLinks(
    links: { label?: string; url: string; platform?: string | null; sortOrder?: number }[],
  ) {
    return this.request('/users/me/social-links', {
      method: 'PATCH',
      body: JSON.stringify({ links }),
    });
  }

  startEmailChange(newEmail: string, currentPassword: string) {
    return this.request<{ ok: boolean; message: string; devToken?: string }>(
      '/users/me/email-change/start',
      { method: 'POST', body: JSON.stringify({ newEmail, currentPassword }) },
    );
  }

  confirmEmailChange(token: string) {
    return this.request<{ ok: boolean; email: string }>('/users/me/email-change/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  getSessions() {
    return this.request<{ sessions: UserSessionInfo[] }>('/users/me/sessions');
  }

  revokeSession(sessionId: string) {
    return this.request<{ ok: boolean; revokedCurrent?: boolean }>(
      `/users/me/sessions/${sessionId}`,
      { method: 'DELETE' },
    );
  }

  setupTwoFactor() {
    return this.request<{ secret: string; otpauthUrl: string }>('/users/me/2fa/setup', {
      method: 'POST',
    });
  }

  confirmTwoFactor(code: string) {
    return this.request<{ ok: boolean; twoFactorEnabled: boolean; backupCodes: string[] }>(
      '/users/me/2fa/confirm',
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
    );
  }

  regenerateTwoFactorBackupCodes(currentPassword: string, code: string) {
    return this.request<{ ok: boolean; backupCodes: string[] }>(
      '/users/me/2fa/backup-codes/regenerate',
      {
        method: 'POST',
        body: JSON.stringify({ currentPassword, code }),
      },
    );
  }

  disableTwoFactor(currentPassword: string, code: string) {
    return this.request('/users/me/2fa', {
      method: 'DELETE',
      body: JSON.stringify({ currentPassword, code }),
    });
  }

  deactivateAccount(currentPassword: string) {
    return this.request('/users/me/deactivate', {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    });
  }

  requestAccountDeletion(currentPassword: string, reason?: string) {
    return this.request('/users/me/delete-request', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, reason }),
    });
  }

  requestDataExport() {
    return this.request<{
      exportId: string;
      status: string;
      downloadUrl: string;
      expiresAt: string;
      downloadExpiresInSeconds: number;
    }>('/users/me/export', { method: 'POST' });
  }

  getDataExport(exportId: string) {
    return this.request<{
      exportId: string;
      status: string;
      downloadUrl: string;
      expiresAt: string;
      downloadExpiresInSeconds: number;
    }>(`/users/me/export/${encodeURIComponent(exportId)}`);
  }

  getHiddenPosts() {
    return this.request<{ items: unknown[] }>('/users/me/hidden-posts');
  }

  resetPassword(pendingToken: string, newPassword: string) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, newPassword }),
    });
  }

  loginTwoFactor(pendingToken: string, code: string) {
    return this.request<{
      user: unknown;
      username?: string | null;
      postAuthRedirect?: 'feed' | 'profile-welcome';
      isFirstTimeUser?: boolean;
      accessToken?: string;
      expiresIn?: string;
    }>('/auth/login/2fa', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    }).then((result) => {
      if (result.accessToken && this.authTransport === 'bearer') {
        this.setAccessToken(result.accessToken);
      } else if (result.user) {
        this.markSessionActive();
      }
      return result;
    });
  }

  adminLogin(email: string, password: string) {
    return this.request<{
      user?: unknown;
      requiresTwoFactor?: boolean;
      requiresTwoFactorEnrollment?: boolean;
      requiresEmailVerification?: boolean;
      pendingToken?: string;
      email?: string;
      code?: string;
      message?: string;
    }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  adminLoginVerifyEmail(pendingToken: string, code: string) {
    return this.request<{
      user?: unknown;
      requiresTwoFactor?: boolean;
      requiresTwoFactorEnrollment?: boolean;
      pendingToken?: string;
      code?: string;
      message?: string;
    }>('/admin/auth/login/verify-email', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    });
  }

  adminLoginTwoFactor(pendingToken: string, code: string) {
    return this.request<{ user: unknown }>('/admin/auth/login/2fa', {
      method: 'POST',
      body: JSON.stringify({ pendingToken, code }),
    });
  }

  adminMfaEnrollmentSetup(pendingToken: string) {
    return this.request<{ secret: string; otpauthUrl: string }>('/admin/auth/mfa-enrollment/setup', {
      method: 'POST',
      body: JSON.stringify({ pendingToken }),
    });
  }

  adminMfaEnrollmentConfirm(pendingToken: string, code: string) {
    return this.request<{ user: unknown; backupCodes?: string[] }>(
      '/admin/auth/mfa-enrollment/confirm',
      {
        method: 'POST',
        body: JSON.stringify({ pendingToken, code }),
      },
    );
  }

  adminLogout() {
    return this.request<{ ok: boolean }>('/admin/auth/logout', { method: 'POST' });
  }

  adminMe() {
    return this.request<{ user: unknown }>('/admin/auth/me');
  }

  updateNotificationPrefs(prefs: {
    preset?: 'recommended' | 'in_app_only' | 'all_email' | 'custom';
    digest?: { enabled?: boolean; frequency?: 'daily' | 'weekly'; hourUtc?: number };
    categories?: Record<
      string,
      {
        inApp?: boolean;
        email?: 'off' | 'missed_only' | 'immediate' | 'daily_digest' | 'weekly_digest';
        push?: boolean;
        granular?: {
          likes?: boolean;
          follows?: boolean;
          reposts?: boolean;
          saves?: boolean;
          likesPush?: boolean;
          followsPush?: boolean;
          repostsPush?: boolean;
          savesPush?: boolean;
        };
      }
    >;
    likes?: boolean;
    comments?: boolean;
    follows?: boolean;
    creatorPosts?: boolean;
    mentions?: boolean;
    directMessages?: boolean;
    pushDirectMessages?: boolean;
  }) {
    return this.request('/users/me/settings', { method: 'PATCH', body: JSON.stringify(prefs) });
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.request('/users/me/password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  getPublicProfile(username: string) {
    return this.request<PublicProfile>(`/users/${username}`);
  }

  getPublicPosts(username: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PaginatedFeedResponse>(`/users/${username}/posts${q}`);
  }

  getPublicReplies(username: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PublicPaginatedListResponse<PublicProfileReply>>(
      `/users/${username}/replies${q}`,
    );
  }

  getPublicLikes(username: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PublicPaginatedListResponse<PublicProfileLike>>(
      `/users/${username}/likes${q}`,
    );
  }

  getPublicLikesReplies(username: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PublicPaginatedListResponse<PublicProfileActivityItem>>(
      `/users/${username}/likes-replies${q}`,
    );
  }

  getPublicBookmarks(username: string, cursor?: string, limit?: number) {
    const q = this.buildFeedQuery(cursor, limit);
    return this.request<PublicPaginatedListResponse<unknown>>(`/users/${username}/bookmarks${q}`);
  }

  getFollowingByUsername(username: string) {
    return this.request<FollowListResponse>(`/users/${username}/following`);
  }

  getFollowersByUsername(username: string) {
    return this.request<FollowListResponse>(`/users/${username}/followers`);
  }

  suggestUsers(prefix: string) {
    return this.request<
      { username: string; displayName: string | null; avatarUrl: string | null; userId: string }[]
    >(`/users/suggest?prefix=${encodeURIComponent(prefix)}`);
  }

  blockUser(userId: string) {
    return this.request(`/social/block/${userId}`, { method: 'POST' });
  }

  unblockUser(userId: string) {
    return this.request(`/social/block/${userId}`, { method: 'DELETE' });
  }

  muteUser(userId: string) {
    return this.request(`/social/mute/${userId}`, { method: 'POST' });
  }

  unmuteUser(userId: string) {
    return this.request(`/social/mute/${userId}`, { method: 'DELETE' });
  }

  hidePost(postId: string, reason?: string, otherText?: string) {
    return this.request(`/social/hide/post/${postId}`, {
      method: 'POST',
      body: JSON.stringify({ reason, otherText }),
    });
  }

  unhidePost(postId: string) {
    return this.request(`/social/hide/post/${postId}`, { method: 'DELETE' });
  }

  getBlocks() {
    return this.request<
      { userId: string; username?: string; displayName?: string; avatarUrl?: string }[]
    >('/users/me/blocks');
  }

  getMutes() {
    return this.request<
      { userId: string; username?: string; displayName?: string; avatarUrl?: string }[]
    >('/users/me/mutes');
  }

  getNotifications(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<NotificationListPage>(`/notifications${qs ? `?${qs}` : ''}`);
  }

  getUnreadNotificationCount() {
    return this.request<{ count: number }>('/notifications/unread-count');
  }

  markNotificationRead(id: string) {
    return this.request(`/notifications/${id}/read`, { method: 'PATCH' });
  }

  markNotificationsReadMany(ids: string[]) {
    return this.request('/notifications/read-many', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'POST' });
  }

  listConversations() {
    return this.request<
      {
        id: string;
        type: string;
        community?: {
          id: string;
          title: string;
          slug: string | null;
        } | null;
        otherParticipant: {
          userId: string;
          username: string | null;
          displayName: string | null;
          avatarUrl: string | null;
        };
        lastMessage: {
          id: string;
          senderId: string;
          content: string;
          createdAt: string;
        } | null;
        unreadCount: number;
        updatedAt: string;
      }[]
    >('/messaging/conversations');
  }

  createConversation(recipientUserId: string, options?: { communitySlug?: string }) {
    return this.request<{ id: string }>('/messaging/conversations', {
      method: 'POST',
      body: JSON.stringify({
        recipientUserId,
        communitySlug: options?.communitySlug,
      }),
    });
  }

  getConversation(id: string) {
    return this.request(`/messaging/conversations/${id}`);
  }

  listMessages(conversationId: string, cursor?: string) {
    const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.request<{
      messages: { id: string; senderId: string; content: string; createdAt: string }[];
      nextCursor: string | null;
    }>(`/messaging/conversations/${conversationId}/messages${q}`);
  }

  sendMessage(conversationId: string, content: string) {
    return this.request<{ id: string; senderId: string; content: string; createdAt: string }>(
      `/messaging/conversations/${conversationId}/messages`,
      { method: 'POST', body: JSON.stringify({ content }) },
    );
  }

  markConversationRead(conversationId: string) {
    return this.request(`/messaging/conversations/${conversationId}/read`, { method: 'PATCH' });
  }

  getMessagingUnreadCount() {
    return this.request<{ count: number }>('/messaging/unread-count');
  }

  getPushVapidPublicKey() {
    return this.request<{ publicKey: string | null }>('/notifications/push/vapid-public-key');
  }

  savePushSubscription(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this.request('/notifications/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  deletePushSubscription(endpoint: string) {
    return this.request('/notifications/push/subscriptions', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    });
  }

  saveExpoPushDevice(token: string, platform?: string) {
    return this.request('/notifications/push/expo', {
      method: 'POST',
      body: JSON.stringify({ token, platform }),
    });
  }

  deleteExpoPushDevice(token: string) {
    return this.request('/notifications/push/expo', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  }

  /** @deprecated Use cookie-auth SSE at /notifications/events instead. */
  getMessagingStreamToken() {
    return this.request<{ streamToken: string; expiresIn: number }>('/messaging/events/token');
  }

  canMessageUser(userId: string, options?: { communitySlug?: string }) {
    const query = options?.communitySlug
      ? `?communitySlug=${encodeURIComponent(options.communitySlug)}`
      : '';
    return this.request<{ canMessage: boolean; communityContext?: boolean }>(
      `/messaging/can-message/${encodeURIComponent(userId)}${query}`,
    );
  }

  verifyAdminSession() {
    return this.adminMe().then((res) => ({ ok: true, user: res.user }));
  }

  createReport(data: {
    targetType:
      | 'post'
      | 'user'
      | 'comment'
      | 'message'
      | 'article'
      | 'community_member'
      | 'course'
      | 'course_lesson_comment'
      | 'membership'
      | 'service_offer'
      | 'creator_site';
    targetId: string;
    contextId?: string;
    communitySlug?: string;
    reason: string;
    details?: string;
  }) {
    return this.request('/reports', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        contextId: data.contextId ?? data.communitySlug,
      }),
    });
  }

  submitDmcaTakedown(data: {
    claimantName: string;
    claimantEmail: string;
    claimantAddress?: string;
    claimantPhone?: string;
    infringingUrl: string;
    copyrightedWorkDescription: string;
    statementGoodFaith: boolean;
    statementAccuracy: boolean;
    signature: string;
  }) {
    return this.request<{ request: LegalComplianceRequestRecord }>('/legal/dmca', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  submitDmcaCounterNotice(
    parentRequestId: string,
    data: {
      claimantName: string;
      claimantEmail: string;
      claimantAddress?: string;
      claimantPhone?: string;
      counterStatement: string;
      counterConsentJurisdiction: boolean;
      counterConsentService: boolean;
      signature: string;
    },
  ) {
    return this.request<{ request: LegalComplianceRequestRecord }>(
      `/legal/dmca/${encodeURIComponent(parentRequestId)}/counter-notice`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  createCreatorProfile(data: Record<string, string>) {
    return this.request('/creators/profile', { method: 'POST', body: JSON.stringify(data) });
  }

  getCreatorStatus() {
    return this.request<{
      status: string;
      creatorId: string | null;
      activatedAt: string | null;
      checkoutStartedAt: string | null;
      isPublic: boolean;
      canAccessStudio: boolean;
      canUseStudioApp: boolean;
      studioLaunchAt: string | null;
      studioEarlyAccessGrantedAt: string | null;
      studioOnboardingCompleted: boolean;
      studioOnboardingDismissed: boolean;
      studioOnboardingStep: string | null;
      subscription: { tier: string; status: string } | null;
    }>('/creators/me/status');
  }

  getCreatorSetup() {
    return this.request<{
      status: string;
      creatorId: string | null;
      memberProfile: Record<string, unknown> | null;
      draft: Record<string, unknown> | null;
      checklist: Record<string, boolean> | null;
      introPostId: string | null;
      activatedAt: string | null;
      activationSource: 'member_upgrade' | 'direct_signup';
    }>('/creators/me/setup');
  }

  startCreatorSetup() {
    return this.request('/creators/setup/start', { method: 'POST' });
  }

  saveCreatorSetupProfile(data: Record<string, unknown>) {
    return this.request('/creators/setup/profile', { method: 'PATCH', body: JSON.stringify(data) });
  }

  updateCreatorProfile(data: Record<string, unknown>) {
    return this.request('/creators/me', { method: 'PATCH', body: JSON.stringify(data) });
  }

  markCreatorPreviewReady() {
    return this.request('/creators/setup/preview', { method: 'POST' });
  }

  getCreatorPreview() {
    return this.request<{ status: string; preview: Record<string, unknown> }>(
      '/creators/me/preview',
    );
  }

  getCreatorSite() {
    return this.request<CreatorSiteResponse>('/creator/site');
  }

  updateCreatorSite(data: {
    themePreset?: string;
    themeTokens?: Record<string, string> | null;
    siteTitle?: string | null;
    seoTitle?: string | null;
    seoDescription?: string | null;
    ogImageAssetId?: string | null;
    hidePlatformAttribution?: boolean;
  }) {
    return this.request<CreatorSiteResponse>('/creator/site', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  updateCreatorSiteTheme(data: {
    themePreset: string;
    themeTokens?: Record<string, string> | null;
  }) {
    return this.request<CreatorSiteResponse>('/creator/site/theme', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  seedCreatorSiteFromProfile() {
    return this.request<CreatorSiteResponse>('/creator/site/seed-from-profile', {
      method: 'POST',
    });
  }

  generateCreatorSiteAiCopyDraft(data: { sectionType: 'hero' | 'about' | 'rich_text' }) {
    return this.request<{
      generatedAt: string;
      fields: Record<string, string>;
    }>('/creator/site/ai-copy-draft', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getCreatorSiteTemplates() {
    return this.request<{
      templates: Array<{
        id: 'coach' | 'creator' | 'educator' | 'agency' | 'speaker';
        label: string;
        description: string;
        themePreset: string;
        subpageCount: number;
      }>;
    }>('/creator/site/templates');
  }

  applyCreatorSiteTemplate(data: {
    templateId: 'coach' | 'creator' | 'educator' | 'agency' | 'speaker';
  }) {
    return this.request<CreatorSiteResponse>('/creator/site/apply-template', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  patchCreatorSiteSections(data: {
    pageId?: string;
    sections: Array<{
      id?: string;
      type: CreatorSiteSectionType;
      variant?: string;
      visible?: boolean;
      visibility?: CreatorSiteSectionVisibility;
      content: Record<string, unknown>;
      bindings?: Record<string, unknown> | null;
    }>;
  }) {
    return this.request<CreatorSiteResponse>('/creator/site/sections', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  createCreatorSitePage(data: { slug: string; title?: string | null }) {
    return this.request<CreatorSiteResponse>('/creator/site/pages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateCreatorSitePage(
    pageId: string,
    data: {
      slug?: string;
      title?: string | null;
      seoTitle?: string | null;
      seoDescription?: string | null;
      status?: 'draft' | 'published';
      sortOrder?: number;
    },
  ) {
    return this.request<CreatorSiteResponse>(`/creator/site/pages/${encodeURIComponent(pageId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteCreatorSitePage(pageId: string) {
    return this.request<CreatorSiteResponse>(`/creator/site/pages/${encodeURIComponent(pageId)}`, {
      method: 'DELETE',
    });
  }

  publishCreatorSite() {
    return this.request<CreatorSiteResponse>('/creator/site/publish', { method: 'POST' });
  }

  unpublishCreatorSite() {
    return this.request<{ unpublished: boolean; siteId?: string }>('/creator/site/unpublish', {
      method: 'POST',
    });
  }

  getPublicCreatorSite(username: string) {
    return this.request<PublicCreatorSiteResponse>(
      `/public/sites/${encodeURIComponent(username)}`,
    );
  }

  getPublicCreatorSitePage(username: string, pageSlug: string) {
    return this.request<PublicCreatorSitePageResponse>(
      `/public/sites/${encodeURIComponent(username)}/pages/${encodeURIComponent(pageSlug)}`,
    );
  }

  submitCreatorSiteLead(
    username: string,
    input:
      | {
          captureType?: 'newsletter';
          email: string;
          sectionId: string;
          consentAccepted: boolean;
          consentTextVersion: string;
          source: 'creator_site';
          honeypot?: string;
        }
      | {
          captureType: 'inquiry';
          name: string;
          email: string;
          message: string;
          sectionId: string;
          consentAccepted: boolean;
          consentTextVersion: string;
          source: 'creator_site';
          inquiryType?: string;
          honeypot?: string;
        },
  ) {
    return this.request<{ accepted: true; contactId?: string }>(
      `/public/sites/${encodeURIComponent(username)}/leads`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  }

  trackCreatorSiteEvent(
    username: string,
    input: {
      eventName:
        | 'creator_site_view'
        | 'creator_site_section_view'
        | 'creator_site_cta_click'
        | 'creator_site_offer_click'
        | 'creator_site_lead_submit';
      pageSlug?: string;
      sectionId?: string;
      sectionType?: string;
      offerId?: string;
      ctaType?: string;
      targetUrl?: string;
      captureType?: string;
      device?: 'mobile' | 'desktop' | 'tablet' | 'unknown';
      utmSource?: string | null;
      utmMedium?: string | null;
      utmCampaign?: string | null;
      utmTerm?: string | null;
      utmContent?: string | null;
    },
  ) {
    return this.request<{ ok: boolean }>(
      `/public/sites/${encodeURIComponent(username)}/events`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      },
    );
  }

  getCreatorSiteAnalytics() {
    return this.request<{
      rangeDays: number;
      views: number;
      leads: number;
      topCtaClicks: Array<{
        sectionId: string;
        sectionType: string | null;
        eventName: string;
        count: number;
      }>;
      topSectionViews: Array<{
        sectionId: string;
        sectionType: string | null;
        count: number;
      }>;
      attributedRevenueCents: number;
      siteStatus: 'draft' | 'published';
    }>('/creator/site/analytics');
  }

  startCreatorCheckout(returnUrl: string, planSlug = 'creator_plan_annual') {
    return this.request<{
      sessionId: string;
      clientSecret: string;
      publishableKey: string | null;
      devStub: boolean;
    }>('/creators/checkout', {
      method: 'POST',
      body: JSON.stringify({ returnUrl, planSlug }),
    });
  }

  confirmCreatorCheckout(sessionId: string) {
    return this.request('/billing/checkout/confirm', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  syncCreatorCheckoutSession(sessionId: string) {
    return this.request<{ ok: boolean; status?: string }>('/billing/checkout/sync-session', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  updateCreatorChecklist(data: Record<string, boolean>) {
    return this.request('/creators/me/checklist', { method: 'PATCH', body: JSON.stringify(data) });
  }

  setCreatorIntroPost(postId: string) {
    return this.request('/creators/me/intro-post', {
      method: 'POST',
      body: JSON.stringify({ postId }),
    });
  }

  listAdminCreators(params?: { q?: string; status?: string; limit?: number; offset?: number }) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.status) search.set('status', params.status);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return this.request<{
      items: unknown[];
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/creators${qs ? `?${qs}` : ''}`);
  }

  getRecommendedFollows(limit = 10) {
    const params = limit === 10 ? '' : `?limit=${limit}`;
    return this.request<RecommendedFollow[]>(`/discovery/recommended-follows${params}`);
  }

  getFeaturedCreators(limit = 5) {
    const params = limit === 5 ? '' : `?limit=${limit}`;
    return this.request<FeaturedCreator[]>(`/discovery/featured-creators${params}`);
  }

  /** @deprecated Use getRecommendedFollows() for members + creators. */
  getRecommendedCreatorsForYou() {
    return this.request<unknown[]>('/creators/recommended');
  }

  recordStudioOpened() {
    return this.request('/creators/me/studio-opened', { method: 'POST' });
  }

  completeStudioOnboarding(data: {
    primaryPersona: string;
    secondaryPersonas?: string[];
    monetizationInterests: string[];
    countryCode?: string;
    defaultCurrency?: string;
    topics?: string[];
  }) {
    return this.request('/creators/me/studio-onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  saveStudioOnboardingProgress(data: {
    step: string;
    primaryPersona?: string;
    secondaryPersonas?: string[];
    monetizationInterests?: string[];
    countryCode?: string;
    defaultCurrency?: string;
    topics?: string[];
  }) {
    return this.request('/creators/me/studio-onboarding/progress', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  dismissStudioOnboarding() {
    return this.request<{
      status: string;
      studioOnboardingCompleted: boolean;
      studioOnboardingDismissed: boolean;
    }>('/creators/me/studio-onboarding/dismiss', { method: 'POST' });
  }

  recoverBilling() {
    return this.request('/billing/recover', { method: 'POST' });
  }

  getBillingConfig() {
    return this.request<{ devStub: boolean; publishableKey: string | null }>('/billing/config');
  }

  getBillingPlans() {
    return this.request<
      Array<{
        id: string;
        slug: string;
        name: string;
        description: string | null;
        amountCents: number;
        currency: string;
        interval: 'month' | 'year';
        sortOrder: number;
      }>
    >('/billing/plans');
  }

  createCreatorCheckoutSession(input: {
    planSlug: string;
    returnUrl?: string;
    promotionCode?: string;
    flow?: 'direct_creator_signup' | 'member_upgrade';
  }) {
    return this.request<{
      sessionId: string;
      clientSecret: string;
      publishableKey: string | null;
      devStub: boolean;
    }>('/billing/checkout/creator-plan', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  createBillingPortalSession(returnUrl?: string, flow: 'manage' | 'cancel' = 'manage') {
    return this.request<{ url: string }>('/billing/portal', {
      method: 'POST',
      body: JSON.stringify({ ...(returnUrl ? { returnUrl } : {}), flow }),
    });
  }

  getBillingSubscription() {
    return this.request<{
      tier: string;
      status: string;
      planName: string;
      planSlug: string | null;
      billingInterval: 'month' | 'year' | null;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
      stripeCustomerId: string | null;
      paymentMethodLabel: string | null;
      latestInvoiceUrl?: string | null;
      nextChargeAmountCents?: number | null;
      currency?: string | null;
    }>('/billing/subscription');
  }

  disableAdminCreator(creatorId: string) {
    return this.request(`/admin/creators/${encodeURIComponent(creatorId)}/disable`, {
      method: 'POST',
    });
  }

  enableAdminCreator(creatorId: string) {
    return this.request(`/admin/creators/${encodeURIComponent(creatorId)}/enable`, {
      method: 'POST',
    });
  }

  updateAdminCreatorBadge(creatorId: string, badge: string | null) {
    return this.request(`/admin/creators/${encodeURIComponent(creatorId)}/badge`, {
      method: 'PATCH',
      body: JSON.stringify({ badge }),
    });
  }

  updateAdminCreatorFeatured(
    creatorId: string,
    data: { featured: boolean; featuredOrder?: number },
  ) {
    return this.request(`/admin/creators/${creatorId}/featured`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  updateAdminCreatorStudioEarlyAccess(creatorId: string, granted: boolean) {
    return this.request(`/admin/creators/${encodeURIComponent(creatorId)}/studio-early-access`, {
      method: 'PATCH',
      body: JSON.stringify({ granted }),
    });
  }

  getAdminCreatorAuditEvents(creatorId: string, params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<unknown>>(
      `/admin/creators/${encodeURIComponent(creatorId)}/audit-events${query ? `?${query}` : ''}`,
    );
  }

  getAdminCreatorConnectHealth(creatorId: string) {
    return this.request<{
      creatorId: string;
      userId: string;
      email: string;
      username: string | null;
      displayName: string | null;
      connect: {
        connected: boolean;
        accountId: string | null;
        chargesEnabled: boolean;
        payoutsEnabled: boolean;
        detailsSubmitted: boolean;
        ready: boolean;
        commerceRestricted: boolean;
        commerceRestrictedAt: string | null;
        commerceRestrictReason: string | null;
        conflicts: string[];
      };
      commerce: {
        paidCommunities: number;
        paidCourses: number;
        paidServiceOffers: number;
      };
      ledger: {
        lifetimeNetCents: number;
        saleCount: number;
      };
      riskSignals: string[];
    }>(`/admin/creators/${encodeURIComponent(creatorId)}/connect-health`);
  }

  restrictAdminCreatorCommerce(creatorId: string, reason?: string) {
    return this.request<{ ok: boolean }>(
      `/admin/creators/${encodeURIComponent(creatorId)}/restrict-commerce`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  }

  unrestrictAdminCreatorCommerce(creatorId: string) {
    return this.request<{ ok: boolean }>(
      `/admin/creators/${encodeURIComponent(creatorId)}/unrestrict-commerce`,
      { method: 'POST' },
    );
  }

  banAdminUser(userId: string) {
    return this.request(`/admin/users/${encodeURIComponent(userId)}/ban`, { method: 'POST' });
  }

  unbanAdminUser(userId: string) {
    return this.request(`/admin/users/${encodeURIComponent(userId)}/unban`, { method: 'POST' });
  }

  grantAdminCompCreatorAccess(userId: string) {
    return this.request(`/admin/users/${encodeURIComponent(userId)}/comp-creator-access`, {
      method: 'POST',
    });
  }

  revokeAdminCompCreatorAccess(userId: string) {
    return this.request(`/admin/users/${encodeURIComponent(userId)}/comp-creator-access/revoke`, {
      method: 'POST',
    });
  }

  getAdminUserAuditEvents(userId: string, params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<unknown>>(
      `/admin/users/${encodeURIComponent(userId)}/audit-events${query ? `?${query}` : ''}`,
    );
  }

  getAdminAuditEvents(params?: {
    actor?: string;
    action?: string;
    target?: string;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.actor) query.set('actor', params.actor);
    if (params?.action) query.set('action', params.action);
    if (params?.target) query.set('target', params.target);
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    const search = query.toString();
    return this.request<{
      items: Array<{
        id: string;
        actorId: string;
        actionType: string;
        targetId: string | null;
        metadata: Record<string, unknown> | null;
        timestamp: string;
        actor: { id: string; role: string };
      }>;
      pageInfo: { nextCursor: string | null; hasMore: boolean };
    }>(`/admin/audit-events${search ? `?${search}` : ''}`);
  }

  getDashboard() {
    return this.request('/creators/dashboard');
  }

  fetchLinkPreview(url: string) {
    return this.request<{
      url: string;
      title?: string;
      description?: string;
      imageUrl?: string;
      hostname?: string;
      previewKind?: 'link' | 'article';
      readTimeLabel?: string;
    }>(`/content/link-preview?url=${encodeURIComponent(url)}`);
  }

  createArticle(data?: { title?: string; subtitle?: string }) {
    return this.request<ArticleRecord>('/content/articles', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  getMyArticles(status?: 'draft' | 'published') {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request<ArticleRecord[]>(`/content/articles/mine${query}`);
  }

  getMyArticle(id: string) {
    return this.request<ArticleRecord>(`/content/articles/by-id/${encodeURIComponent(id)}`);
  }

  getArticleBySlug(slug: string) {
    return this.request<ArticleRecord>(`/content/articles/${encodeURIComponent(slug)}`);
  }

  getPublicArticles(username: string, cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit != null) params.set('limit', String(limit));
    const q = params.toString();
    return this.request<{
      items: ArticleRecord[];
      nextCursor: string | null;
      hidden?: boolean;
    }>(`/content/u/${encodeURIComponent(username)}/articles${q ? `?${q}` : ''}`);
  }

  updateArticle(
    id: string,
    data: {
      title?: string;
      subtitle?: string | null;
      excerpt?: string;
      coverUrl?: string | null;
      coverKey?: string | null;
      bodyJson?: Record<string, unknown>;
      bodyText?: string;
      seoTitle?: string | null;
      seoDescription?: string | null;
      slug?: string | null;
      commentPolicy?: 'everyone' | 'off';
      topics?: string[];
    },
  ) {
    return this.request<ArticleRecord>(`/content/articles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  publishArticle(id: string, data?: { slug?: string }) {
    return this.request<ArticleRecord>(`/content/articles/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  createCommunity(data?: { title?: string; about?: string; rules?: string }) {
    return this.request<CommunityRecord>('/circles', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  listCommunities(params?: { search?: string; topic?: string; cursor?: string; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.topic) query.set('topic', params.topic);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<PaginatedCommunitiesResponse>(`/circles${suffix}`);
  }

  listFeaturedCommunities(limit?: number) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: CommunityRecord[] }>(`/circles/featured${suffix}`);
  }

  listTrendingCommunities(limit?: number) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: Array<
        CommunityRecord & {
          trendingScore?: number;
          joins7d?: number;
          wau?: number;
        }
      >;
    }>(`/growth/circles/trending${suffix}`);
  }

  listCommunityTopics() {
    return this.request<CommunityDirectoryTopic[]>('/circles/topics');
  }

  getCreatorCommunities(username: string) {
    return this.request<{ items: CommunityRecord[] }>(
      `/circles/by-creator/${encodeURIComponent(username)}`,
    );
  }

  getMyCommunities() {
    return this.request<CommunityRecord[]>('/circles/mine');
  }

  getOwnedCommunity(id: string) {
    return this.request<CommunityRecord>(`/circles/by-id/${encodeURIComponent(id)}`);
  }

  getCirclePublishChecklist(id: string) {
    return this.request<CirclePublishChecklist>(
      `/circles/by-id/${encodeURIComponent(id)}/publish-checklist`,
    );
  }

  getCommunityOwnershipTransfer(communityId: string) {
    return this.request<{ transfer: CommunityOwnershipTransferRecord | null }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer`,
    );
  }

  listIncomingCommunityOwnershipTransfers() {
    return this.request<{ items: CommunityOwnershipTransferRecord[] }>(
      '/circles/ownership-transfers/incoming',
    );
  }

  initiateCommunityOwnershipTransfer(
    communityId: string,
    data: { targetUserId: string; confirmNote?: string },
  ) {
    return this.request<{ transfer: CommunityOwnershipTransferRecord }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  cancelCommunityOwnershipTransfer(communityId: string) {
    return this.request<{ transfer: CommunityOwnershipTransferRecord }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer`,
      { method: 'DELETE' },
    );
  }

  acceptCommunityOwnershipTransfer(communityId: string) {
    return this.request<{ transfer: CommunityOwnershipTransferRecord }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer/accept`,
      { method: 'POST' },
    );
  }

  getCommunityOwnershipStripeReconnect(communityId: string) {
    return this.request<{
      transfer: CommunityOwnershipTransferRecord | null;
      tiers: Array<{
        id: string;
        name: string;
        slug: string;
        billingType: string;
        monthlyPriceCents?: number | null;
        annualPriceCents?: number | null;
        lifetimePriceCents?: number | null;
        stripeProductId?: string | null;
        stripeMonthlyPriceId?: string | null;
        stripeAnnualPriceId?: string | null;
        stripeLifetimePriceId?: string | null;
      }>;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer/stripe-reconnect`);
  }

  completeCommunityOwnershipStripeReconnect(
    communityId: string,
    data: {
      tiers: Array<{
        tierId: string;
        stripeProductId?: string | null;
        stripeMonthlyPriceId?: string | null;
        stripeAnnualPriceId?: string | null;
        stripeLifetimePriceId?: string | null;
      }>;
    },
  ) {
    return this.request<{ transfer: CommunityOwnershipTransferRecord }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/ownership-transfer/stripe-reconnect`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  listCreatorDomains() {
    return this.request<{ items: CreatorDomainRecord[] }>('/domains');
  }

  listCreatorDomainTargetOptions() {
    return this.request<CreatorDomainTargetOptions>('/domains/target-options');
  }

  createCreatorDomain(data: {
    hostname: string;
    targetType: string;
    targetId?: string;
    redirectMode?: 'none' | 'to_www' | 'to_apex';
    pairWww?: boolean;
  }) {
    return this.request<CreatorDomainRecord>('/domains', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getCreatorDomain(domainId: string) {
    return this.request<CreatorDomainRecord>(`/domains/${encodeURIComponent(domainId)}`);
  }

  updateCreatorDomainBinding(
    domainId: string,
    data: {
      targetType: string;
      targetId?: string | null;
      redirectMode?: 'none' | 'to_www' | 'to_apex';
    },
  ) {
    return this.request<CreatorDomainRecord>(
      `/domains/${encodeURIComponent(domainId)}/binding`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  verifyCreatorDomain(domainId: string) {
    return this.request<CreatorDomainRecord & { devAutoVerified?: boolean }>(
      `/domains/${encodeURIComponent(domainId)}/verify`,
      { method: 'POST' },
    );
  }

  healthCheckCreatorDomain(domainId: string) {
    return this.request<CreatorDomainRecord>(
      `/domains/${encodeURIComponent(domainId)}/health`,
      { method: 'POST' },
    );
  }

  disconnectCreatorDomain(domainId: string) {
    return this.request<{ ok: boolean }>(`/domains/${encodeURIComponent(domainId)}`, {
      method: 'DELETE',
    });
  }

  getCircleDomain(circleId: string) {
    return this.request<{
      configured: boolean;
      hostname: string | null;
      status: string | null;
      verifiedAt: string | null;
      domainId?: string | null;
      instructions: {
        txtHost: string;
        txtValue: string;
        note: string;
        cnameHost?: string | null;
        cnameTarget?: string | null;
        apexIps?: string[];
        apexAliasTarget?: string | null;
        cloudflareNote?: string;
      } | null;
    }>(`/domains/circles/${encodeURIComponent(circleId)}`);
  }

  upsertCircleDomain(circleId: string, data: { hostname: string }) {
    return this.request(`/domains/circles/${encodeURIComponent(circleId)}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  verifyCircleDomain(circleId: string) {
    return this.request(`/domains/circles/${encodeURIComponent(circleId)}/verify`, {
      method: 'POST',
    });
  }

  disableCircleDomain(circleId: string) {
    return this.request(`/domains/circles/${encodeURIComponent(circleId)}`, {
      method: 'DELETE',
    });
  }

  resolveCircleDomain(hostname: string) {
    return this.resolveCreatorDomain(hostname);
  }

  resolveCreatorDomain(hostname: string) {
    const params = new URLSearchParams({ hostname });
    return this.request<{
      found: boolean;
      slug?: string;
      communityId?: string;
      title?: string;
      canonicalPath?: string;
      canonicalUrl?: string;
      loamifyFallbackPath?: string;
      targetType?: string;
      targetId?: string | null;
      redirectTo?: string;
      hostname?: string;
      creatorId?: string;
    }>(`/domains/resolve?${params.toString()}`);
  }

  adminListCreatorDomains(params?: {
    status?: string;
    q?: string;
    healthIssue?: 'errors' | 'tls' | 'stale' | 'unbound' | 'incidents';
  }) {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    if (params?.healthIssue) query.set('healthIssue', params.healthIssue);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<{ items: AdminCreatorDomainRecord[] }>(`/admin/domains${suffix}`);
  }

  adminDomainsHealthOverview() {
    return this.request<AdminDomainsHealthOverview>('/admin/domains/health/overview');
  }

  adminHealthCheckCreatorDomain(domainId: string) {
    return this.request<CreatorDomainRecord>(
      `/admin/domains/${encodeURIComponent(domainId)}/health-check`,
      { method: 'POST' },
    );
  }

  adminRunDomainsHealthSweep() {
    return this.request<{ checked: number }>('/admin/domains/cron/health', { method: 'POST' });
  }

  adminDetachCreatorDomain(domainId: string, reason?: string) {
    return this.request(`/admin/domains/${encodeURIComponent(domainId)}/detach`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  adminSearchAudienceContacts(params: {
    q: string;
    creatorId?: string;
    cursor?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    query.set('q', params.q);
    if (params.creatorId) query.set('creatorId', params.creatorId);
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit != null) query.set('limit', String(params.limit));
    return this.request<{
      items: AdminAudienceContactRecord[];
      pageInfo: { nextCursor: string | null };
    }>(`/admin/audience/contacts?${query.toString()}`);
  }

  adminAudienceContact(contactId: string) {
    return this.request<AdminAudienceContactDetail>(
      `/admin/audience/contacts/${encodeURIComponent(contactId)}`,
    );
  }

  getCirclePlatformBacklog() {
    return this.request<{
      contractVersion: string;
      items: Array<{
        id: string;
        title: string;
        status: string;
        milestone: string;
        summary: string;
      }>;
    }>('/circles/platform/backlog');
  }

  getAudienceCrmSummary() {
    return this.request<{
      total: number;
      recent30d: number;
      buyers: number;
      members: number;
    }>('/audience/crm/summary');
  }

  listAudienceCrmContacts(params?: {
    search?: string;
    relationshipType?: string;
    tag?: string;
    cursor?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.relationshipType) query.set('relationshipType', params.relationshipType);
    if (params?.tag) query.set('tag', params.tag);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit != null) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<{ items: AudienceCrmContactRecord[]; nextCursor: string | null }>(
      `/audience/crm/contacts${suffix}`,
    );
  }

  getAudienceCrmContact(id: string) {
    return this.request<AudienceCrmContactDetail>(
      `/audience/crm/contacts/${encodeURIComponent(id)}`,
    );
  }

  listAudienceTags() {
    return this.request<{ items: Array<{ id: string; name: string; contactCount: number }> }>(
      '/audience/crm/tags',
    );
  }

  createAudienceTag(name: string) {
    return this.request<{ id: string; name: string }>('/audience/crm/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  addAudienceContactTag(contactId: string, tagName: string) {
    return this.request<{ ok: boolean }>(
      `/audience/crm/contacts/${encodeURIComponent(contactId)}/tags`,
      {
        method: 'POST',
        body: JSON.stringify({ tagName }),
      },
    );
  }

  removeAudienceContactTag(contactId: string, tagName: string) {
    return this.request<{ ok: boolean }>(
      `/audience/crm/contacts/${encodeURIComponent(contactId)}/tags/${encodeURIComponent(tagName)}`,
      { method: 'DELETE' },
    );
  }

  getAudienceSegmentCount(segmentId: string) {
    return this.request<{ count: number }>(
      `/audience/crm/segments/${encodeURIComponent(segmentId)}/count`,
    );
  }

  listAudienceContactTimeline(
    contactId: string,
    params?: { cursor?: string; eventType?: string; limit?: number },
  ) {
    const query = new URLSearchParams();
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.eventType) query.set('eventType', params.eventType);
    if (params?.limit != null) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<{
      items: Array<{
        id: string;
        eventType: string;
        sourceModule: string;
        payload: unknown;
        occurredAt: string;
      }>;
      nextCursor: string | null;
    }>(`/audience/crm/contacts/${encodeURIComponent(contactId)}/timeline${suffix}`);
  }

  updateAudienceContactConsent(
    contactId: string,
    data: { consentProductUpdates?: boolean; consentPromotional?: boolean },
  ) {
    return this.request<{ ok: boolean }>(
      `/audience/crm/contacts/${encodeURIComponent(contactId)}/consent`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  listAudienceSegments() {
    return this.request<{ items: AudienceSegmentRecord[] }>('/audience/crm/segments');
  }

  saveAudienceSegment(name: string, filter: Record<string, unknown>) {
    return this.request<AudienceSegmentRecord>('/audience/crm/segments', {
      method: 'POST',
      body: JSON.stringify({ name, filter }),
    });
  }

  exportAudienceSegment(segmentId: string) {
    return this.request<{ filename: string; csv: string }>(
      `/audience/crm/segments/${encodeURIComponent(segmentId)}/export`,
    );
  }

  exportAudienceCrm() {
    return this.request<{ filename: string; csv: string }>('/audience/crm/export');
  }

  getCircleAnalytics(id: string) {
    return this.request<CircleAnalyticsResponse>(
      `/circles/by-id/${encodeURIComponent(id)}/analytics`,
    );
  }

  getCreatorAnalyticsOverview(range: CreatorAnalyticsRange) {
    return this.request<CreatorAnalyticsOverview>(
      `/analytics/overview?range=${encodeURIComponent(range)}`,
    );
  }

  getCreatorAnalyticsCsvUrl(range: CreatorAnalyticsRange) {
    return `${resolveApiBaseUrl()}/api/analytics/overview.csv?range=${encodeURIComponent(range)}`;
  }

  exportCircleRevenueCsv(id: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/circles/by-id/${encodeURIComponent(id)}/analytics/revenue-export`,
    );
  }

  listOwnedCommunityMembers(
    id: string,
    params?: { search?: string; cursor?: string; limit?: number },
  ) {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<{ items: OwnedCommunityMemberRecord[]; nextCursor: string | null }>(
      `/circles/by-id/${encodeURIComponent(id)}/members${suffix}`,
    );
  }

  getCommunityBySlug(slug: string) {
    return this.request<CommunityRecord>(`/circles/${encodeURIComponent(slug)}`);
  }

  getCommunityMembership(slug: string) {
    return this.request<CommunityMembershipStatusResponse>(
      `/circles/${encodeURIComponent(slug)}/membership`,
    );
  }

  getMyCommunityAppeal(slug: string) {
    return this.request<{ appeal: CommunityModerationAppealRecord | null }>(
      `/circles/${encodeURIComponent(slug)}/appeals/me`,
    );
  }

  submitCommunityAppeal(
    slug: string,
    data: {
      appealType: 'ban' | 'content_removal';
      message: string;
      targetType?: string;
      targetId?: string;
    },
  ) {
    return this.request<CommunityModerationAppealRecord>(
      `/circles/${encodeURIComponent(slug)}/appeals`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  listCommunityAppeals(communityId: string) {
    return this.request<{
      items: Array<
        CommunityModerationAppealRecord & {
          user?: {
            id: string;
            profile?: { username?: string | null; displayName?: string | null };
          };
          targetType?: string | null;
          targetId?: string | null;
        }
      >;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/moderation/appeals`);
  }

  resolveCommunityAppeal(
    communityId: string,
    appealId: string,
    data: { status: 'approved' | 'denied'; resolutionNote?: string },
  ) {
    return this.request<CommunityModerationAppealRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/appeals/${encodeURIComponent(appealId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  joinCommunity(
    slug: string,
    data: {
      acceptGuidelines: boolean;
      tierId?: string;
      inviteToken?: string;
      referralCode?: string;
      affiliateCode?: string;
      intakeAnswers?: Record<string, string>;
      profileFieldAnswers?: Record<string, string>;
      confirmAdultContent?: boolean;
    },
  ) {
    return this.request<{ membership: CommunityMembershipRecord; alreadyMember: boolean }>(
      `/circles/${encodeURIComponent(slug)}/join`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  getCommunityMemberArea(slug: string) {
    return this.request<{
      ok: boolean;
      communityId: string;
      slug: string | null;
      role: string;
      moderatorCapabilities: string[];
      mapOptInCount: number;
      message: string;
    }>(`/circles/${encodeURIComponent(slug)}/member-area`);
  }

  getConnectStatus() {
    return this.request<ConnectStatusResponse>('/commerce/connect/status');
  }

  startConnectOnboarding(data?: { returnUrl?: string; refreshUrl?: string }) {
    return this.request<{ url: string; accountId: string }>('/commerce/connect/onboarding', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  refreshConnectStatus() {
    return this.request<ConnectStatusResponse>('/commerce/connect/refresh', { method: 'POST' });
  }

  createExpressDashboardLink(data?: { returnUrl?: string }) {
    return this.request<{ url: string }>('/commerce/connect/express-link', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  getPayoutSummary() {
    return this.request<PayoutSummaryResponse>('/commerce/payouts/summary');
  }

  getPayoutLedger(query: PayoutLedgerQuery = {}) {
    const params = new URLSearchParams();
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.eventType) params.set('eventType', query.eventType);
    if (query.stream) params.set('stream', query.stream);
    if (query.search?.trim()) params.set('search', query.search.trim());
    const qs = params.toString();
    return this.request<PayoutLedgerResponse>(`/commerce/payouts/ledger${qs ? `?${qs}` : ''}`);
  }

  exportPayoutLedgerCsv(query: Omit<PayoutLedgerQuery, 'limit'> = {}) {
    const params = new URLSearchParams();
    if (query.eventType) params.set('eventType', query.eventType);
    if (query.stream) params.set('stream', query.stream);
    if (query.search?.trim()) params.set('search', query.search.trim());
    const qs = params.toString();
    return this.request<PayoutLedgerExportResponse>(
      `/commerce/payouts/ledger/export${qs ? `?${qs}` : ''}`,
    );
  }

  getPayoutWithdrawals(limit = 20) {
    return this.request<PayoutWithdrawalsResponse>(`/commerce/payouts/withdrawals?limit=${limit}`);
  }

  requestPayoutWithdraw(amountCents?: number) {
    return this.request<PayoutWithdrawResponse>('/commerce/payouts/withdraw', {
      method: 'POST',
      body: JSON.stringify(amountCents != null ? { amountCents } : {}),
    });
  }

  createCircleMembershipCheckout(
    slug: string,
    data: {
      acceptGuidelines: boolean;
      acceptPlatformTerms?: boolean;
      acceptCreatorPolicies?: boolean;
      tierId?: string;
      interval?: 'month' | 'year';
      couponCode?: string;
      inviteToken?: string;
      returnUrl?: string;
      referralCode?: string;
      affiliateCode?: string;
      profileFieldAnswers?: Record<string, string>;
      confirmAdultContent?: boolean;
    },
  ) {
    return this.request<CircleMembershipCheckoutResponse>(
      `/commerce/circles/${encodeURIComponent(slug)}/membership/checkout`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  cancelCircleMembership(slug: string, data?: { returnUrl?: string }) {
    return this.request<{ url: string }>(
      `/commerce/circles/${encodeURIComponent(slug)}/membership/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  pauseCircleMembership(slug: string) {
    return this.request<{
      ok: boolean;
      subscriptionPausedAt?: string | null;
      subscriptionPauseResumesAt?: string | null;
    }>(`/commerce/circles/${encodeURIComponent(slug)}/membership/pause`, {
      method: 'POST',
    });
  }

  resumeCircleMembership(slug: string) {
    return this.request<{ ok: boolean }>(
      `/commerce/circles/${encodeURIComponent(slug)}/membership/resume`,
      { method: 'POST' },
    );
  }

  completeDevCircleMembershipCheckout(sessionId: string) {
    return this.request<{ ok: boolean }>('/commerce/dev/complete-membership-checkout', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  getCircleMembershipTiers(slug: string) {
    return this.request<{
      currency: string;
      accessType: string;
      items: CircleMembershipTierRecord[];
    }>(`/commerce/circles/${encodeURIComponent(slug)}/membership-tiers`);
  }

  listCircleMembershipTiers(circleId: string) {
    return this.request<{ items: CircleMembershipTierRecord[] }>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-tiers`,
    );
  }

  replaceCircleMembershipTiers(
    circleId: string,
    tiers: Array<
      Omit<
        CircleMembershipTierRecord,
        'id' | 'communityId' | 'activeMemberCount' | 'atCapacity' | 'accessRank'
      > & {
        id?: string;
        accessRank?: number;
      }
    >,
    membershipPolicyText?: string | null,
    policies?: ReplaceCircleMembershipPoliciesPayload,
  ) {
    return this.request<{ items: CircleMembershipTierRecord[] }>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-tiers`,
      {
        method: 'PUT',
        body: JSON.stringify({
          tiers,
          ...(membershipPolicyText !== undefined ? { membershipPolicyText } : {}),
          ...(policies?.membershipRefundPolicyText !== undefined
            ? { membershipRefundPolicyText: policies.membershipRefundPolicyText }
            : {}),
          ...(policies?.membershipCancellationPolicyText !== undefined
            ? { membershipCancellationPolicyText: policies.membershipCancellationPolicyText }
            : {}),
          ...(policies?.membershipSellerOfRecordPolicyText !== undefined
            ? { membershipSellerOfRecordPolicyText: policies.membershipSellerOfRecordPolicyText }
            : {}),
        }),
      },
    );
  }

  listCircleMembershipCoupons(circleId: string) {
    return this.request<{ items: CircleMembershipCouponRecord[] }>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-coupons`,
    );
  }

  upsertCircleMembershipCoupon(
    circleId: string,
    data: Omit<CircleMembershipCouponRecord, 'id' | 'communityId' | 'redemptionCount'> & {
      id?: string;
    },
  ) {
    return this.request<CircleMembershipCouponRecord>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-coupons`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  joinCircleMembershipWaitlist(
    slug: string,
    data: {
      tierId: string;
      message?: string;
      inviteToken?: string;
      intakeAnswers?: Record<string, string>;
      profileFieldAnswers?: Record<string, string>;
      confirmAdultContent?: boolean;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/commerce/circles/${encodeURIComponent(slug)}/membership-waitlist`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  listCircleMembershipWaitlist(circleId: string) {
    return this.request<{ items: CircleMembershipWaitlistEntryRecord[] }>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-waitlist`,
    );
  }

  getPublicCommunityJoinQuestions(slug: string) {
    return this.request<{ items: CommunityJoinQuestionRecord[] }>(
      `/circles/${encodeURIComponent(slug)}/join-questions`,
    );
  }

  getPublicCommunityMemberFields(slug: string) {
    return this.request<{ items: CommunityMemberFieldRecord[] }>(
      `/circles/${encodeURIComponent(slug)}/member-fields`,
    );
  }

  updateMyCommunityMembership(
    slug: string,
    data: {
      profileFieldAnswers?: Record<string, string>;
      hideMembershipFromProfile?: boolean;
      showLocationOnMap?: boolean;
      locationCity?: string | null;
      locationRegion?: string | null;
    },
  ) {
    return this.request<{
      hideMembershipFromProfile: boolean;
      profileFieldAnswers: Record<string, string> | null;
      showLocationOnMap: boolean;
      locationCity: string | null;
      locationRegion: string | null;
      locationLatitude: number | null;
      locationLongitude: number | null;
    }>(`/circles/${encodeURIComponent(slug)}/membership/settings`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getProfileMemberCommunities(username: string) {
    return this.request<{
      items: Array<{
        id: string;
        slug: string | null;
        title: string;
        about: string | null;
        coverUrl: string | null;
        joinMode?: string;
        accessType?: string;
      }>;
    }>(`/circles/member-of/${encodeURIComponent(username)}`);
  }

  listCommunityJoinQuestions(communityId: string) {
    return this.request<{ items: CommunityJoinQuestionRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/join-questions`,
    );
  }

  replaceCommunityJoinQuestions(
    communityId: string,
    items: Array<{ id?: string; prompt: string; required?: boolean; enabled?: boolean }>,
  ) {
    return this.request<{ items: CommunityJoinQuestionRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/join-questions`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      },
    );
  }

  listCommunityMemberFields(communityId: string) {
    return this.request<{ items: CommunityMemberFieldRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/member-fields`,
    );
  }

  replaceCommunityMemberFields(
    communityId: string,
    items: Array<{
      id?: string;
      prompt: string;
      required?: boolean;
      enabled?: boolean;
      fieldType?: CommunityMemberFieldRecord['fieldType'];
      options?: string[];
      conditionalRules?: CommunityMemberFieldRecord['conditionalRules'];
    }>,
  ) {
    return this.request<{ items: CommunityMemberFieldRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/member-fields`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      },
    );
  }

  reviewCircleMembershipWaitlistEntry(
    circleId: string,
    entryId: string,
    status: 'pending' | 'approved' | 'rejected' | 'promoted',
    options?: { rejectionReason?: string; staffNotes?: string },
  ) {
    return this.request<Record<string, unknown>>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-waitlist/${encodeURIComponent(entryId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status, ...options }),
      },
    );
  }

  bulkReviewCircleMembershipWaitlist(
    circleId: string,
    data: {
      entryIds: string[];
      status: 'approved' | 'rejected';
      rejectionReason?: string;
      staffNotes?: string;
    },
  ) {
    return this.request<{ updated: number; items: Array<Record<string, unknown>> }>(
      `/commerce/circles/by-id/${encodeURIComponent(circleId)}/membership-waitlist/bulk-review`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  listCommunityModeratorRoles(communityId: string) {
    return this.request<{ items: CommunityModeratorRoleRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderator-roles`,
    );
  }

  createCommunityModeratorRole(
    communityId: string,
    data: { name: string; permissions?: string[] },
  ) {
    return this.request<CommunityModeratorRoleRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderator-roles`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  updateCommunityModeratorRole(
    communityId: string,
    roleId: string,
    data: { name?: string; permissions?: string[] },
  ) {
    return this.request<CommunityModeratorRoleRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderator-roles/${encodeURIComponent(roleId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteCommunityModeratorRole(communityId: string, roleId: string) {
    return this.request<{ ok: boolean }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderator-roles/${encodeURIComponent(roleId)}`,
      { method: 'DELETE' },
    );
  }

  assignCommunityModeratorRole(
    slug: string,
    userId: string,
    data: { moderatorRoleId?: string | null; permissions?: string[] },
  ) {
    return this.request<{ ok: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/moderator-role`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  acceptCommunityGuidelines(slug: string, scrolledToEnd: boolean) {
    return this.request<{ ok: boolean }>(`/circles/${encodeURIComponent(slug)}/guidelines/accept`, {
      method: 'POST',
      body: JSON.stringify({ scrolledToEnd }),
    });
  }

  getCommunityModerationQueue(communityId: string) {
    return this.request<{
      reports: Array<Record<string, unknown>>;
      appeals: Array<Record<string, unknown>>;
      autoFlagged: Array<Record<string, unknown>>;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/moderation/queue`);
  }

  bulkResolveCommunityReports(
    communityId: string,
    data: { reportIds: string[]; status: 'resolved' | 'dismissed' },
  ) {
    return this.request<{ updated: number }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/reports/bulk-resolve`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  reviewAutoFlaggedCommunityPost(
    communityId: string,
    postId: string,
    status: 'cleared' | 'actioned',
  ) {
    return this.request<Record<string, unknown>>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/auto-flagged/${encodeURIComponent(postId)}`,
      { method: 'PATCH', body: JSON.stringify({ status }) },
    );
  }

  exportCommunityModerationAuditCsv(communityId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/exports/moderation-audit`,
    );
  }

  listCircleCampaigns(circleId: string) {
    return this.request<{ items: Array<Record<string, unknown>> }>(
      `/marketing/circles/${encodeURIComponent(circleId)}/campaigns`,
    );
  }

  listNewsletterPublications() {
    return this.request<{ items: NewsletterPublicationRecord[] }>('/marketing/publications');
  }

  getMarketingAnalyticsReport(range: CreatorAnalyticsRange) {
    return this.request<MarketingAnalyticsReport>(
      `/marketing/analytics/report?range=${encodeURIComponent(range)}`,
    );
  }

  exportMarketingAnalyticsCsv(range: CreatorAnalyticsRange) {
    return this.request<{ filename: string; csv: string }>(
      `/marketing/analytics/report.csv?range=${encodeURIComponent(range)}`,
    );
  }

  createNewsletterPublication(data: {
    name: string;
    slug?: string;
    description?: string;
    logoUrl?: string;
    logoKey?: string;
    brandColor?: string;
    fromName?: string;
  }) {
    return this.request<NewsletterPublicationRecord>('/marketing/publications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getNewsletterPublication(id: string) {
    return this.request<NewsletterPublicationRecord>(
      `/marketing/publications/${encodeURIComponent(id)}`,
    );
  }

  updateNewsletterPublication(
    id: string,
    data: {
      name?: string;
      slug?: string;
      description?: string | null;
      logoUrl?: string | null;
      logoKey?: string | null;
      brandColor?: string | null;
      fromName?: string | null;
      requireDoubleOptIn?: boolean;
      physicalAddress?: string | null;
      leadMagnetUrl?: string | null;
      leadMagnetLabel?: string | null;
      accessMode?: 'free' | 'paid';
      paidInterval?: 'monthly' | 'annual' | null;
      paidAmountCents?: number | null;
      paidCurrency?: string | null;
      freePreviewBlocks?: number;
      linkedCommunityId?: string | null;
      status?: 'draft' | 'active' | 'archived';
    },
  ) {
    return this.request<NewsletterPublicationRecord>(
      `/marketing/publications/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  listNewsletterEditions(publicationId: string) {
    return this.request<{ items: NewsletterEditionRecord[] }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions`,
    );
  }

  createNewsletterEdition(
    publicationId: string,
    data: { title: string; subject?: string; previewText?: string },
  ) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  getNewsletterEdition(publicationId: string, editionId: string) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}`,
    );
  }

  updateNewsletterEdition(
    publicationId: string,
    editionId: string,
    data: {
      title?: string;
      subject?: string;
      previewText?: string | null;
      blocks?: unknown[];
      subjectVariantB?: string | null;
    },
  ) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  listNewsletterSections(publicationId: string) {
    return this.request<{ items: NewsletterSectionRecord[] }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sections`,
    );
  }

  createNewsletterSection(publicationId: string, data: { name: string; description?: string | null }) {
    return this.request<NewsletterSectionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sections`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  updateNewsletterSection(
    publicationId: string,
    sectionId: string,
    data: { name?: string; description?: string | null; sortOrder?: number },
  ) {
    return this.request<NewsletterSectionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sections/${encodeURIComponent(sectionId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteNewsletterSection(publicationId: string, sectionId: string) {
    return this.request<{ ok: boolean }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sections/${encodeURIComponent(sectionId)}`,
      { method: 'DELETE' },
    );
  }

  assignNewsletterEditionSection(
    publicationId: string,
    editionId: string,
    sectionId: string | null,
  ) {
    return this.request<{
      id: string;
      sectionId: string | null;
      section: { id: string; name: string; slug: string } | null;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/section`,
      { method: 'PATCH', body: JSON.stringify({ sectionId }) },
    );
  }

  requestNewsletterEditionAudioUpload(
    publicationId: string,
    editionId: string,
    data: { mimeType: string; sizeBytes: number },
  ) {
    return this.request<{ assetId: string; uploadUrl: string; key: string; expiresIn: number }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/audio/upload-url`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  confirmNewsletterEditionAudioUpload(
    publicationId: string,
    editionId: string,
    data: { assetId: string; durationSeconds?: number },
  ) {
    return this.request<{
      editionId: string;
      ready: boolean;
      scanning?: boolean;
      assetId?: string;
      audioMimeType?: string | null;
      audioSizeBytes?: number | null;
      audioDurationSeconds?: number | null;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/audio/confirm`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  async uploadNewsletterEditionAudio(
    publicationId: string,
    editionId: string,
    file: File,
    onProgress?: (pct: number) => void,
  ) {
    const { assetId, uploadUrl } = await this.requestNewsletterEditionAudioUpload(
      publicationId,
      editionId,
      { mimeType: file.type || 'audio/mpeg', sizeBytes: file.size },
    );
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed'));
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
    await this.confirmNewsletterEditionAudioUpload(publicationId, editionId, { assetId });
    await this.pollMediaAssetUntilReady(assetId);
    let durationSeconds: number | undefined;
    try {
      durationSeconds = await new Promise<number | undefined>((resolve) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
          const duration = Number.isFinite(audio.duration) ? Math.round(audio.duration) : undefined;
          URL.revokeObjectURL(url);
          resolve(duration);
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(undefined);
        };
        audio.src = url;
      });
    } catch {
      durationSeconds = undefined;
    }
    return this.confirmNewsletterEditionAudioUpload(publicationId, editionId, {
      assetId,
      durationSeconds,
    });
  }

  removeNewsletterEditionAudio(publicationId: string, editionId: string) {
    return this.request<{ id: string; audioAssetId: string | null }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/audio`,
      { method: 'DELETE' },
    );
  }

  getNewsletterEditionAudioPlayback(publicationId: string, editionId: string) {
    return this.request<{ editionId: string; audioUrl: string; mimeType: string }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/audio/playback`,
    );
  }

  previewNewsletterEdition(publicationId: string, editionId: string) {
    return this.request<{
      subject: string;
      previewText: string | null;
      html: string;
      plaintext: string;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/preview`,
    );
  }

  scheduleNewsletterEdition(
    publicationId: string,
    editionId: string,
    data: { wallTime: string; timezone: string },
  ) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/schedule`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  cancelNewsletterEditionSchedule(publicationId: string, editionId: string) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/cancel-schedule`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  testSendNewsletterEdition(
    publicationId: string,
    editionId: string,
    data?: { to?: string },
  ) {
    return this.request<{
      ok: boolean;
      to: string;
      providerMessageId: string | null;
      transport: string;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/test-send`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  estimateNewsletterEdition(
    publicationId: string,
    editionId: string,
    params?: { audience?: 'all' | 'tag' | 'segment'; tagId?: string; segmentId?: string },
  ) {
    const query = new URLSearchParams();
    if (params?.audience) query.set('audience', params.audience);
    if (params?.tagId) query.set('tagId', params.tagId);
    if (params?.segmentId) query.set('segmentId', params.segmentId);
    const qs = query.toString();
    return this.request<{
      editionId: string;
      subject: string;
      subjectVariantB?: string | null;
      subjectAbEligible?: boolean;
      audience: string;
      tagId: string | null;
      segmentId: string | null;
      eligibleCount: number;
      confirmRequired: boolean;
      confirmThreshold: number;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/estimate${qs ? `?${qs}` : ''}`,
    );
  }

  sendNewsletterEdition(
    publicationId: string,
    editionId: string,
    data?: {
      confirmed?: boolean;
      audience?: 'all' | 'tag' | 'segment';
      tagId?: string;
      segmentId?: string;
      subjectAbTest?: boolean;
    },
  ) {
    return this.request<{
      sendId: string;
      estimatedCount: number;
      queuedChunks: number;
      audience?: string;
      subjectAbTest?: boolean;
      testPoolSize?: number;
      rolloutPoolSize?: number;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/send`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  getNewsletterSend(publicationId: string, sendId: string) {
    return this.request<Record<string, unknown> & { subjectAb?: MarketingSubjectAbReport | null }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sends/${encodeURIComponent(sendId)}`,
    );
  }

  finalizeNewsletterSubjectAb(publicationId: string, sendId: string) {
    return this.request<MarketingSubjectAbReport | null>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/sends/${encodeURIComponent(sendId)}/subject-ab/finalize`,
      { method: 'POST' },
    );
  }

  listMarketingDeveloperApiKeys() {
    return this.request<{ items: MarketingDeveloperApiKeyRecord[] }>('/marketing/developer/api-keys');
  }

  createMarketingDeveloperApiKey(data: { name: string; scopes?: string[] }) {
    return this.request<MarketingDeveloperApiKeyRecord & { rawKey: string }>(
      '/marketing/developer/api-keys',
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  revokeMarketingDeveloperApiKey(keyId: string) {
    return this.request<MarketingDeveloperApiKeyRecord>(
      `/marketing/developer/api-keys/${encodeURIComponent(keyId)}`,
      { method: 'DELETE' },
    );
  }

  listMarketingWebhookEndpoints() {
    return this.request<{ items: MarketingWebhookEndpointRecord[] }>('/marketing/developer/webhooks');
  }

  createMarketingWebhookEndpoint(data: {
    label: string;
    url: string;
    events?: string[];
    enabled?: boolean;
  }) {
    return this.request<MarketingWebhookEndpointRecord & { signingSecret: string }>(
      '/marketing/developer/webhooks',
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  updateMarketingWebhookEndpoint(
    endpointId: string,
    data: { label?: string; url?: string; events?: string[]; enabled?: boolean },
  ) {
    return this.request<MarketingWebhookEndpointRecord>(
      `/marketing/developer/webhooks/${encodeURIComponent(endpointId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteMarketingWebhookEndpoint(endpointId: string) {
    return this.request<{ deleted: boolean }>(
      `/marketing/developer/webhooks/${encodeURIComponent(endpointId)}`,
      { method: 'DELETE' },
    );
  }

  testMarketingWebhookEndpoint(endpointId: string) {
    return this.request<{ queued: boolean; eventId: string }>(
      `/marketing/developer/webhooks/${encodeURIComponent(endpointId)}/test`,
      { method: 'POST' },
    );
  }

  listMarketingPublicSubscribers(params: {
    publicationId: string;
    status?: 'confirmed' | 'unsubscribed' | 'pending_confirmation';
    limit?: number;
    cursor?: string;
  }) {
    const query = new URLSearchParams();
    query.set('publicationId', params.publicationId);
    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    return this.request<{
      apiVersion: string;
      publication: { id: string; slug: string; name: string };
      items: Array<{
        contactId: string;
        email: string;
        status: string;
        subscribedAt: string;
        metadata: unknown;
      }>;
      nextCursor: string | null;
    }>(`/marketing/public/v1/subscribers?${query.toString()}`);
  }

  publishNewsletterEditionToWeb(
    publicationId: string,
    editionId: string,
    data?: { dualPublishContent?: boolean },
  ) {
    return this.request<{
      id: string;
      title: string;
      archiveSlug: string;
      webPublishedAt: string;
      contentArticleId?: string | null;
      archivePath: string;
      contentDualPublished: boolean;
    }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/publish-to-web`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  importNewsletterSubscribers(
    publicationId: string,
    data: { csv: string; attested: boolean; importTag?: string },
  ) {
    return this.request<{
      publicationId: string;
      rowCount: number;
      imported: number;
      updated: number;
      suppressed: number;
      failed: number;
      skippedEmpty: number;
      importTag: string;
      errors: Array<{ line: number; email: string; reason: string }>;
    }>(`/marketing/publications/${encodeURIComponent(publicationId)}/subscribers/import`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  exportNewsletterSubscribers(publicationId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/subscribers/export`,
    );
  }

  createNewsletterPaidCheckout(slug: string) {
    return this.request<{
      sessionId: string;
      clientSecret: string;
      connectAccountId?: string | null;
      stripeAccountId?: string | null;
      publishableKey?: string;
      testMode: boolean;
      devStub?: boolean;
      amountCents: number;
      currency: string;
      interval: string;
    }>(`/marketing/public/publications/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  completeDevNewsletterCheckout(slug: string, sessionId: string) {
    return this.request<{ ok: boolean; publicationId: string }>(
      `/marketing/public/publications/${encodeURIComponent(slug)}/dev-complete-checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      },
    );
  }

  unpublishNewsletterEditionFromWeb(publicationId: string, editionId: string) {
    return this.request<NewsletterEditionRecord>(
      `/marketing/publications/${encodeURIComponent(publicationId)}/editions/${encodeURIComponent(editionId)}/unpublish-from-web`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  getPublicNewsletterArchive(slug: string) {
    return this.request<{
      publication: {
        id: string;
        name: string;
        slug: string;
        description?: string | null;
        brandColor?: string | null;
        logoUrl?: string | null;
        fromName?: string | null;
      };
      items: Array<{
        id: string;
        title: string;
        subject: string;
        previewText?: string | null;
        archiveSlug: string;
        webPublishedAt: string;
        path: string;
        contentArticleId?: string | null;
      }>;
    }>(`/marketing/public/publications/${encodeURIComponent(slug)}/archive`);
  }

  getPublicNewsletterArchiveEdition(slug: string, editionSlug: string) {
    return this.request<{
      publication: {
        id: string;
        name: string;
        slug: string;
        brandColor?: string | null;
        logoUrl?: string | null;
        fromName?: string | null;
      };
      edition: {
        id: string;
        title: string;
        subject: string;
        previewText?: string | null;
        archiveSlug: string;
        webPublishedAt: string;
        htmlCache?: string | null;
        plaintextCache?: string | null;
        contentArticleId?: string | null;
        path: string;
      };
    }>(
      `/marketing/public/publications/${encodeURIComponent(slug)}/archive/${encodeURIComponent(editionSlug)}`,
    );
  }

  getPublicNewsletterPublication(slug: string) {
    return this.request<{
      id: string;
      name: string;
      slug: string;
      description?: string | null;
      logoUrl?: string | null;
      brandColor?: string | null;
      fromName?: string | null;
      status: string;
      creatorUsername: string | null;
      creatorDisplayName: string | null;
    }>(`/marketing/public/publications/${encodeURIComponent(slug)}`);
  }

  getNewsletterSubscribePage(slug: string) {
    return this.request<{
      id: string;
      name: string;
      slug: string;
      description?: string | null;
      brandColor?: string | null;
      logoUrl?: string | null;
      fromName?: string | null;
      requireDoubleOptIn: boolean;
    }>(`/marketing/public/publications/${encodeURIComponent(slug)}/subscribe`);
  }

  getMarketingPublicForm(slug: string) {
    return this.request<{
      id: string;
      name: string;
      slug: string;
      headline?: string | null;
      buttonLabel: string;
      consentText: string;
      consentTextVersion: string;
      publication: { id: string; name: string; slug: string };
    }>(`/marketing/public/forms/${encodeURIComponent(slug)}`);
  }

  subscribeToNewsletterPublication(
    slug: string,
    data: {
      email: string;
      consentText: string;
      consentTextVersion: string;
      honeypot?: string;
      formId?: string;
      referralCode?: string;
      recommendationSlug?: string;
    },
  ) {
    return this.request<{
      accepted: boolean;
      pendingConfirmation?: boolean;
      publicationName?: string;
    }>(`/marketing/public/publications/${encodeURIComponent(slug)}/subscribe`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  confirmNewsletterSubscription(token: string) {
    return this.request<{
      ok: boolean;
      alreadyConfirmed: boolean;
      publicationName: string;
      welcomeReady: boolean;
      leadMagnet: { url: string; label: string } | null;
      referral: { enabled: boolean; referralCode: string; path: string } | null;
    }>(`/marketing/public/confirm?token=${encodeURIComponent(token)}`);
  }

  getMarketingEmailPreferences(token: string) {
    return this.request<{
      publication: { id: string; name: string; slug: string };
      status: string;
      token: string;
    }>(`/marketing/public/preferences?token=${encodeURIComponent(token)}`);
  }

  unsubscribeMarketingByToken(token: string) {
    return this.request<{ ok: boolean; publicationName: string }>(
      '/marketing/public/unsubscribe',
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    );
  }

  listMarketingForms() {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        slug: string;
        publication: { id: string; name: string; slug: string };
      }>;
    }>('/marketing/forms');
  }

  createMarketingForm(data: {
    name: string;
    publicationId: string;
    consentText: string;
    headline?: string;
    buttonLabel?: string;
    leadMagnetUrl?: string;
    leadMagnetLabel?: string;
  }) {
    return this.request<Record<string, unknown>>('/marketing/forms', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  listMarketingSequences() {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        status: string;
        trigger: string;
        publicationId: string | null;
        stepCount: number;
        enrollmentCount: number;
        updatedAt: string;
      }>;
    }>('/marketing/sequences');
  }

  getMarketingSequence(sequenceId: string) {
    return this.request<{
      id: string;
      name: string;
      status: string;
      trigger: string;
      publicationId: string | null;
      timezone: string | null;
      sendWindowStartHour: number | null;
      sendWindowEndHour: number | null;
      steps: Array<{
        id: string;
        position: number;
        subject: string;
        previewText: string | null;
        delayMinutes: number;
        blocks: unknown[];
      }>;
    }>(`/marketing/sequences/${encodeURIComponent(sequenceId)}`);
  }

  createMarketingSequence(data: {
    name: string;
    publicationId?: string;
    trigger?: string;
  }) {
    return this.request<{ id: string }>('/marketing/sequences', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateMarketingSequence(
    sequenceId: string,
    data: {
      name?: string;
      publicationId?: string | null;
      status?: string;
      trigger?: string;
      timezone?: string | null;
      sendWindowStartHour?: number | null;
      sendWindowEndHour?: number | null;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/marketing/sequences/${encodeURIComponent(sequenceId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  replaceMarketingSequenceSteps(
    sequenceId: string,
    data: {
      steps: Array<{
        subject: string;
        previewText?: string | null;
        delayMinutes: number;
        blocks: unknown[];
      }>;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/marketing/sequences/${encodeURIComponent(sequenceId)}/steps`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  listMarketingAutomations() {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        status: string;
        triggerType: string;
        triggerFormId: string | null;
        triggerTagId: string | null;
        triggerRefId: string | null;
        inactivityDays: number | null;
        actions: unknown;
        runCount: number;
        updatedAt: string;
      }>;
    }>('/marketing/automations');
  }

  getMarketingAutomation(automationId: string) {
    return this.request<{
      id: string;
      name: string;
      status: string;
      triggerType: string;
      triggerFormId: string | null;
      triggerTagId: string | null;
      triggerRefId: string | null;
      inactivityDays: number | null;
      actions: Array<{ type: string; tagName?: string; sequenceId?: string }>;
      version: number;
      runCount: number;
    }>(`/marketing/automations/${encodeURIComponent(automationId)}`);
  }

  createMarketingAutomation(data: {
    name: string;
    triggerType: string;
    triggerFormId?: string;
    triggerTagId?: string;
    triggerRefId?: string;
    inactivityDays?: number;
    actions: Array<{ type: string; tagName?: string; sequenceId?: string }>;
  }) {
    return this.request<{ id: string }>('/marketing/automations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateMarketingAutomation(
    automationId: string,
    data: {
      name?: string;
      status?: string;
      triggerType?: string;
      triggerFormId?: string | null;
      triggerTagId?: string | null;
      triggerRefId?: string | null;
      inactivityDays?: number | null;
      actions?: Array<{ type: string; tagName?: string; sequenceId?: string }>;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/marketing/automations/${encodeURIComponent(automationId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  installMarketingWinBackPack(data?: {
    courseCtaUrl?: string;
    offerCtaUrl?: string;
    inactivityDays?: number;
  }) {
    return this.request<{
      winBackSequenceId: string;
      buyerNurtureSequenceId: string;
      winBackAutomationId: string;
      buyerNurtureAutomationId: string;
      inactivityDays: number;
      note: string;
    }>('/marketing/automations/win-back-pack', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  sendMarketingTestEmail(data?: { to?: string; subject?: string }) {
    return this.request<{
      ok: boolean;
      to: string;
      providerMessageId: string | null;
      transport: string;
    }>('/marketing/test-send', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  getMarketingOpsHealth() {
    return this.request<Record<string, unknown>>('/marketing/ops/health');
  }

  estimateCircleCampaignRecipients(circleId: string) {
    return this.request<{ eligibleMembers: number }>(
      `/marketing/circles/${encodeURIComponent(circleId)}/campaigns/estimate`,
    );
  }

  sendCircleCampaign(
    circleId: string,
    data: {
      subject: string;
      body: string;
      audience: 'test' | 'members';
      testEmail?: string;
      confirmed?: boolean;
      includeDiscussionCta?: boolean;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/marketing/circles/${encodeURIComponent(circleId)}/campaigns`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  listCommunityReports(communityId: string) {
    return this.request<{ items: Array<Record<string, unknown>> }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/reports`,
    );
  }

  resolveCommunityReport(communityId: string, reportId: string, status: 'resolved' | 'dismissed') {
    return this.request<Record<string, unknown>>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/reports/${encodeURIComponent(reportId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      },
    );
  }

  listCommunityAuditLog(communityId: string, limit?: number) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/moderation/audit-log${suffix}`,
    );
  }

  getCommunitySpamSettings(communityId: string) {
    return this.request<{
      postSlowModeSeconds: number | null;
      postRateLimitPerHour: number | null;
      blockedWords: string[];
      commentMaxReplyDepth: number;
      reportEscalationThreshold: number;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/moderation/spam-settings`);
  }

  updateCommunitySpamSettings(
    communityId: string,
    data: {
      postSlowModeSeconds?: number | null;
      postRateLimitPerHour?: number | null;
      blockedWords?: string[];
      commentMaxReplyDepth?: number;
      reportEscalationThreshold?: number;
    },
  ) {
    return this.request<{
      postSlowModeSeconds: number | null;
      postRateLimitPerHour: number | null;
      blockedWords: string[];
      commentMaxReplyDepth: number;
      reportEscalationThreshold: number;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/moderation/spam-settings`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  exportCommunityMembersCsv(communityId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/exports/members`,
    );
  }

  exportCommunityPaymentsCsv(communityId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/exports/payments`,
    );
  }

  getCommunityGamification(communityId: string) {
    return this.request<{
      enabled: boolean;
      levels: Array<{ level: number; name: string; xpRequired: number }>;
      rules: Array<{ actionKey: string; points: number; enabled: boolean }>;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/gamification`);
  }

  listCommunityBadges(communityId: string) {
    return this.request<{
      items: CommunityBadgeRecord[];
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges`);
  }

  createCommunityBadge(
    communityId: string,
    data: {
      name: string;
      slug?: string;
      description?: string | null;
      iconEmoji?: string;
      awardType: CommunityBadgeAwardType;
      awardConfig?: Record<string, unknown>;
      enabled?: boolean;
      sortOrder?: number;
    },
  ) {
    return this.request<CommunityBadgeRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  updateCommunityBadge(
    communityId: string,
    badgeId: string,
    data: {
      name?: string;
      slug?: string;
      description?: string | null;
      iconEmoji?: string;
      awardType?: CommunityBadgeAwardType;
      awardConfig?: Record<string, unknown>;
      enabled?: boolean;
      sortOrder?: number;
    },
  ) {
    return this.request<CommunityBadgeRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges/${encodeURIComponent(badgeId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteCommunityBadge(communityId: string, badgeId: string) {
    return this.request<{ ok: boolean }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges/${encodeURIComponent(badgeId)}`,
      { method: 'DELETE' },
    );
  }

  listCommunityChallenges(communityId: string) {
    return this.request<{
      items: CommunityChallengeRecord[];
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/gamification/challenges`);
  }

  createCommunityChallenge(
    communityId: string,
    data: {
      title: string;
      description?: string | null;
      startsAt: string;
      endsAt: string;
      goalActionKey: string;
      goalTarget: number;
      enabled?: boolean;
      sortOrder?: number;
    },
  ) {
    return this.request<CommunityChallengeRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/challenges`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  updateCommunityChallenge(
    communityId: string,
    challengeId: string,
    data: {
      title?: string;
      description?: string | null;
      startsAt?: string;
      endsAt?: string;
      goalActionKey?: string;
      goalTarget?: number;
      enabled?: boolean;
      sortOrder?: number;
    },
  ) {
    return this.request<CommunityChallengeRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/challenges/${encodeURIComponent(challengeId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteCommunityChallenge(communityId: string, challengeId: string) {
    return this.request<{ ok: boolean }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/challenges/${encodeURIComponent(challengeId)}`,
      { method: 'DELETE' },
    );
  }

  getCommunityStreakSettings(communityId: string) {
    return this.request<{
      streakGraceDays: number;
      streakFreezeBadgeId: string | null;
      streakFreezeBadge: { id: string; name: string; iconEmoji: string } | null;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/gamification/streak-settings`);
  }

  updateCommunityStreakSettings(
    communityId: string,
    data: { streakGraceDays?: number; streakFreezeBadgeId?: string | null },
  ) {
    return this.request(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/streak-settings`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  awardCommunityBadge(communityId: string, badgeId: string, userId: string) {
    return this.request<{ awarded: boolean; alreadyHad: boolean }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges/${encodeURIComponent(badgeId)}/award`,
      { method: 'POST', body: JSON.stringify({ userId }) },
    );
  }

  revokeCommunityBadge(communityId: string, badgeId: string, userId: string) {
    return this.request<{ ok: boolean }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/badges/${encodeURIComponent(badgeId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  }

  listCommunityImportJobs(communityId: string) {
    return this.request<{ items: CommunityImportJobRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/import-jobs`,
    );
  }

  createCommunityImportJob(
    communityId: string,
    data: {
      kind: 'members' | 'posts';
      csvContent: string;
      dryRun?: boolean;
      sourceFilename?: string;
    },
  ) {
    return this.request(`/circles/by-id/${encodeURIComponent(communityId)}/import-jobs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getCommunityWelcomeSettings(communityId: string) {
    return this.request<CommunityWelcomeSettings>(
      `/circles/by-id/${encodeURIComponent(communityId)}/welcome`,
    );
  }

  updateCommunityWelcomeSettings(
    communityId: string,
    data: {
      welcomeChecklistEnabled?: boolean;
      welcomeChecklistSteps?: Array<'intro_post' | 'profile_complete' | 'first_comment'>;
      autoWelcomeEnabled?: boolean;
      autoWelcomeMode?: 'notification' | 'post' | 'dm';
      autoWelcomeMessage?: string | null;
      autoWelcomePostVariant?: 'announcement' | 'pinned' | 'standard';
    },
  ) {
    return this.request<CommunityWelcomeSettings>(
      `/circles/by-id/${encodeURIComponent(communityId)}/welcome`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  getCommunityWelcomeChecklist(slug: string) {
    return this.request<{
      enabled: boolean;
      visible: boolean;
      allComplete: boolean;
      dismissed: boolean;
      items: CommunityWelcomeChecklistItem[];
    }>(`/circles/${encodeURIComponent(slug)}/welcome/checklist`);
  }

  dismissCommunityWelcomeChecklist(slug: string) {
    return this.request(`/circles/${encodeURIComponent(slug)}/welcome/checklist/dismiss`, {
      method: 'POST',
    });
  }

  heartbeatCommunityPresence(slug: string) {
    return this.request<{ ok: boolean }>(
      `/circles/${encodeURIComponent(slug)}/presence/heartbeat`,
      { method: 'POST' },
    );
  }

  getCommunityNotificationPrefs(slug: string) {
    return this.request<CommunityNotificationPrefsView>(
      `/circles/${encodeURIComponent(slug)}/notifications/prefs`,
    );
  }

  updateCommunityNotificationPrefs(
    slug: string,
    data: Partial<CommunityNotificationPrefsView['events']> & {
      emailMode?: CommunityNotificationPrefsView['emailMode'];
      pushEnabled?: boolean;
    },
  ) {
    return this.request<CommunityNotificationPrefsView>(
      `/circles/${encodeURIComponent(slug)}/notifications/prefs`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  updateCommunityGamification(
    communityId: string,
    data: {
      enabled?: boolean;
      levels?: Array<{ level: number; name: string; xpRequired: number }>;
      rules?: Array<{ actionKey: string; points: number; enabled: boolean }>;
    },
  ) {
    return this.request(`/circles/by-id/${encodeURIComponent(communityId)}/gamification`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getOwnedCommunityLeaderboard(
    communityId: string,
    window: 'daily' | 'weekly' | 'all' = 'all',
    limit?: number,
  ) {
    const params = new URLSearchParams({ window });
    if (limit != null) params.set('limit', String(limit));
    return this.request<{ window: string; items: Array<Record<string, unknown>> }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/gamification/leaderboard?${params.toString()}`,
    );
  }

  getCommunityMemberXp(slug: string) {
    return this.request<{
      enabled: boolean;
      totalXp: number;
      level: number;
      levelName: string;
      nextLevel: {
        level: number;
        name: string;
        xpRequired: number;
        xpRemaining: number;
      } | null;
      badges: CommunityMemberBadgeRecord[];
      streak: CommunityMemberStreakRecord;
      challenges: CommunityChallengeRecord[];
    }>(`/circles/${encodeURIComponent(slug)}/gamification/me`);
  }

  getCommunityLeaderboard(
    slug: string,
    window: 'daily' | 'weekly' | 'all' = 'all',
    limit?: number,
  ) {
    const params = new URLSearchParams({ window });
    if (limit != null) params.set('limit', String(limit));
    return this.request<{
      enabled: boolean;
      window: string;
      items: Array<Record<string, unknown>>;
    }>(`/circles/${encodeURIComponent(slug)}/gamification/leaderboard?${params.toString()}`);
  }

  getCircleMemberReferral(slug: string) {
    return this.request<{
      enabled: boolean;
      referralCode: string;
      path: string;
      stats: { totalReferrals: number; paidReferrals: number };
    }>(`/growth/circles/by-slug/${encodeURIComponent(slug)}/referral`);
  }

  listPublicationReferralPrograms() {
    return this.request<{
      items: Array<{
        publicationId: string;
        name: string;
        slug: string;
        status: string;
        enabled: boolean;
        milestoneCount: number;
        milestoneLabel: string;
        confirmedReferrals: number;
        sharePathTemplate: string;
      }>;
    }>('/growth/publications/referral-programs');
  }

  getPublicationReferralProgram(publicationId: string) {
    return this.request<{
      publicationId: string;
      name: string;
      slug: string;
      enabled: boolean;
      milestoneCount: number;
      milestoneLabel: string;
      stats: {
        confirmed: number;
        pending: number;
        rejected: number;
        topReferrers: Array<{
          contactId: string | null;
          count: number;
          milestoneReached: boolean;
          displayName: string | null;
        }>;
        verificationLabels: Array<{ label: string; count: number }>;
      };
    }>(`/growth/publications/${encodeURIComponent(publicationId)}/referrals`);
  }

  updatePublicationReferralProgram(
    publicationId: string,
    data: { enabled?: boolean; milestoneCount?: number; milestoneLabel?: string },
  ) {
    return this.request<{
      publicationId: string;
      enabled: boolean;
      milestoneCount: number;
      milestoneLabel: string;
    }>(`/growth/publications/${encodeURIComponent(publicationId)}/referral-program`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  listGrowthSponsorshipOffers() {
    return this.request<{ items: GrowthSponsorshipOfferRecord[] }>('/growth/sponsorship-offers');
  }

  createGrowthSponsorshipOffer(data: {
    name: string;
    advertiserName: string;
    headline: string;
    body?: string;
    imageUrl?: string;
    ctaLabel: string;
    ctaUrl: string;
    disclosure?: string;
    affiliateCode?: string;
    enabled?: boolean;
  }) {
    return this.request<GrowthSponsorshipOfferRecord>('/growth/sponsorship-offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateGrowthSponsorshipOffer(
    offerId: string,
    data: {
      name?: string;
      advertiserName?: string;
      headline?: string;
      body?: string | null;
      imageUrl?: string | null;
      ctaLabel?: string;
      ctaUrl?: string;
      disclosure?: string;
      affiliateCode?: string | null;
      enabled?: boolean;
    },
  ) {
    return this.request<GrowthSponsorshipOfferRecord>(
      `/growth/sponsorship-offers/${encodeURIComponent(offerId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteGrowthSponsorshipOffer(offerId: string) {
    return this.request<{ deleted: boolean }>(
      `/growth/sponsorship-offers/${encodeURIComponent(offerId)}`,
      { method: 'DELETE' },
    );
  }

  getPublicationMemberReferral(slug: string) {
    return this.request<{
      enabled: boolean;
      referralCode: string;
      path: string;
      stats: { confirmedReferrals: number; milestoneCount: number; milestoneLabel: string };
    }>(`/growth/publications/by-slug/${encodeURIComponent(slug)}/referral`);
  }

  listPublicPublicationCatalog(query?: string) {
    const params = new URLSearchParams();
    if (query?.trim()) params.set('q', query.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        slug: string;
        description: string | null;
        logoUrl: string | null;
        brandColor: string | null;
        fromName: string | null;
        creatorUsername: string | null;
        creatorDisplayName: string | null;
        subscribePath: string;
      }>;
    }>(`/growth/public/publications${suffix}`);
  }

  listPublicPublicationRecommendations(slug: string) {
    return this.request<{
      fromSlug: string;
      items: Array<{
        id: string;
        name: string;
        slug: string;
        description: string | null;
        logoUrl: string | null;
        brandColor: string | null;
        fromName: string | null;
        creatorUsername: string | null;
        creatorDisplayName: string | null;
        subscribePath: string;
      }>;
    }>(`/growth/public/publications/${encodeURIComponent(slug)}/recommendations`);
  }

  searchPublicationRecommendationCatalog(query?: string, excludePublicationId?: string) {
    const params = new URLSearchParams();
    if (query?.trim()) params.set('q', query.trim());
    if (excludePublicationId?.trim()) params.set('excludePublicationId', excludePublicationId.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        slug: string;
        description: string | null;
        logoUrl: string | null;
        brandColor: string | null;
        fromName: string | null;
        creatorUsername: string | null;
        creatorDisplayName: string | null;
        subscribePath: string;
      }>;
    }>(`/growth/publications/recommendation-catalog${suffix}`);
  }

  listPublicationRecommendations(publicationId: string) {
    return this.request<{
      fromPublicationId: string;
      fromSlug: string;
      max: number;
      items: Array<{
        id: string;
        toPublicationId: string;
        name: string;
        slug: string;
        status: string;
        enabled: boolean;
        confirmedSubscribes: number;
        subscribePath: string | null;
      }>;
    }>(`/growth/publications/${encodeURIComponent(publicationId)}/recommendations`);
  }

  addPublicationRecommendation(publicationId: string, toPublicationId: string) {
    return this.request(`/growth/publications/${encodeURIComponent(publicationId)}/recommendations`, {
      method: 'POST',
      body: JSON.stringify({ toPublicationId }),
    });
  }

  removePublicationRecommendation(publicationId: string, recommendationId: string) {
    return this.request(
      `/growth/publications/${encodeURIComponent(publicationId)}/recommendations/${encodeURIComponent(recommendationId)}`,
      { method: 'DELETE' },
    );
  }

  getCircleGrowthSettings(circleId: string) {
    return this.request<{ referralsEnabled: boolean; affiliatesEnabled: boolean }>(
      `/growth/circles/${encodeURIComponent(circleId)}/settings`,
    );
  }

  updateCircleGrowthSettings(
    circleId: string,
    data: { referralsEnabled?: boolean; affiliatesEnabled?: boolean },
  ) {
    return this.request(`/growth/circles/${encodeURIComponent(circleId)}/settings`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getCircleReferralStats(circleId: string) {
    return this.request<{
      totalReferrals: number;
      paidReferrals: number;
      topReferrers: Array<{
        userId: string | null;
        count: number;
        profile: {
          username?: string | null;
          displayName?: string | null;
          avatarUrl?: string | null;
        } | null;
      }>;
    }>(`/growth/circles/${encodeURIComponent(circleId)}/referrals`);
  }

  listCircleAffiliatePrograms(circleId: string) {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        code: string;
        email: string | null;
        commissionBps: number;
        enabled: boolean;
        offerId: string;
        conversionCount: number;
        membershipCount: number;
      }>;
    }>(`/growth/circles/${encodeURIComponent(circleId)}/affiliate-programs`);
  }

  createCircleAffiliateProgram(
    circleId: string,
    data: {
      name: string;
      code: string;
      email?: string;
      commissionBps?: number;
      offerId?: string;
    },
  ) {
    return this.request(`/growth/circles/${encodeURIComponent(circleId)}/affiliate-programs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateCircleAffiliateProgram(
    circleId: string,
    affiliateId: string,
    data: {
      name?: string;
      email?: string | null;
      commissionBps?: number;
      enabled?: boolean;
      offerId?: string;
    },
  ) {
    return this.request(
      `/growth/circles/${encodeURIComponent(circleId)}/affiliate-programs/${encodeURIComponent(affiliateId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  listCircleAffiliateConversions(circleId: string, limit?: number) {
    const params = new URLSearchParams();
    if (limit != null) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: Array<Record<string, unknown>> }>(
      `/growth/circles/${encodeURIComponent(circleId)}/affiliate-programs/conversions${suffix}`,
    );
  }

  listCircleAffiliateOffers(circleId: string) {
    return this.request<{
      items: Array<{
        id: string;
        title: string;
        amountCents: number;
        currency: string;
        billingInterval: string | null;
        membershipTierId: string | null;
      }>;
    }>(`/growth/circles/${encodeURIComponent(circleId)}/affiliate-offers`);
  }

  getCommunityFeed(
    slug: string,
    cursor?: string,
    limit?: number,
    q?: string,
    category?: string,
    type?: 'audio',
  ) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit != null) params.set('limit', String(limit));
    if (q?.trim()) params.set('q', q.trim());
    if (category?.trim()) params.set('category', category.trim());
    if (type) params.set('type', type);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<PaginatedFeedResponse>(
      `/circles/${encodeURIComponent(slug)}/feed${suffix}`,
    );
  }

  getCommunityPostCategories(slug: string) {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        slug: string;
        sortOrder: number;
        color?: string | null;
        icon?: string | null;
        status: 'active' | 'archived';
        postCount: number;
      }>;
    }>(`/circles/${encodeURIComponent(slug)}/post-categories`);
  }

  getCommunityStickers() {
    return this.request<{
      items: Array<{ id: string; label: string; url: string }>;
    }>('/circles/stickers/catalog');
  }

  listStudioCommunityPostCategories(communityId: string) {
    return this.request<{
      items: Array<{
        id: string;
        name: string;
        slug: string;
        sortOrder: number;
        color?: string | null;
        icon?: string | null;
        status: 'active' | 'archived';
        postCount: number;
      }>;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/post-categories`);
  }

  bulkAssignStudioCommunityPostCategories(
    communityId: string,
    postIds: string[],
    categories: string[],
  ) {
    return this.request<{ updatedCount: number; categories: string[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/post-categories/bulk-assign`,
      {
        method: 'POST',
        body: JSON.stringify({ postIds, categories }),
      },
    );
  }

  listStudioScheduledCommunityPosts(communityId: string) {
    return this.request<{
      items: Array<Record<string, unknown>>;
    }>(`/circles/by-id/${encodeURIComponent(communityId)}/scheduled-posts`);
  }

  createStudioCommunityPostCategory(communityId: string, name: string) {
    return this.request(`/circles/by-id/${encodeURIComponent(communityId)}/post-categories`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  renameStudioCommunityPostCategory(communityId: string, categoryId: string, name: string) {
    return this.request(
      `/circles/by-id/${encodeURIComponent(communityId)}/post-categories/${encodeURIComponent(categoryId)}`,
      { method: 'PATCH', body: JSON.stringify({ name }) },
    );
  }

  mergeStudioCommunityPostCategories(
    communityId: string,
    sourceCategoryId: string,
    targetCategoryId: string,
  ) {
    return this.request(`/circles/by-id/${encodeURIComponent(communityId)}/post-categories/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourceCategoryId, targetCategoryId }),
    });
  }

  archiveStudioCommunityPostCategory(communityId: string, categoryId: string) {
    return this.request(
      `/circles/by-id/${encodeURIComponent(communityId)}/post-categories/${encodeURIComponent(categoryId)}/archive`,
      { method: 'POST' },
    );
  }

  getCommunityResources(slug: string, parentId?: string | null, search?: string | null) {
    const params = new URLSearchParams();
    if (parentId?.trim()) params.set('parentId', parentId.trim());
    if (search?.trim()) params.set('q', search.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<CommunityResourcesMemberResponse>(
      `/circles/${encodeURIComponent(slug)}/resources${suffix}`,
    );
  }

  downloadCommunityResource(slug: string, resourceId: string) {
    return this.request<{
      ok: boolean;
      fileUrl: string;
      mimeType: string | null;
      sizeBytes: number | null;
      title: string;
    }>(
      `/circles/${encodeURIComponent(slug)}/resources/${encodeURIComponent(resourceId)}/download`,
      {
        method: 'POST',
      },
    );
  }

  listStudioCommunityResources(communityId: string, search?: string | null) {
    const params = new URLSearchParams();
    if (search?.trim()) params.set('q', search.trim());
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{ items: CommunityResourceStudioRecord[] }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources${suffix}`,
    );
  }

  createStudioCommunityResourceFolder(
    communityId: string,
    payload: CreateCommunityResourceFolderPayload,
  ) {
    return this.request<CommunityResourceStudioRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/folder`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  createStudioCommunityResourceLink(
    communityId: string,
    payload: CreateCommunityResourceLinkPayload,
  ) {
    return this.request<CommunityResourceStudioRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/link`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  createStudioCommunityResourceFile(
    communityId: string,
    payload: CreateCommunityResourceFilePayload,
  ) {
    return this.request<CommunityResourceStudioRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/file`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  updateStudioCommunityResource(
    communityId: string,
    resourceId: string,
    payload: UpdateCommunityResourcePayload,
  ) {
    return this.request<CommunityResourceStudioRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/${encodeURIComponent(resourceId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
  }

  archiveStudioCommunityResource(communityId: string, resourceId: string) {
    return this.request(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/${encodeURIComponent(resourceId)}/archive`,
      { method: 'POST' },
    );
  }

  listStudioCommunityResourceVersions(communityId: string, resourceId: string) {
    return this.request<{
      resourceId: string;
      currentVersion: number;
      items: CommunityResourceVersionRecord[];
    }>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/${encodeURIComponent(resourceId)}/versions`,
    );
  }

  restoreStudioCommunityResourceVersion(
    communityId: string,
    resourceId: string,
    versionId: string,
  ) {
    return this.request<CommunityResourceStudioRecord>(
      `/circles/by-id/${encodeURIComponent(communityId)}/resources/${encodeURIComponent(resourceId)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: 'POST' },
    );
  }

  getCommunityPost(slug: string, postId: string) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}`,
    );
  }

  getCommunityPostSharePreview(slug: string, postId: string) {
    return this.request<{
      title: string;
      description: string;
      imageUrl?: string | null;
      canonicalPath?: string | null;
    }>(`/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}/share-preview`);
  }

  listCommunityDrafts(slug: string, cursor?: string, limit?: number) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (limit != null) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<PaginatedFeedResponse>(
      `/circles/${encodeURIComponent(slug)}/posts/drafts${suffix}`,
    );
  }

  createCommunityPost(
    slug: string,
    data: {
      content: string;
      type?: 'text' | 'link' | 'image' | 'video' | 'audio' | 'poll';
      linkUrl?: string;
      mediaKey?: string;
      mediaUrl?: string;
      mediaUrls?: string[];
      pollData?: { options: string[]; expiresAt: string } | null;
      attachments?: Array<{
        name: string;
        url: string;
        key?: string;
        mimeType: string;
        sizeBytes: number;
      }>;
      category?: string;
      categories?: string[];
      publish?: boolean;
      scheduledFor?: string | null;
      minLevel?: number | null;
      quotePostId?: string;
    },
  ) {
    return this.request<Record<string, unknown>>(`/circles/${encodeURIComponent(slug)}/posts`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  publishCommunityPost(slug: string, postId: string) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}/publish`,
      { method: 'PATCH' },
    );
  }

  updateCommunityPost(
    slug: string,
    postId: string,
    data: {
      content?: string;
      type?: 'text' | 'link' | 'image' | 'video' | 'audio' | 'poll';
      linkUrl?: string;
      mediaKey?: string;
      mediaUrl?: string;
      mediaUrls?: string[];
      pollData?: { options: string[]; expiresAt: string } | null;
      attachments?: Array<{
        name: string;
        url: string;
        key?: string;
        mimeType: string;
        sizeBytes: number;
      }>;
      category?: string;
      scheduledFor?: string | null;
    },
  ) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteCommunityPost(slug: string, postId: string) {
    return this.request<{ ok: boolean }>(
      `/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}`,
      { method: 'DELETE' },
    );
  }

  pinCommunityPost(
    slug: string,
    postId: string,
    data?: { pinned?: boolean; announcement?: boolean },
  ) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}/pin`,
      { method: 'POST', body: JSON.stringify(data ?? {}) },
    );
  }

  listCommunityMembers(
    slug: string,
    params?: { search?: string; cursor?: string; limit?: number; sort?: 'active' | 'joined' },
  ) {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit != null) query.set('limit', String(params.limit));
    if (params?.sort) query.set('sort', params.sort);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.request<PaginatedFeedResponse<CommunityMemberDirectoryRecord>>(
      `/circles/${encodeURIComponent(slug)}/members${suffix}`,
    );
  }

  listCommunityMemberMapLocations(slug: string) {
    return this.request<{
      items: Array<{
        userId: string;
        profile?: {
          username?: string | null;
          displayName?: string | null;
          avatarUrl?: string | null;
          headline?: string | null;
        } | null;
        location: {
          city: string | null;
          region: string | null;
          latitude: number | null;
          longitude: number | null;
          label: string | null;
          hasMapPin: boolean;
        };
      }>;
    }>(`/circles/${encodeURIComponent(slug)}/members/map`);
  }

  listCommunityMemberMapRegions(slug: string) {
    return this.request<{ items: Array<{ region: string; country: string; count: number }> }>(
      `/circles/${encodeURIComponent(slug)}/members/map/regions`,
    );
  }

  retryCommunityMemberMapGeocode(slug: string) {
    return this.request<{ ok: boolean; queued: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/map/retry-geocode`,
      { method: 'POST' },
    );
  }

  updateCommunityMemberRole(
    slug: string,
    userId: string,
    data: { role: 'moderator' | 'member'; permissions?: string[] },
  ) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/role`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  updateCommunityModeratorPermissions(slug: string, userId: string, permissions: string[]) {
    return this.request<Record<string, unknown>>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/moderator-permissions`,
      { method: 'PATCH', body: JSON.stringify({ permissions }) },
    );
  }

  banCommunityMember(slug: string, userId: string) {
    return this.request<{ ok: boolean; status: string }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/ban`,
      { method: 'POST' },
    );
  }

  removeCommunityMember(slug: string, userId: string) {
    return this.request<{ ok: boolean; status: string }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/remove`,
      { method: 'POST' },
    );
  }

  muteCommunityMember(slug: string, userId: string) {
    return this.request<{ muted: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/mute`,
      { method: 'POST' },
    );
  }

  unmuteCommunityMember(slug: string, userId: string) {
    return this.request<{ muted: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/mute`,
      { method: 'DELETE' },
    );
  }

  blockCommunityMember(slug: string, userId: string) {
    return this.request<{ blocked: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/block`,
      { method: 'POST' },
    );
  }

  unblockCommunityMember(slug: string, userId: string) {
    return this.request<{ blocked: boolean }>(
      `/circles/${encodeURIComponent(slug)}/members/${encodeURIComponent(userId)}/block`,
      { method: 'DELETE' },
    );
  }

  muteCommunityNotifications(slug: string) {
    return this.request<{ muted: boolean; notificationsMutedAt?: string }>(
      `/circles/${encodeURIComponent(slug)}/mute-notifications`,
      { method: 'POST' },
    );
  }

  unmuteCommunityNotifications(slug: string) {
    return this.request<{ muted: boolean }>(
      `/circles/${encodeURIComponent(slug)}/mute-notifications`,
      { method: 'DELETE' },
    );
  }

  getCircleCourseClassroom(slug: string) {
    return this.request<CircleCourseClassroomResponse>(
      `/courses/circles/${encodeURIComponent(slug)}/classroom`,
    );
  }

  listCircleCoursePerks(circleId: string) {
    return this.request<{ items: CircleCoursePerkRecord[] }>(
      `/courses/circles/by-id/${encodeURIComponent(circleId)}/classroom-perks`,
    );
  }

  setCircleCoursePerks(
    circleId: string,
    items: Array<{
      courseId: string;
      minTierId?: string | null;
      perkMode?: 'free' | 'member_price' | 'public_price';
      memberPriceAmountCents?: number | null;
      sortOrder?: number;
    }>,
  ) {
    return this.request<{ items: CircleCoursePerkRecord[] }>(
      `/courses/circles/by-id/${encodeURIComponent(circleId)}/classroom-perks`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      },
    );
  }

  getCircleCourseClassroomPreview(slug: string) {
    return this.request<CircleCourseClassroomPreview>(
      `/courses/circles/${encodeURIComponent(slug)}/classroom-preview`,
    );
  }

  listMyCourses() {
    return this.request<CourseRecord[]>('/courses/mine');
  }

  getCourseMediaLibrary() {
    return this.request<CourseMediaLibrary>('/courses/media-library');
  }

  listCoursesCatalog(params?: {
    q?: string;
    topic?: string;
    skill?: string;
    creatorId?: string;
    limit?: number;
    cursor?: string;
  }) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.topic) search.set('topic', params.topic);
    if (params?.skill) search.set('skill', params.skill);
    if (params?.creatorId) search.set('creatorId', params.creatorId);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const qs = search.toString();
    return this.request<{ items: CourseCatalogItem[]; nextCursor: string | null }>(
      `/courses${qs ? `?${qs}` : ''}`,
    );
  }

  listMyCourseEnrollments() {
    return this.request<{ items: MyCourseEnrollment[] }>('/learning/enrollments');
  }

  createCourse(data: { title: string; description?: string }) {
    return this.request<CourseRecord>('/courses', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  createCourseWithStarter(data: {
    title: string;
    description?: string;
    subtitle?: string;
    coverUrl: string;
    coverKey: string;
    priceType: 'free' | 'one_time';
    priceAmountCents?: number;
    refundPolicyText?: string;
    visibility: 'public' | 'unlisted' | 'circle_only';
    termsAcknowledgment: boolean;
    moduleTitle: string;
    lessonTitle: string;
  }) {
    return this.request<CourseRecord>('/courses/with-starter', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getCourseById(id: string) {
    return this.request<CourseRecord>(`/courses/by-id/${encodeURIComponent(id)}`);
  }

  updateCourse(id: string, data: Partial<CourseRecord>) {
    return this.request<CourseRecord>(`/courses/by-id/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getCoursePublishChecklist(id: string) {
    return this.request<CoursePublishChecklist>(
      `/courses/by-id/${encodeURIComponent(id)}/publish-checklist`,
    );
  }

  publishCourse(id: string) {
    return this.request<CourseRecord>(`/courses/by-id/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    });
  }

  unpublishCourse(id: string) {
    return this.request<CourseRecord>(`/courses/by-id/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    });
  }

  archiveCourse(id: string) {
    return this.request<CourseRecord>(`/courses/by-id/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    });
  }

  getCourseCurriculum(id: string) {
    return this.request<{ modules: CourseModuleRecord[] }>(
      `/courses/by-id/${encodeURIComponent(id)}/curriculum`,
    );
  }

  createCourseModule(courseId: string, data: { title: string; description?: string }) {
    return this.request<CourseModuleRecord>(
      `/courses/by-id/${encodeURIComponent(courseId)}/modules`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  updateCourseModule(moduleId: string, data: Partial<CourseModuleRecord>) {
    return this.request<CourseModuleRecord>(`/courses/modules/${encodeURIComponent(moduleId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteCourseModule(moduleId: string) {
    return this.request<{ ok: boolean }>(`/courses/modules/${encodeURIComponent(moduleId)}`, {
      method: 'DELETE',
    });
  }

  createCourseLesson(
    moduleId: string,
    data: Partial<CourseLessonRecord> & { title: string; type: string },
  ) {
    return this.request<CourseLessonRecord>(
      `/courses/modules/${encodeURIComponent(moduleId)}/lessons`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  updateCourseLesson(lessonId: string, data: Partial<CourseLessonRecord>) {
    return this.request<CourseLessonRecord>(`/courses/lessons/${encodeURIComponent(lessonId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteCourseLesson(lessonId: string) {
    return this.request<{ ok: boolean }>(`/courses/lessons/${encodeURIComponent(lessonId)}`, {
      method: 'DELETE',
    });
  }

  reorderCourseCurriculum(
    courseId: string,
    modules: Array<{
      id: string;
      sortOrder: number;
      lessons?: Array<{ id: string; sortOrder: number }>;
    }>,
  ) {
    return this.request<{ modules: CourseModuleRecord[] }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/curriculum/reorder`,
      {
        method: 'PUT',
        body: JSON.stringify({ modules }),
      },
    );
  }

  enrollInCourse(slug: string, inviteToken?: string) {
    const query = inviteToken ? `?invite=${encodeURIComponent(inviteToken)}` : '';
    return this.request<{ id: string }>(`/courses/${encodeURIComponent(slug)}/enroll${query}`, {
      method: 'POST',
    });
  }

  createCourseCheckout(
    slug: string,
    data: {
      acceptRefundPolicy: boolean;
      returnUrl?: string;
      couponCode?: string;
      communitySlug?: string;
      inviteToken?: string;
    },
  ) {
    return this.request<CourseCheckoutResponse>(`/courses/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  completeDevCourseCheckout(sessionId: string) {
    return this.request<{ ok: boolean; courseSlug?: string }>(
      '/courses/dev/complete-course-checkout',
      {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      },
    );
  }

  listCoursePurchases(courseId: string) {
    return this.request<{ items: CoursePurchaseRecord[] }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/purchases`,
    );
  }

  refundCoursePurchase(courseId: string, purchaseId: string) {
    return this.request<{ ok: boolean }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/purchases/${encodeURIComponent(purchaseId)}/refund`,
      { method: 'POST' },
    );
  }

  listCourseStudents(
    courseId: string,
    params?: {
      q?: string;
      cursor?: string;
      limit?: number;
      status?: 'active' | 'completed';
    },
  ) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.status) search.set('status', params.status);
    const query = search.toString();
    return this.request<{
      items: CourseStudentRecord[];
      nextCursor: string | null;
      totalLessons: number;
    }>(`/courses/by-id/${encodeURIComponent(courseId)}/students${query ? `?${query}` : ''}`);
  }

  getCourseStudentProgress(courseId: string, userId: string) {
    return this.request<CourseStudentProgressRecord>(
      `/courses/by-id/${encodeURIComponent(courseId)}/students/${encodeURIComponent(userId)}/progress`,
    );
  }

  exportCourseStudentsCsv(courseId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/students/export`,
    );
  }

  grantCourseStudent(courseId: string, data: { email?: string; username?: string }) {
    return this.request<{
      created: boolean;
      enrollment: {
        id: string;
        userId: string;
        accessSource: string;
        enrolledAt: string;
      };
      learner?: {
        email: string;
        username: string | null;
        displayName: string | null;
      };
    }>(`/courses/by-id/${encodeURIComponent(courseId)}/students/grant`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  revokeCourseStudent(courseId: string, enrollmentId: string) {
    return this.request<{ ok: boolean }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/students/${encodeURIComponent(enrollmentId)}`,
      { method: 'DELETE' },
    );
  }

  banCourseStudent(courseId: string, enrollmentId: string, data?: { reason?: string }) {
    return this.request<{ ok: boolean; courseTitle?: string }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/students/${encodeURIComponent(enrollmentId)}/ban`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  getCourseAnalytics(courseId: string) {
    return this.request<CourseAnalyticsResponse>(
      `/courses/by-id/${encodeURIComponent(courseId)}/analytics`,
    );
  }

  exportCourseAnalyticsCsv(courseId: string) {
    return this.request<{ filename: string; csv: string; rowCount: number }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/analytics/export`,
    );
  }

  listCourseLessonComments(lessonId: string, params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit != null) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<{
      items: CourseLessonCommentRecord[];
      nextCursor: string | null;
      maxReplyDepth: number;
    }>(`/courses/lessons/${encodeURIComponent(lessonId)}/comments${query ? `?${query}` : ''}`);
  }

  createCourseLessonComment(lessonId: string, data: { content: string; parentCommentId?: string }) {
    return this.request<CourseLessonCommentRecord>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/comments`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  deleteCourseLessonComment(commentId: string) {
    return this.request<{ ok: boolean }>(
      `/courses/lessons/comments/${encodeURIComponent(commentId)}`,
      { method: 'DELETE' },
    );
  }

  listCourseCoupons(courseId: string) {
    return this.request<{ items: CourseCouponRecord[] }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/coupons`,
    );
  }

  upsertCourseCoupon(
    courseId: string,
    data: {
      id?: string;
      code: string;
      percentOff?: number;
      amountOffCents?: number;
      maxRedemptions?: number;
      expiresAt?: string;
      enabled?: boolean;
    },
  ) {
    return this.request<CourseCouponRecord>(
      `/courses/by-id/${encodeURIComponent(courseId)}/coupons`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  }

  getCourseLessonQuiz(lessonId: string) {
    return this.request<CourseQuizRecord>(`/courses/lessons/${encodeURIComponent(lessonId)}/quiz`);
  }

  upsertCourseLessonQuiz(
    lessonId: string,
    data: Omit<CourseQuizRecord, 'lessonId' | 'lessonTitle'>,
  ) {
    return this.request<CourseQuizRecord>(`/courses/lessons/${encodeURIComponent(lessonId)}/quiz`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  getCourseQuizAnalytics(courseId: string) {
    return this.request<CourseQuizAnalyticsResponse>(
      `/courses/by-id/${encodeURIComponent(courseId)}/quiz-analytics`,
    );
  }

  getCourseQuizTakeView(lessonId: string) {
    return this.request<CourseQuizTakeView>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/quiz/take`,
    );
  }

  submitCourseQuizAttempt(lessonId: string, answers: Record<string, string>) {
    return this.request<CourseQuizSubmitResult>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/quiz/submit`,
      {
        method: 'POST',
        body: JSON.stringify({ answers }),
      },
    );
  }

  getCourseCertificate(slug: string) {
    return this.request<CourseCertificateView>(`/courses/${encodeURIComponent(slug)}/certificate`);
  }

  getCourseCertificateDownload(certificateId: string) {
    return this.request<CourseCertificateDownload>(
      `/courses/certificates/${encodeURIComponent(certificateId)}/download`,
    );
  }

  getCourseLearnView(slug: string) {
    return this.request<{
      course: {
        id: string;
        slug: string | null;
        title: string;
        coverUrl?: string | null;
        isFree?: boolean;
        certificateEnabled?: boolean;
      };
      entitled: boolean;
      accessSource: string | null;
      progressPercent?: number;
      continueLessonId?: string | null;
      completed?: boolean;
      modules: CourseModuleRecord[];
    }>(`/courses/${encodeURIComponent(slug)}/learn`);
  }

  getCourseLesson(lessonId: string) {
    return this.request<CourseLessonRecord & { courseSlug?: string }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}`,
    );
  }

  getCourseBySlug(slug: string, options?: { communitySlug?: string }) {
    const query = options?.communitySlug
      ? `?community=${encodeURIComponent(options.communitySlug)}`
      : '';
    return this.request<CourseRecord>(`/courses/${encodeURIComponent(slug)}${query}`);
  }

  getCreatorStorefrontCourses(username: string) {
    return this.request<{ items: CourseCatalogItem[] }>(
      `/courses/storefront/${encodeURIComponent(username)}`,
    );
  }

  getCourseSeo(slug: string) {
    return this.request<{
      slug: string | null;
      title: string;
      description: string;
      imageUrl: string | null;
    }>(`/courses/meta/${encodeURIComponent(slug)}`);
  }

  getCourseStudentPreview(courseId: string) {
    return this.request<{
      previewMode: boolean;
      course: {
        id: string;
        slug: string | null;
        title: string;
        coverUrl?: string | null;
        isFree?: boolean;
        status: string;
      };
      entitled: boolean;
      accessSource: string;
      modules: CourseModuleRecord[];
    }>(`/courses/by-id/${encodeURIComponent(courseId)}/student-preview`);
  }

  getCourseLessonPreview(lessonId: string) {
    return this.request<CourseLessonRecord & { previewMode?: boolean; courseSlug?: string }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/preview`,
    );
  }

  markCourseLessonComplete(lessonId: string) {
    return this.request<{ ok: boolean }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/complete`,
      {
        method: 'POST',
      },
    );
  }

  updateCourseLessonProgress(lessonId: string, lastPositionSeconds: number) {
    return this.request<{ ok: boolean }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/progress`,
      {
        method: 'PATCH',
        body: JSON.stringify({ lastPositionSeconds }),
      },
    );
  }

  requestCourseLessonVideoUpload(
    lessonId: string,
    data: { mimeType: string; sizeBytes: number; durationSeconds?: number },
  ) {
    return this.request<{ assetId: string; uploadUrl: string; key: string; expiresIn: number }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/video/upload-url`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  confirmCourseLessonVideoUpload(lessonId: string, assetId: string) {
    return this.request<{
      lessonId: string;
      status: string;
      error?: string | null;
      ready: boolean;
      processing: boolean;
      posterUrl?: string | null;
      playbackUrl?: string | null;
    }>(`/courses/lessons/${encodeURIComponent(lessonId)}/video/confirm`, {
      method: 'POST',
      body: JSON.stringify({ assetId }),
    });
  }

  requestCourseLessonCaptionsUpload(
    lessonId: string,
    data: { mimeType: string; sizeBytes: number },
  ) {
    return this.request<{ assetId: string; key: string; uploadUrl: string; expiresIn: number }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/captions/upload-url`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  async uploadCourseLessonCaptions(lessonId: string, file: File) {
    if (file.type !== 'text/vtt' && !file.name.toLowerCase().endsWith('.vtt')) {
      throw new Error('Captions must be a WebVTT (.vtt) file');
    }
    const { assetId, uploadUrl } = await this.requestCourseLessonCaptionsUpload(lessonId, {
      mimeType: 'text/vtt',
      sizeBytes: file.size,
    });
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'text/vtt' },
      body: file,
    });
    if (!response.ok) throw new Error('Captions upload failed');
    await this.request<{ lessonId: string; captionsUploaded: boolean }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/captions/confirm`,
      { method: 'POST', body: JSON.stringify({ assetId }) },
    );
    await this.pollMediaAssetUntilReady(assetId);
    return this.request<{ lessonId: string; captionsUploaded: boolean }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/captions/confirm`,
      { method: 'POST', body: JSON.stringify({ assetId }) },
    );
  }

  requestCoursePromoVideoUpload(
    courseId: string,
    data: { mimeType: string; sizeBytes: number; durationSeconds?: number },
  ) {
    return this.request<{ assetId: string; uploadUrl: string; key: string; expiresIn: number }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/promo-video/upload-url`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  confirmCoursePromoVideoUpload(courseId: string, assetId: string) {
    return this.request<{ promoVideoKey: string; promoVideoUrl: string }>(
      `/courses/by-id/${encodeURIComponent(courseId)}/promo-video/confirm`,
      { method: 'POST', body: JSON.stringify({ assetId }) },
    );
  }

  async uploadCoursePromoVideo(
    courseId: string,
    file: File,
    durationSeconds?: number,
    onProgress?: (pct: number) => void,
  ) {
    const { assetId, uploadUrl } = await this.requestCoursePromoVideoUpload(courseId, {
      mimeType: file.type || 'video/mp4',
      sizeBytes: file.size,
      durationSeconds: durationSeconds ? Math.round(durationSeconds) : undefined,
    });
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('Upload failed'));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
    await this.confirmCoursePromoVideoUpload(courseId, assetId);
    await this.pollMediaAssetUntilReady(assetId);
    return this.confirmCoursePromoVideoUpload(courseId, assetId);
  }

  async uploadCourseLessonVideo(
    lessonId: string,
    file: File,
    durationSeconds?: number,
    onProgress?: (pct: number) => void,
  ) {
    const { assetId, uploadUrl } = await this.requestCourseLessonVideoUpload(lessonId, {
      mimeType: file.type || 'video/mp4',
      sizeBytes: file.size,
      durationSeconds: durationSeconds ? Math.round(durationSeconds) : undefined,
    });

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('Upload failed'));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    await this.confirmCourseLessonVideoUpload(lessonId, assetId);
    await this.pollMediaAssetUntilReady(assetId);
    return this.confirmCourseLessonVideoUpload(lessonId, assetId);
  }

  requestCourseLessonAudioUpload(lessonId: string, data: { mimeType: string; sizeBytes: number }) {
    return this.request<{ assetId: string; uploadUrl: string; key: string; expiresIn: number }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/audio/upload-url`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  confirmCourseLessonAudioUpload(lessonId: string, assetId: string) {
    return this.request<{ lessonId: string; ready: boolean }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/audio/confirm`,
      { method: 'POST', body: JSON.stringify({ assetId }) },
    );
  }

  async uploadCourseLessonAudio(lessonId: string, file: File, onProgress?: (pct: number) => void) {
    const { assetId, uploadUrl } = await this.requestCourseLessonAudioUpload(lessonId, {
      mimeType: file.type || 'audio/mpeg',
      sizeBytes: file.size,
    });
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed'));
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
    await this.confirmCourseLessonAudioUpload(lessonId, assetId);
    await this.pollMediaAssetUntilReady(assetId);
    return this.confirmCourseLessonAudioUpload(lessonId, assetId);
  }

  getCourseLessonAudioPlayback(lessonId: string) {
    return this.request<{ lessonId: string; audioUrl: string }>(
      `/courses/lessons/${encodeURIComponent(lessonId)}/audio/playback`,
    );
  }

  getCourseLessonVideoStatus(lessonId: string) {
    return this.request<{
      lessonId: string;
      status: string;
      error?: string | null;
      durationSeconds?: number | null;
      ready: boolean;
      processing: boolean;
      posterUrl?: string | null;
      playbackUrl?: string | null;
    }>(`/courses/lessons/${encodeURIComponent(lessonId)}/video/status`);
  }

  getCourseLessonPlayback(lessonId: string) {
    return this.request<{
      lessonId: string;
      title: string;
      playbackType: 'hls' | 'mp4';
      manifestUrl?: string | null;
      videoUrl?: string | null;
      posterUrl?: string | null;
      captionsUrl?: string | null;
      playbackSessionToken?: string;
      playbackSessionExpiresIn?: number;
      encrypted?: boolean;
      durationSeconds?: number | null;
      autoCompleteOnVideoEnd?: boolean;
      courseSlug?: string;
    }>(`/courses/lessons/${encodeURIComponent(lessonId)}/playback`);
  }

  getCircleEventCalendar(slug: string) {
    return this.request<CircleEventCalendarResponse>(
      `/events/circles/${encodeURIComponent(slug)}/calendar`,
    );
  }

  listCircleEventPerks(circleId: string) {
    return this.request<{ items: CircleEventPerkRecord[] }>(
      `/events/circles/by-id/${encodeURIComponent(circleId)}/calendar-perks`,
    );
  }

  setCircleEventPerks(
    circleId: string,
    items: Array<{ eventId: string; minTierId?: string | null }>,
  ) {
    return this.request<{ items: CircleEventPerkRecord[] }>(
      `/events/circles/by-id/${encodeURIComponent(circleId)}/calendar-perks`,
      {
        method: 'PUT',
        body: JSON.stringify({ items }),
      },
    );
  }

  listMyEvents() {
    return this.request<CreatorEventRecord[]>('/events/mine');
  }

  getMyEvent(id: string) {
    return this.request<CreatorEventRecord>(`/events/by-id/${encodeURIComponent(id)}`);
  }

  createEvent(data: {
    title: string;
    description?: string;
    startsAt?: string;
    startsAtLocal?: string;
    endsAt?: string;
    endsAtLocal?: string;
    timezone?: string;
    meetingLink?: string;
    location?: string;
    capacity?: number;
    eventType?: 'in_person' | 'virtual' | 'hybrid';
    access?:
      | 'public'
      | 'registered'
      | 'circle_members'
      | 'circle_tier'
      | 'ticket_holders'
      | 'circle_only';
    coverUrl?: string;
    coverKey?: string;
    registrationOpen?: boolean;
  }) {
    return this.request<CreatorEventRecord>('/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateEvent(
    id: string,
    data: {
      title?: string;
      description?: string;
      startsAt?: string;
      startsAtLocal?: string;
      endsAt?: string;
      endsAtLocal?: string;
      timezone?: string;
      meetingLink?: string;
      location?: string;
      capacity?: number;
      eventType?: 'in_person' | 'virtual' | 'hybrid';
      access?:
        | 'public'
        | 'registered'
        | 'circle_members'
        | 'circle_tier'
        | 'ticket_holders'
        | 'circle_only';
      coverUrl?: string;
      coverKey?: string;
      registrationOpen?: boolean;
      hideGuestList?: boolean;
      requiresApproval?: boolean;
      waitlistEnabled?: boolean;
      meetingLinkReveal?: 'always' | 'going' | 'start_window' | 'ticket';
      organizerMessage?: string;
      rruleFreq?: 'daily' | 'weekly' | 'monthly';
      rruleInterval?: number;
      rruleCount?: number;
      rruleUntil?: string;
      ticketPriceCents?: number;
      ticketCurrency?: string;
      refundPolicyText?: string;
      recordingUrl?: string;
      recordingKey?: string;
      announceToCircleFeed?: boolean;
    },
  ) {
    return this.request<CreatorEventRecord>(`/events/by-id/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  publishEvent(id: string) {
    return this.request<CreatorEventRecord>(`/events/by-id/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
    });
  }

  duplicateEvent(id: string) {
    return this.request<CreatorEventRecord>(`/events/by-id/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
    });
  }

  getEventPublishChecklist(id: string) {
    return this.request<{
      ready: boolean;
      items: Array<{ key: string; label: string; passed: boolean }>;
    }>(`/events/by-id/${encodeURIComponent(id)}/publish-checklist`);
  }

  getEventAnalytics(id: string) {
    return this.request<CreatorEventAnalytics>(`/events/by-id/${encodeURIComponent(id)}/analytics`);
  }

  listEventRegistrationQuestions(id: string) {
    return this.request<{ items: EventRegistrationQuestionRecord[] }>(
      `/events/by-id/${encodeURIComponent(id)}/questions`,
    );
  }

  replaceEventRegistrationQuestions(
    id: string,
    questions: Array<{ id?: string; prompt: string; required?: boolean }>,
  ) {
    return this.request<{ items: EventRegistrationQuestionRecord[] }>(
      `/events/by-id/${encodeURIComponent(id)}/questions`,
      { method: 'PUT', body: JSON.stringify({ questions }) },
    );
  }

  listEventSeriesChildren(id: string) {
    return this.request<{
      parentId: string;
      items: Array<{
        id: string;
        slug: string | null;
        title: string;
        startsAt?: string | null;
        endsAt?: string | null;
        status: string;
      }>;
    }>(`/events/by-id/${encodeURIComponent(id)}/series/children`);
  }

  listEventsDiscover(params?: { q?: string; limit?: number; cursor?: string }) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const query = search.toString();
    return this.request<{ items: DiscoverEventListItem[]; nextCursor: string | null }>(
      `/events/discover${query ? `?${query}` : ''}`,
    );
  }

  listCreatorPublicEvents(username: string) {
    return this.request<{
      username: string;
      displayName?: string | null;
      items: DiscoverEventListItem[];
    }>(`/events/creators/${encodeURIComponent(username)}`);
  }

  listEventRsvps(
    id: string,
    query: {
      q?: string;
      status?: CreatorEventRsvpRecord['status'];
      cursor?: string;
      limit?: number;
    } = {},
  ) {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.status) params.set('status', query.status);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit) params.set('limit', String(query.limit));
    const suffix = params.size ? `?${params.toString()}` : '';
    return this.request<{ items: CreatorEventRsvpRecord[]; nextCursor: string | null }>(
      `/events/by-id/${encodeURIComponent(id)}/rsvps${suffix}`,
    );
  }

  exportEventRsvps(
    id: string,
    query: { q?: string; status?: CreatorEventRsvpRecord['status'] } = {},
  ) {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.status) params.set('status', query.status);
    const suffix = params.size ? `?${params.toString()}` : '';
    return this.request<{ filename: string; rowCount: number; csv: string }>(
      `/events/by-id/${encodeURIComponent(id)}/rsvps/export${suffix}`,
    );
  }

  moderateEventRsvp(
    id: string,
    body: { action: 'approve' | 'reject' | 'promote'; userId: string },
  ) {
    return this.request<{ status?: string; promoted?: boolean; userId?: string }>(
      `/events/by-id/${encodeURIComponent(id)}/rsvps/moderate`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  recordEventOrganizerMessage(id: string, message: string) {
    return this.request<{ recorded: boolean; recipientCount: number }>(
      `/events/by-id/${encodeURIComponent(id)}/communications`,
      { method: 'POST', body: JSON.stringify({ message }) },
    );
  }

  cancelEvent(id: string) {
    return this.request<CreatorEventRecord>(`/events/by-id/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    });
  }

  materializeEventSeries(id: string) {
    return this.request<{ materialized: number; created?: number; skipped?: boolean }>(
      `/events/by-id/${encodeURIComponent(id)}/series/materialize`,
      { method: 'POST' },
    );
  }

  updateEventSeriesOccurrence(
    id: string,
    scope: 'this_occurrence' | 'this_and_future' | 'all',
    data: {
      title?: string;
      description?: string;
      startsAt?: string;
      endsAt?: string;
      location?: string;
      meetingLink?: string;
    },
  ) {
    return this.request<CreatorEventRecord>(
      `/events/by-id/${encodeURIComponent(id)}/series/occurrence?scope=${encodeURIComponent(scope)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  checkInEventGuest(
    id: string,
    data: { userId?: string; username?: string; code?: string },
  ) {
    return this.request<{ checkedIn: boolean; userId: string; checkedInAt: string }>(
      `/events/by-id/${encodeURIComponent(id)}/check-in`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  getEventIcsUrl(slug: string) {
    return `${getPublicApiBaseUrl()}/events/${encodeURIComponent(slug)}/ics`;
  }

  getCreatorEventsCalendarIcsUrl(username: string) {
    return `${getPublicApiBaseUrl()}/events/creators/${encodeURIComponent(username)}/calendar.ics`;
  }

  getEventGoogleCalendarLink(slug: string) {
    return this.request<{ url: string }>(`/events/${encodeURIComponent(slug)}/google-calendar`);
  }

  createEventTicketCheckout(
    slug: string,
    input: { acceptRefundPolicy: boolean; returnUrl?: string },
  ) {
    return this.request<EventTicketCheckoutResponse>(
      `/events/${encodeURIComponent(slug)}/ticket-checkout`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  }

  confirmEventTicketCheckout(slug: string, sessionId?: string) {
    return this.request<{ paid: boolean; ticketPaidAt: string; rsvpStatus: string }>(
      `/events/${encodeURIComponent(slug)}/ticket-checkout/confirm`,
      { method: 'POST', body: JSON.stringify({ sessionId }) },
    );
  }

  adminEvents(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<AdminEventRecord>>(`/admin/events${query ? `?${query}` : ''}`);
  }

  adminTakedownEvent(eventId: string, reason?: string) {
    return this.request<CreatorEventRecord>(`/admin/events/${encodeURIComponent(eventId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  adminMarketingKillSwitch() {
    return this.request<AdminMarketingKillSwitchState>('/admin/marketing/kill-switch');
  }

  adminSetMarketingKillSwitch(enabled: boolean) {
    return this.request<{ enabled: boolean; updatedAt: string }>('/admin/marketing/kill-switch', {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  adminMarketingDeliverabilityOverview() {
    return this.request<AdminMarketingDeliverabilityOverview>('/admin/marketing/deliverability/overview');
  }

  adminMarketingCreatorDeliverability(params?: { limit?: number }) {
    const search = new URLSearchParams();
    if (params?.limit != null) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<{ items: AdminCreatorDeliverabilityRecord[] }>(
      `/admin/marketing/deliverability/creators${qs ? `?${qs}` : ''}`,
    );
  }

  adminSetCreatorMarketingSuspension(
    creatorId: string,
    input: { suspended: boolean; reason?: string },
  ) {
    return this.request<AdminCreatorMarketingSuspensionState>(
      `/admin/marketing/creators/${encodeURIComponent(creatorId)}/suspension`,
      {
        method: 'PATCH',
        body: JSON.stringify(input),
      },
    );
  }

  platformSettings() {
    return this.request<PlatformPublicSettings>('/platform/settings');
  }

  adminPlatformSettings() {
    return this.request<PlatformAdminSettings>('/admin/platform-settings');
  }

  adminUpdatePlatformSettings(data: {
    maintenanceMode?: boolean;
    maintenanceMessage?: string | null;
    signupEnabled?: boolean;
  }) {
    return this.request<PlatformAdminSettings>('/admin/platform-settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getEventBySlug(slug: string) {
    return this.request<CreatorEventRecord | Record<string, unknown>>(
      `/events/${encodeURIComponent(slug)}`,
    );
  }

  setEventRsvp(
    slug: string,
    status: 'going' | 'maybe' | 'not_going' | 'waitlisted' = 'going',
    meta?: {
      utmSource?: string;
      utmMedium?: string;
      utmCampaign?: string;
      answers?: Array<{ questionId: string; answer: string }>;
    },
  ) {
    return this.request<Record<string, unknown>>(`/events/${encodeURIComponent(slug)}/rsvp`, {
      method: 'POST',
      body: JSON.stringify({
        status,
        utmSource: meta?.utmSource,
        utmMedium: meta?.utmMedium,
        utmCampaign: meta?.utmCampaign,
        answers: meta?.answers,
      }),
    });
  }

  clearEventRsvp(slug: string) {
    return this.request<{ cleared: boolean }>(`/events/${encodeURIComponent(slug)}/rsvp`, {
      method: 'DELETE',
    });
  }

  updateCommunity(
    id: string,
    data: {
      title?: string;
      about?: string;
      rules?: string;
      coverUrl?: string | null;
      coverKey?: string | null;
      logoUrl?: string | null;
      logoKey?: string | null;
      slug?: string;
      joinMode?: 'open' | 'invite_only';
      listingVisibility?: 'listed' | 'unlisted' | 'secret';
      adultContentEnabled?: boolean;
      minimumAgeYears?: number;
      seoTitle?: string | null;
      seoDescription?: string | null;
      seoCanonicalPath?: string | null;
      seoIndexable?: boolean;
      brandPrimaryColor?: string | null;
      brandAccentColor?: string | null;
      brandSecondaryColor?: string | null;
      memberMessagingMode?: 'owner_mod_only' | 'members_allowed';
      digestSchedule?: 'inherit' | 'daily' | 'weekly' | 'off';
    },
  ) {
    return this.request<CommunityRecord>(`/circles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  publishCommunity(id: string, data?: { slug?: string }) {
    return this.request<CommunityRecord>(`/circles/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  unpublishCommunity(id: string) {
    return this.request<CommunityRecord>(`/circles/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    });
  }

  archiveCommunity(id: string) {
    return this.request<CommunityRecord>(`/circles/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    });
  }

  deleteArticle(id: string) {
    return this.request<{ ok: boolean }>(`/content/articles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  recordArticleView(articleId: string) {
    return this.request<{ ok: boolean }>(
      `/content/articles/${encodeURIComponent(articleId)}/view`,
      {
        method: 'POST',
      },
    );
  }

  getArticleStats() {
    return this.request<{
      totals: { published: number; views: number; likes: number; comments: number };
      topArticles: Array<{
        id: string;
        title: string;
        slug: string | null;
        coverUrl: string | null;
        readTimeMinutes: number;
        publishedAt: string;
        views: number;
        likes: number;
        comments: number;
      }>;
    }>('/content/articles/stats');
  }

  createPost(data: {
    content: string;
    status?: string;
    context?: string;
    type?: string;
    linkUrl?: string;
    linkPreviewData?: Record<string, unknown>;
    mediaKey?: string;
    mediaUrl?: string;
    mediaUrls?: string[];
    pollData?: { options: string[]; expiresAt?: string };
    quotePostId?: string;
    scheduledFor?: string | null;
  }) {
    return this.request('/content/posts', { method: 'POST', body: JSON.stringify(data) });
  }

  isPostReshared(postId: string) {
    return this.request<{ reshared: boolean; resharePostId: string | null }>(
      `/content/posts/${encodeURIComponent(postId)}/reshared`,
    );
  }

  resharePost(postId: string) {
    return this.request(`/content/posts/${encodeURIComponent(postId)}/reshare`, { method: 'POST' });
  }

  getMyPosts(status?: 'draft' | 'published') {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/content/posts/mine${query}`);
  }

  getMyPost(id: string) {
    return this.request(`/content/posts/mine/${id}`);
  }

  updatePost(
    id: string,
    data: {
      content?: string;
      context?: string;
      type?: string;
      linkUrl?: string;
      linkPreviewData?: Record<string, unknown>;
      mediaUrl?: string;
      mediaUrls?: string[];
      pollData?: { options: string[]; expiresAt?: string };
      status?: string;
      scheduledFor?: string | null;
    },
  ) {
    return this.request(`/content/posts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deletePost(id: string) {
    return this.request(`/content/posts/${id}`, { method: 'DELETE' });
  }

  getPoll(postId: string) {
    return this.request<{
      options: string[];
      counts: number[];
      totalVotes: number;
      userVote: number | null;
      closed?: boolean;
      expiresAt?: string | null;
    }>(`/engagement/posts/${postId}/poll`);
  }

  getPollForUser(postId: string) {
    return this.request<{
      options: string[];
      counts: number[];
      totalVotes: number;
      userVote: number | null;
      closed?: boolean;
      expiresAt?: string | null;
    }>(`/engagement/posts/${postId}/poll/me`);
  }

  votePoll(postId: string, optionIndex: number) {
    return this.request(`/engagement/posts/${postId}/poll/vote`, {
      method: 'POST',
      body: JSON.stringify({ optionIndex }),
    });
  }

  publishPost(id: string) {
    return this.request(`/content/posts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'published' }),
    });
  }

  getSubscription() {
    return this.request('/billing/subscription');
  }

  adminUsers(params?: { q?: string; status?: string; limit?: number; offset?: number }) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.status) search.set('status', params.status);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return this.request<{
      items: unknown[];
      total: number;
      limit: number;
      offset: number;
    }>(`/admin/users${qs ? `?${qs}` : ''}`);
  }

  adminContent(params?: { cursor?: string; limit?: number; type?: 'article' | 'post' | 'all' }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.type) search.set('type', params.type);
    const query = search.toString();
    return this.request<CursorPage<unknown>>(`/admin/content${query ? `?${query}` : ''}`);
  }

  adminCommunities(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<AdminCommunityRecord>>(
      `/admin/circles${query ? `?${query}` : ''}`,
    );
  }

  adminCommunityConnectHealth(communityId: string) {
    return this.request<AdminCommunityConnectHealthResponse>(
      `/admin/circles/${encodeURIComponent(communityId)}/connect-health`,
    );
  }

  adminTakedownCommunity(communityId: string, data?: { reason?: string }) {
    return this.request(`/admin/circles/${encodeURIComponent(communityId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminCourses(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<AdminCourseRecord>>(`/admin/courses${query ? `?${query}` : ''}`);
  }

  adminTakedownCourse(courseId: string, data?: { reason?: string }) {
    return this.request(`/admin/courses/${encodeURIComponent(courseId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminCreatorSites(params?: { cursor?: string; limit?: number; status?: 'published' | 'draft' }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.status) search.set('status', params.status);
    const query = search.toString();
    return this.request<CursorPage<AdminCreatorSiteRecord>>(
      `/admin/creator-sites${query ? `?${query}` : ''}`,
    );
  }

  adminTakedownCreatorSite(siteId: string, data?: { reason?: string }) {
    return this.request(`/admin/creator-sites/${encodeURIComponent(siteId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminMemberships(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<AdminMembershipRecord>>(
      `/admin/memberships${query ? `?${query}` : ''}`,
    );
  }

  adminMembershipConnectHealth(membershipId: string) {
    return this.request<AdminMembershipConnectHealthResponse>(
      `/admin/memberships/${encodeURIComponent(membershipId)}/connect-health`,
    );
  }

  adminTakedownMembership(membershipId: string, data?: { reason?: string }) {
    return this.request(`/admin/memberships/${encodeURIComponent(membershipId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminServiceOffers(params?: { cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<AdminServiceOfferRecord>>(
      `/admin/offers${query ? `?${query}` : ''}`,
    );
  }

  adminTakedownServiceOffer(offerId: string, data?: { reason?: string }) {
    return this.request(`/admin/offers/${encodeURIComponent(offerId)}/takedown`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  updateAdminCommunityFeatured(
    communityId: string,
    data: { featured: boolean; featuredOrder?: number; featuredUntil?: string | null },
  ) {
    return this.request(`/admin/circles/${encodeURIComponent(communityId)}/featured`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  adminReports(params?: {
    cursor?: string;
    limit?: number;
    targetType?: string;
    targetId?: string;
  }) {
    const search = new URLSearchParams();
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.targetType) search.set('targetType', params.targetType);
    if (params?.targetId) search.set('targetId', params.targetId);
    const query = search.toString();
    return this.request<CursorPage<AdminOpenReportRecord>>(`/admin/reports${query ? `?${query}` : ''}`);
  }

  adminUpdateReport(id: string, status: string) {
    return this.request(`/admin/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  adminLegalRequests(params?: {
    status?: LegalComplianceRequestRecord['status'];
    type?: LegalComplianceRequestRecord['type'];
    cursor?: string;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.type) search.set('type', params.type);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<LegalComplianceRequestRecord>>(
      `/admin/legal-requests${query ? `?${query}` : ''}`,
    );
  }

  adminUpdateLegalRequest(
    id: string,
    data: {
      status: 'reviewing' | 'action_taken' | 'rejected' | 'restored' | 'closed';
      adminNotes?: string;
    },
  ) {
    return this.request<{ request: LegalComplianceRequestRecord }>(
      `/admin/legal-requests/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  submitSupportRequest(data: {
    category: PlatformSupportRequestRecord['category'];
    subject: string;
    message: string;
    screenshotAssetId?: string;
  }) {
    return this.request<{ request: PlatformSupportRequestRecord }>('/support/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  submitAccountBanAppeal(data: { email: string; password: string; message: string }) {
    return this.request<PlatformModerationAppealRecord>('/platform/appeals/account-ban', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getAccountBanAppealStatus(data: { email: string; password: string }) {
    return this.request<{ appeal: PlatformModerationAppealRecord | null }>(
      '/platform/appeals/account-ban/status',
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  submitCreatorDisabledAppeal(message: string) {
    return this.request<PlatformModerationAppealRecord>('/platform/appeals/creator-disabled', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  getMyPlatformAppeals() {
    return this.request<{ items: PlatformModerationAppealRecord[] }>('/platform/appeals/me');
  }

  adminSupportRequests(params?: {
    status?: PlatformSupportRequestRecord['status'];
    category?: PlatformSupportRequestRecord['category'];
    cursor?: string;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.category) search.set('category', params.category);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<PlatformSupportRequestRecord>>(
      `/admin/support-requests${query ? `?${query}` : ''}`,
    );
  }

  adminUpdateSupportRequest(
    id: string,
    data: {
      status?: PlatformSupportRequestRecord['status'];
      adminNotes?: string;
    },
  ) {
    return this.request<{ request: PlatformSupportRequestRecord }>(
      `/admin/support-requests/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  adminPlatformAppeals(params?: {
    status?: PlatformModerationAppealRecord['status'];
    cursor?: string;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<CursorPage<PlatformModerationAppealRecord>>(
      `/admin/appeals${query ? `?${query}` : ''}`,
    );
  }

  adminResolvePlatformAppeal(
    id: string,
    data: { decision: 'approved' | 'denied'; resolutionNote?: string },
  ) {
    return this.request<PlatformModerationAppealRecord>(`/admin/appeals/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  adminHoneypotEvents(params?: { limit?: number; cursor?: string }) {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const query = search.toString();
    return this.request<CursorPage<HoneypotTripRecord>>(
      `/admin/security/honeypot-events${query ? `?${query}` : ''}`,
    );
  }

  adminUnblockIp(ip: string) {
    return this.request<{ ok: boolean }>(`/admin/security/blocked-ips/${encodeURIComponent(ip)}`, {
      method: 'DELETE',
    });
  }

  adminSubscriptions(params?: { q?: string; status?: string; cursor?: string; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    if (params?.status) search.set('status', params.status);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request(`/admin/billing/subscriptions${query ? `?${query}` : ''}`);
  }

  adminBillingMetrics() {
    return this.request('/admin/billing/metrics');
  }

  adminBillingHealth() {
    return this.request('/admin/billing/health');
  }

  adminBillingWebhookEvents(params?: { limit?: number; status?: string; cursor?: string }) {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.status) search.set('status', params.status);
    if (params?.cursor) search.set('cursor', params.cursor);
    return this.request<CursorPage<unknown>>(`/admin/billing/webhook-events?${search.toString()}`);
  }

  adminBillingWebhookEvent(id: string) {
    return this.request(`/admin/billing/webhook-events/${id}`);
  }

  adminRetryBillingWebhook(id: string) {
    return this.request(`/admin/billing/webhook-events/${id}/retry`, { method: 'POST' });
  }

  adminSyncBillingSubscription(userId: string) {
    return this.request(`/admin/billing/subscriptions/${userId}/sync`, { method: 'POST' });
  }

  adminBillingSubscriptionRefunds(subscriptionId: string) {
    return this.request<{ items: AdminSubscriptionRefundRecord[] }>(
      `/admin/billing/subscriptions/${encodeURIComponent(subscriptionId)}/refunds`,
    );
  }

  adminCommerceDisputesOverview() {
    return this.request<AdminCommerceDisputesOverview>('/admin/commerce/disputes/overview');
  }

  adminCommerceDisputes(params?: {
    status?: 'open' | 'lost' | 'won' | 'closed';
    creatorId?: string;
    q?: string;
    cursor?: string;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.creatorId) search.set('creatorId', params.creatorId);
    if (params?.q) search.set('q', params.q);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit != null) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<{
      items: AdminCommerceDisputeRecord[];
      pageInfo: { nextCursor: string | null; hasMore: boolean };
    }>(`/admin/commerce/disputes${query ? `?${query}` : ''}`);
  }

  adminCommerceDispute(disputeId: string) {
    return this.request<AdminCommerceDisputeDetail>(
      `/admin/commerce/disputes/${encodeURIComponent(disputeId)}`,
    );
  }

  adminBillingPlans(params?: { page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<NumberedPage<unknown>>(`/admin/billing/plans${query ? `?${query}` : ''}`);
  }

  adminCreateBillingPlan(data: Record<string, unknown>) {
    return this.request('/admin/billing/plans', { method: 'POST', body: JSON.stringify(data) });
  }

  adminImportBillingPlan(data: Record<string, unknown>) {
    return this.request('/admin/billing/plans/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminSyncBillingCatalog(data?: Record<string, unknown>) {
    return this.request('/admin/billing/catalog/sync', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminBillingCatalogDrift(productId?: string) {
    const query = productId ? `?productId=${encodeURIComponent(productId)}` : '';
    return this.request(`/admin/billing/catalog/drift${query}`);
  }

  adminStripeCatalog(params?: { productId?: string; page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.productId) search.set('productId', params.productId);
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<NumberedPage<unknown>>(
      `/admin/billing/stripe/catalog${query ? `?${query}` : ''}`,
    );
  }

  adminUpdateBillingPlan(id: string, data: Record<string, unknown>) {
    return this.request(`/admin/billing/plans/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  adminReplaceBillingPrice(id: string, data: { amountCents: number; currency: string }) {
    return this.request(`/admin/billing/plans/${id}/replace-price`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminBillingPromotions(params?: { page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<NumberedPage<unknown>>(
      `/admin/billing/promotions${query ? `?${query}` : ''}`,
    );
  }

  adminCreateBillingPromotion(data: Record<string, unknown>) {
    return this.request('/admin/billing/promotions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminUpdateBillingPromotion(id: string, data: Record<string, unknown>) {
    return this.request(`/admin/billing/promotions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  adminMetrics() {
    return this.request('/admin/metrics');
  }

  adminDiscoveryMetrics(windowDays: 7 | 30 = 7) {
    return this.request<DiscoveryAdminMetrics>(`/admin/discovery/metrics?windowDays=${windowDays}`);
  }

  adminNotificationDeliveries(status?: string, limit?: number, cursor?: string, channel?: string) {
    const search = new URLSearchParams();
    if (status) search.set('status', status);
    if (channel) search.set('channel', channel);
    if (limit) search.set('limit', String(limit));
    if (cursor) search.set('cursor', cursor);
    const qs = search.toString();
    return this.request<CursorPage<unknown>>(
      `/admin/notifications/deliveries${qs ? `?${qs}` : ''}`,
    );
  }

  adminNotificationMetrics() {
    return this.request<{
      byStatus: Record<string, number>;
      failedLast24h: number;
      suppressionsCount: number;
    }>('/admin/notifications/metrics');
  }

  adminUserNotificationPrefs(params: { email?: string; userId?: string }) {
    const search = new URLSearchParams();
    if (params.email) search.set('email', params.email);
    if (params.userId) search.set('userId', params.userId);
    const qs = search.toString();
    return this.request<{
      userId: string;
      email: string;
      username: string | null;
      displayName: string | null;
      notificationPrefs: unknown;
    }>(`/admin/notifications/user-prefs${qs ? `?${qs}` : ''}`);
  }

  adminMuteUserNotifications(userId: string, muted: boolean) {
    return this.request('/admin/notifications/mute-user', {
      method: 'POST',
      body: JSON.stringify({ userId, muted }),
    });
  }

  adminRetryNotificationDelivery(id: string) {
    return this.request<{ ok: boolean }>(`/admin/notifications/deliveries/${id}/retry`, {
      method: 'POST',
    });
  }

  adminNotificationSuppressions(params?: { limit?: number; cursor?: string }) {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const qs = search.toString();
    return this.request<CursorPage<unknown>>(
      `/admin/notifications/suppressions${qs ? `?${qs}` : ''}`,
    );
  }

  adminAddEmailSuppression(email: string, reason?: string) {
    return this.request('/admin/notifications/suppressions', {
      method: 'POST',
      body: JSON.stringify({ email, reason }),
    });
  }

  adminRemoveEmailSuppression(id: string) {
    return this.request(`/admin/notifications/suppressions/${id}`, { method: 'DELETE' });
  }

  adminInAppNotifications(params?: {
    userId?: string;
    email?: string;
    cursor?: string;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.userId) search.set('userId', params.userId);
    if (params?.email) search.set('email', params.email);
    if (params?.cursor) search.set('cursor', params.cursor);
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<CursorPage<unknown>>(`/admin/notifications/in-app${qs ? `?${qs}` : ''}`);
  }

  adminMailTemplates(params?: {
    category?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.category) search.set('category', params.category);
    if (params?.status) search.set('status', params.status);
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<NumberedPage<unknown>>(`/admin/mail-templates${qs ? `?${qs}` : ''}`);
  }

  adminMailTemplate(id: string) {
    return this.request<unknown>(`/admin/mail-templates/${id}`);
  }

  adminCreateMailTemplate(data: {
    key: string;
    name: string;
    description?: string;
    subject: string;
    htmlBody: string;
    textBody: string;
  }) {
    return this.request('/admin/mail-templates', { method: 'POST', body: JSON.stringify(data) });
  }

  adminUpdateMailTemplate(
    id: string,
    data: {
      name?: string;
      description?: string;
      subject?: string;
      htmlBody?: string;
      textBody?: string;
      changeNote?: string;
    },
  ) {
    return this.request(`/admin/mail-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  adminPublishMailTemplate(
    id: string,
    data?: { changeNote?: string; subject?: string; htmlBody?: string; textBody?: string },
  ) {
    return this.request(`/admin/mail-templates/${id}/publish`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  adminPreviewMailTemplate(
    id: string,
    data: {
      variables: Record<string, unknown>;
      subject?: string;
      htmlBody?: string;
      textBody?: string;
    },
  ) {
    return this.request(`/admin/mail-templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminTestSendMailTemplate(
    id: string,
    data: {
      variables: Record<string, unknown>;
      toEmail?: string;
      subject?: string;
      htmlBody?: string;
      textBody?: string;
    },
  ) {
    return this.request(`/admin/mail-templates/${id}/test-send`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminArchiveMailTemplate(id: string) {
    return this.request(`/admin/mail-templates/${id}/archive`, { method: 'POST' });
  }

  adminResetMailTemplateDefaults(id: string) {
    return this.request(`/admin/mail-templates/${id}/reset-defaults`, { method: 'POST' });
  }

  adminMailTemplateRevisions(id: string, params?: { page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.page) search.set('page', String(params.page));
    if (params?.limit) search.set('limit', String(params.limit));
    const query = search.toString();
    return this.request<NumberedPage<unknown>>(
      `/admin/mail-templates/${id}/revisions${query ? `?${query}` : ''}`,
    );
  }

  adminRollbackMailTemplate(id: string, revisionId: string, changeNote?: string) {
    return this.request(`/admin/mail-templates/${id}/revisions/${revisionId}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ changeNote }),
    });
  }

  adminSendPlatformEmail(data: {
    templateKey: string;
    variables: Record<string, unknown>;
    audience: 'test' | 'user_ids' | 'all_users';
    testEmail?: string;
    userIds?: string[];
    confirmed?: boolean;
    subject?: string;
    htmlBody?: string;
    textBody?: string;
  }) {
    return this.request('/admin/platform-emails/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  adminPlatformEmailAudienceEstimate(
    audience: 'test' | 'user_ids' | 'all_users',
    userIds?: string[],
  ) {
    const params = new URLSearchParams({ audience });
    if (userIds?.length) params.set('userIds', userIds.join(','));
    return this.request<{ audience: string; recipientCount: number }>(
      `/admin/platform-emails/audience-estimate?${params.toString()}`,
    );
  }

  adminPlatformEmailSends(params?: { limit?: number; cursor?: string }) {
    const search = new URLSearchParams();
    if (params?.limit) search.set('limit', String(params.limit));
    if (params?.cursor) search.set('cursor', params.cursor);
    const query = search.toString();
    return this.request<CursorPage<unknown>>(
      `/admin/platform-emails/sends${query ? `?${query}` : ''}`,
    );
  }

  getFeatures() {
    return this.request<{ features: Record<string, boolean> }>('/platform/features');
  }

  adminListPlatformFeatures() {
    return this.request<{
      features: Array<{
        key: string;
        name: string;
        description: string;
        category: string;
        stage: string;
        visibility: string;
        creatorOverrideable: boolean;
        audienceScopable: boolean;
        defaultState: boolean;
        effectiveEnabled: boolean;
        memberEffectiveEnabled: boolean | null;
        creatorEffectiveEnabled: boolean | null;
        platformOverride: boolean | null;
        memberOverride: boolean | null;
        creatorOverride: boolean | null;
        envOverride: boolean | null;
        envVarName: string;
        source: string;
      }>;
    }>('/admin/platform/features');
  }

  adminUpdatePlatformFeature(
    featureKey: string,
    input: { enabled?: boolean; memberEnabled?: boolean; creatorEnabled?: boolean },
  ) {
    return this.request(`/admin/platform/features/${encodeURIComponent(featureKey)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  adminClearPlatformFeature(featureKey: string) {
    return this.request(`/admin/platform/features/${encodeURIComponent(featureKey)}`, {
      method: 'DELETE',
    });
  }

  adminListOfferTypeCapabilities() {
    return this.request<{
      offersModuleEnabled: boolean;
      offersEnvLocked: boolean;
      types: AdminOfferTypeCapabilityRow[];
    }>('/admin/platform/features/offers/types');
  }

  adminUpdateOfferTypeCapability(offerType: string, input: { enabled: boolean }) {
    return this.request(`/admin/platform/features/offers/types/${encodeURIComponent(offerType)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  }

  adminClearOfferTypeCapability(offerType: string) {
    return this.request(`/admin/platform/features/offers/types/${encodeURIComponent(offerType)}`, {
      method: 'DELETE',
    });
  }

  getCreatorModuleAccess() {
    return this.request<{
      creator: {
        id: string;
        userId: string;
        email: string;
        setupStatus: string;
        disabled: boolean;
        eligible: boolean;
      };
      features: Array<{
        key: string;
        name: string;
        description: string;
        category: string;
        stage: string;
        moduleProfile?: string;
        recordCount?: number;
        studioAccessState?: 'available' | 'suspended' | 'hidden';
        platformEnabled: boolean;
        envOverride: boolean | null;
        envVarName: string;
        override: {
          id: string;
          mode: 'grant' | 'deny';
          reason: string | null;
          expiresAt: string | null;
          isExpired: boolean;
          updatedAt: string;
          createdBy: { id: string; email: string };
        } | null;
        effectiveEnabled: boolean;
        source: string;
      }>;
      offerTypeCapabilities?: Record<string, boolean>;
    }>('/creator/module-access');
  }

  adminListCreatorModuleAccess(creatorId: string) {
    return this.request<{
      creator: {
        id: string;
        userId: string;
        email: string;
        setupStatus: string;
        disabled: boolean;
        eligible: boolean;
      };
      features: Array<{
        key: string;
        name: string;
        description: string;
        category: string;
        stage: string;
        moduleProfile?: string;
        recordCount?: number;
        studioAccessState?: 'available' | 'suspended' | 'hidden';
        platformEnabled: boolean;
        envOverride: boolean | null;
        envVarName: string;
        override: {
          id: string;
          mode: 'grant' | 'deny';
          reason: string | null;
          expiresAt: string | null;
          isExpired: boolean;
          updatedAt: string;
          createdBy: { id: string; email: string };
        } | null;
        effectiveEnabled: boolean;
        source: string;
      }>;
    }>(`/admin/creators/${encodeURIComponent(creatorId)}/module-access`);
  }

  adminUpsertCreatorModuleAccess(
    creatorId: string,
    featureKey: string,
    data: { mode: 'grant' | 'deny'; reason?: string; expiresAt?: string | null },
  ) {
    return this.request(
      `/admin/creators/${encodeURIComponent(creatorId)}/module-access/${encodeURIComponent(featureKey)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    );
  }

  adminRemoveCreatorModuleAccess(creatorId: string, featureKey: string) {
    return this.request(
      `/admin/creators/${encodeURIComponent(creatorId)}/module-access/${encodeURIComponent(featureKey)}`,
      { method: 'DELETE' },
    );
  }

  moderatePost(id: string, status: string) {
    return this.request(`/admin/content/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  getMyMembership() {
    return this.request<MembershipMineResponse>('/memberships/mine');
  }

  createMyMembership(data?: { title?: string }) {
    return this.request<{ membership: MembershipRecord; created: boolean }>('/memberships/mine', {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  updateMyMembership(data: { title?: string; description?: string | null; slug?: string }) {
    return this.request<MembershipRecord>('/memberships/mine', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  getMembershipOverview() {
    return this.getMyMembership().then((data) => ({
      membership: data.membership,
      metrics: {
        mrrCents: data.overview?.mrrCents ?? 0,
        activeMembers: data.overview?.activeMembers ?? 0,
        paidActiveMembers: data.overview?.paidActiveMembers ?? 0,
        publishedTiers: data.overview?.publishedTierCount ?? 0,
        currency: data.overview?.currency ?? 'usd',
        tierBreakdown: data.overview?.tierBreakdown ?? [],
        connectReady: data.overview?.connectReady ?? false,
      },
      publicUrl: data.membership ? `/memberships/${data.membership.slug}` : '',
    }));
  }

  getMembershipGoLiveChecklist() {
    return this.request<{
      ready: boolean;
      canGoLive: boolean;
      items: Array<{ id: string; label: string; ok: boolean; required: boolean }>;
    }>('/memberships/mine/go-live-checklist');
  }

  goLiveMembership() {
    return this.request<MembershipRecord>('/memberships/mine/go-live', { method: 'POST' });
  }

  updateMembershipPage(data: Partial<MembershipRecord>) {
    return this.request<MembershipRecord>('/memberships/mine/page', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  updateMembershipSettings(data: {
    publicButtonLabel?: string;
    title?: string;
    description?: string;
    hidden?: boolean;
    refundPolicyText?: string;
  }) {
    return this.request<MembershipRecord>('/memberships/mine/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  listMembershipTiers() {
    return this.request<{ items: MembershipTierRecord[] }>('/memberships/mine/tiers').then(
      (r) => r.items,
    );
  }

  createMembershipTier(data: {
    name: string;
    description?: string;
    billingType: 'free' | 'paid';
    monthlyPriceCents?: number;
    currency?: string;
    perks?: Array<{ title: string; description?: string; manualLink?: string }>;
  }) {
    return this.request<MembershipTierRecord>('/memberships/mine/tiers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateMembershipTier(
    tierId: string,
    data: {
      name?: string;
      description?: string;
      monthlyPriceCents?: number;
      currency?: string;
      perks?: Array<{ title: string; description?: string; manualLink?: string }>;
    },
  ) {
    return this.request<MembershipTierRecord>(
      `/memberships/mine/tiers/${encodeURIComponent(tierId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
    );
  }

  archiveMembershipTier(tierId: string) {
    return this.request<{ tier: MembershipTierRecord; activeMemberCount: number }>(
      `/memberships/mine/tiers/${encodeURIComponent(tierId)}`,
      { method: 'DELETE' },
    );
  }

  restoreMembershipTier(tierId: string) {
    return this.request<{ tier: MembershipTierRecord; activeMemberCount: number }>(
      `/memberships/mine/tiers/${encodeURIComponent(tierId)}/restore`,
      { method: 'POST' },
    );
  }

  listMembershipPosts() {
    return this.request<{ items: MembershipPostRecord[] }>('/memberships/mine/posts').then(
      (r) => r.items,
    );
  }

  createMembershipPost(data: Partial<MembershipPostRecord>) {
    return this.request<MembershipPostRecord>('/memberships/mine/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateMembershipPost(postId: string, data: Partial<MembershipPostRecord>) {
    return this.request<MembershipPostRecord>(
      `/memberships/mine/posts/${encodeURIComponent(postId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  publishMembershipPost(postId: string) {
    return this.request<MembershipPostRecord>(
      `/memberships/mine/posts/${encodeURIComponent(postId)}/publish`,
      { method: 'POST' },
    );
  }

  unpublishMembershipPost(postId: string) {
    return this.request<MembershipPostRecord>(
      `/memberships/mine/posts/${encodeURIComponent(postId)}/unpublish`,
      { method: 'POST' },
    );
  }

  deleteMembershipPost(postId: string) {
    return this.request<{ ok: boolean }>(`/memberships/mine/posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE',
    });
  }

  listMembershipMembers(params?: {
    status?: 'all' | 'active' | 'past_due' | 'cancelled';
    tierId?: string;
    q?: string;
    cursor?: string;
    limit?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'all') {
      query.set('status', params.status);
    }
    if (params?.tierId) query.set('tierId', params.tierId);
    if (params?.q) query.set('q', params.q);
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString() ? `?${query.toString()}` : '';
    return this.request<{ items: MembershipMemberRecord[]; nextCursor?: string | null }>(
      `/memberships/mine/members${qs}`,
    );
  }

  listMembershipAuditLog(limit?: number) {
    const suffix = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return this.request<{
      items: Array<{
        id: string;
        actionType: string;
        targetId?: string | null;
        metadata?: unknown;
        createdAt: string;
        actor?: {
          id: string;
          profile?: { username?: string | null; displayName?: string | null };
        };
      }>;
    }>(`/memberships/mine/audit-log${suffix}`);
  }

  exportMembershipMembers(params?: {
    status?: 'all' | 'active' | 'past_due' | 'cancelled';
    tierId?: string;
    q?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.status && params.status !== 'all') {
      query.set('status', params.status);
    }
    if (params?.tierId) query.set('tierId', params.tierId);
    if (params?.q?.trim()) query.set('q', params.q.trim());
    const qs = query.toString() ? `?${query.toString()}` : '';
    return this.request<{ csv: string; count: number; filename?: string }>(
      `/memberships/mine/members/export${qs}`,
    );
  }

  grantMembershipMember(data: {
    tierId: string;
    userId?: string;
    email?: string;
    username?: string;
  }) {
    return this.request<{
      ok: boolean;
      created: boolean;
      subscriptionId: string;
      member?: {
        email: string;
        username?: string | null;
        displayName?: string | null;
      };
    }>('/memberships/mine/members/grant', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  revokeMembershipMember(subscriptionId: string) {
    return this.request<{
      ok: boolean;
      hadStripeSubscription?: boolean;
      billingType?: string;
    }>(`/memberships/mine/members/${encodeURIComponent(subscriptionId)}/revoke`, {
      method: 'POST',
    });
  }

  refundMembershipMember(subscriptionId: string) {
    return this.request<{ ok: boolean; refundId?: string }>(
      `/memberships/mine/members/${encodeURIComponent(subscriptionId)}/refund`,
      { method: 'POST' },
    );
  }

  resendMembershipFulfillment(subscriptionId: string, perkIndex?: number) {
    return this.request<{ ok: boolean; fulfillments: MembershipFulfillmentRecord[] }>(
      `/memberships/mine/members/${encodeURIComponent(subscriptionId)}/resend-fulfillment`,
      {
        method: 'POST',
        body: JSON.stringify(perkIndex == null ? {} : { perkIndex }),
      },
    );
  }

  getMembershipBySlug(slug: string) {
    return this.request<MembershipPublicPageResponse>(
      `/memberships/by-slug/${encodeURIComponent(slug)}`,
    );
  }

  getMembershipStorefront(username: string) {
    return this.request<MembershipStorefront>(
      `/memberships/storefront/${encodeURIComponent(username)}`,
    );
  }

  joinMembership(slug: string, tierId: string) {
    return this.request<{ subscription: unknown; entitled: boolean }>(
      `/memberships/${encodeURIComponent(slug)}/join`,
      { method: 'POST', body: JSON.stringify({ tierId }) },
    );
  }

  checkoutMembership(
    slug: string,
    data: { tierId: string; returnUrl?: string; acceptRefundPolicy?: boolean },
  ) {
    return this.request<{
      sessionId: string;
      clientSecret: string;
      publishableKey: string;
      stripeAccountId: string;
      devStub?: boolean;
      flow?: string;
    }>(`/memberships/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  completeDevMembershipCheckout(sessionId: string) {
    return this.request<{ ok: boolean; membershipSlug?: string }>(
      '/memberships/dev/complete-checkout',
      { method: 'POST', body: JSON.stringify({ sessionId }) },
    );
  }

  createMembershipBillingPortal(
    slug: string,
    data?: { returnUrl?: string; flow?: 'manage' | 'cancel' },
  ) {
    return this.request<{ url: string }>(
      `/memberships/${encodeURIComponent(slug)}/billing-portal`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  cancelMembership(slug: string, data?: { returnUrl?: string }) {
    return this.request<{ url: string } | { ok: boolean }>(
      `/memberships/${encodeURIComponent(slug)}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      },
    );
  }

  // ——— Services / Offer Catalog ———

  listServiceOffers(params?: { type?: string; status?: string }) {
    const search = new URLSearchParams();
    if (params?.type) search.set('type', params.type);
    if (params?.status) search.set('status', params.status);
    const qs = search.toString();
    return this.request<{ items: ServiceOfferRecord[] }>(`/services/offers${qs ? `?${qs}` : ''}`);
  }

  createServiceOffer(data: {
    type: 'digital' | 'one_to_one' | 'priority_dm' | 'webinar' | 'cohort' | 'package';
    title: string;
    shortDescription?: string;
    priceCents?: number;
    durationMinutes?: number;
    webinarStartsAt?: string;
  }) {
    return this.request<ServiceOfferRecord>('/services/offers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getServiceOffer(offerId: string) {
    return this.request<ServiceOfferRecord>(`/services/offers/${encodeURIComponent(offerId)}`);
  }

  getServiceOfferPublishReadiness(offerId: string) {
    return this.request<{ ready: boolean; errors: string[]; warnings: string[] }>(
      `/services/offers/${encodeURIComponent(offerId)}/publish-readiness`,
    );
  }

  updateServiceOffer(offerId: string, data: Partial<ServiceOfferRecord>) {
    return this.request<ServiceOfferRecord>(`/services/offers/${encodeURIComponent(offerId)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  updateServiceDigitalContent(
    offerId: string,
    data: {
      fileKey?: string;
      fileName?: string;
      fileMimeType?: string;
      externalUrl?: string;
      buyerInstructions?: string;
    },
  ) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/digital`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  publishServiceOffer(offerId: string) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/publish`,
      { method: 'POST' },
    );
  }

  archiveServiceOffer(offerId: string) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/archive`,
      { method: 'POST' },
    );
  }

  duplicateServiceOffer(offerId: string) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/duplicate`,
      { method: 'POST' },
    );
  }

  getPublicServiceOffer(slug: string) {
    return this.request<PublicServiceOfferRecord>(`/offers/${encodeURIComponent(slug)}`);
  }

  listCreatorPublicOffers(username: string) {
    return this.request<{ items: ServiceOfferCard[] }>(
      `/creators/${encodeURIComponent(username)}/offers`,
    );
  }

  listCreatorPublicCatalog(username: string) {
    return this.request<{ items: PublicCatalogItem[] }>(
      `/creators/${encodeURIComponent(username)}/catalog`,
    );
  }

  listStudioCatalog() {
    return this.request<{ items: StudioCatalogItem[] }>('/services/catalog');
  }

  createServiceCheckout(
    slug: string,
    data?: {
      returnUrl?: string;
      bookingId?: string;
      couponCode?: string;
      referralCode?: string;
      affiliateCode?: string;
      intakeAnswers?: Record<string, string>;
    },
  ) {
    return this.request<ServiceCheckoutSession>(`/offers/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  }

  confirmFreeServiceOffer(
    slug: string,
    data?: { bookingId?: string; intakeAnswers?: Record<string, string> },
  ) {
    return this.request<{ ok: boolean; offerSlug: string; bookingId?: string; requestId?: string }>(
      `/offers/${encodeURIComponent(slug)}/confirm-free`,
      { method: 'POST', body: JSON.stringify(data ?? {}) },
    );
  }

  listServiceBookingSlots(slug: string, params: { from: string; to: string; timezone?: string }) {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.timezone) qs.set('timezone', params.timezone);
    return this.request<{
      timezone: string;
      slots: ServiceBookingSlot[];
      maxAdvanceBookingDays?: number;
      minNoticeMinutes?: number;
      calendarSyncFailed?: boolean;
    }>(`/offers/${encodeURIComponent(slug)}/slots?${qs.toString()}`);
  }

  holdServiceBookingSlot(slug: string, data: { startsAt: string; timezone?: string }) {
    return this.request<ServiceBookingHold>(`/offers/${encodeURIComponent(slug)}/hold`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getAccountBookings() {
    return this.request<{ items: AccountBookingItem[] }>('/account/bookings');
  }

  cancelAccountBooking(bookingId: string) {
    return this.request<{ ok: boolean }>(
      `/account/bookings/${encodeURIComponent(bookingId)}/cancel`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  rescheduleAccountBooking(bookingId: string, data: { startsAt: string; timezone?: string }) {
    return this.request<{ ok: boolean; bookingId: string }>(
      `/account/bookings/${encodeURIComponent(bookingId)}/reschedule`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  listCreatorServiceBookings(params?: { status?: 'upcoming' | 'past' | 'cancelled' }) {
    const qs = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return this.request<{ items: CreatorServiceBookingItem[] }>(`/services/bookings${qs}`);
  }

  cancelCreatorServiceBooking(bookingId: string) {
    return this.request<{ ok: boolean }>(
      `/services/bookings/${encodeURIComponent(bookingId)}/cancel`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  completeCreatorServiceBooking(bookingId: string) {
    return this.request<{ ok: boolean }>(
      `/services/bookings/${encodeURIComponent(bookingId)}/complete`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  markCreatorServiceBookingNoShow(bookingId: string) {
    return this.request<{ ok: boolean }>(
      `/services/bookings/${encodeURIComponent(bookingId)}/no-show`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  completeDevServiceCheckout(sessionId: string) {
    return this.request<{ ok: boolean; offerSlug: string; bookingId?: string; requestId?: string }>(
      '/services/dev/complete-service-checkout',
      { method: 'POST', body: JSON.stringify({ sessionId }) },
    );
  }

  getServiceEntitlementDownload(offerId: string) {
    return this.request<ServiceDownloadAccess>(
      `/services/entitlements/${encodeURIComponent(offerId)}/download`,
    );
  }

  getAccountOverview() {
    return this.request<AccountOverview>('/account/overview');
  }

  getAccountLibrary() {
    return this.request<{ items: AccountLibraryItem[] }>('/account/library');
  }

  getAccountOrders(params?: {
    search?: string;
    stream?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.stream) qs.set('stream', params.stream);
    if (params?.status) qs.set('status', params.status);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<{ items: AccountOrderItem[] }>(`/account/orders${suffix}`);
  }

  getAccountSubscriptions() {
    return this.request<{ items: AccountSubscriptionItem[] }>('/account/subscriptions');
  }

  getServiceCatalogOverview(days = 30) {
    return this.request<ServiceCatalogOverview>(`/services/overview?days=${days}`);
  }

  listServiceCreatorOrders(offerId?: string) {
    const qs = offerId ? `?offerId=${encodeURIComponent(offerId)}` : '';
    return this.request<{ items: ServiceCreatorOrderRecord[] }>(`/services/orders${qs}`);
  }

  refundServiceOrder(orderId: string) {
    return this.request<{ ok: boolean; orderId: string }>(
      `/services/orders/${encodeURIComponent(orderId)}/refund`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  listServiceCoupons(offerId: string) {
    return this.request<{ items: ServiceCouponRecord[] }>(
      `/services/offers/${encodeURIComponent(offerId)}/coupons`,
    );
  }

  upsertServiceCoupon(
    offerId: string,
    data: {
      id?: string;
      code: string;
      percentOff?: number;
      amountOffCents?: number;
      maxRedemptions?: number;
      expiresAt?: string;
      enabled?: boolean;
    },
  ) {
    return this.request<ServiceCouponRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/coupons`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  listServiceOfferAudit(offerId: string, limit = 50) {
    return this.request<{ items: ServiceAuditRecord[] }>(
      `/services/offers/${encodeURIComponent(offerId)}/audit?limit=${limit}`,
    );
  }

  trackServiceOfferEvent(slug: string, event: 'view' | 'cta') {
    return this.request<{ ok: boolean }>(`/offers/${encodeURIComponent(slug)}/track`, {
      method: 'POST',
      body: JSON.stringify({ event }),
    });
  }

  getServiceOfferAnalytics(offerId: string) {
    return this.request<{
      offer: { id: string; title: string; slug: string; type: string };
      rangeDays: number;
      funnel: {
        views: number;
        ctaClicks: number;
        checkoutStarted: number;
        paid: number;
        viewToPaidRate: number;
        ctaToPaidRate: number;
      };
    }>(`/services/offers/${encodeURIComponent(offerId)}/analytics`);
  }

  listIntegrations() {
    return this.request<{ items: IntegrationStatusRecord[] }>('/integrations/status');
  }

  connectIntegration(provider: string, returnUrl?: string) {
    return this.request<{ url: string; testMode: boolean }>(
      `/integrations/${encodeURIComponent(provider)}/connect`,
      {
        method: 'POST',
        body: JSON.stringify({ returnUrl }),
      },
    );
  }

  disconnectIntegration(provider: string) {
    return this.request<{ ok: boolean }>(
      `/integrations/${encodeURIComponent(provider)}/disconnect`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  refreshIntegration(provider: string) {
    return this.request<{ ok: boolean; email?: string | null; accountLabel?: string | null }>(
      `/integrations/${encodeURIComponent(provider)}/refresh`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  listGoogleCalendars() {
    return this.request<{ items: GoogleCalendarListItem[] }>('/integrations/google_calendar/calendars');
  }

  updateGoogleCalendarSettings(data: { writeCalendarId?: string; busyCalendarIds?: string[] }) {
    return this.request<{
      writeCalendarId?: string | null;
      busyCalendarIds?: string[] | null;
      status: string;
      externalAccountId?: string | null;
    }>('/integrations/google_calendar/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getCreatorSchedulingProfile() {
    return this.request<CreatorSchedulingProfileRecord>('/services/scheduling/profile');
  }

  updateCreatorSchedulingProfile(data: {
    timezone: string;
    minNoticeMinutes: number;
    maxAdvanceBookingDays: number;
    reschedulePolicyText?: string;
  }) {
    return this.request<CreatorSchedulingProfileRecord>('/services/scheduling/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  listCreatorAvailabilitySchedules() {
    return this.request<{ items: CreatorAvailabilityScheduleRecord[] }>(
      '/services/scheduling/schedules',
    );
  }

  createCreatorAvailabilitySchedule(data: { name: string }) {
    return this.request<CreatorAvailabilityScheduleRecord>('/services/scheduling/schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateCreatorAvailabilitySchedule(scheduleId: string, data: { name?: string }) {
    return this.request<CreatorAvailabilityScheduleRecord>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  deleteCreatorAvailabilitySchedule(scheduleId: string) {
    return this.request<{ ok: boolean }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}`,
      { method: 'DELETE' },
    );
  }

  getCreatorScheduleWindows(scheduleId: string) {
    return this.request<{ timezone: string; items: ServiceAvailabilityWindowRecord[] }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}/windows`,
    );
  }

  replaceCreatorScheduleWindows(
    scheduleId: string,
    data: {
      timezone: string;
      windows: Array<{
        dayOfWeek: number;
        startMinute: number;
        endMinute: number;
        timezone: string;
      }>;
    },
  ) {
    return this.request<{ timezone: string; items: ServiceAvailabilityWindowRecord[] }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}/windows`,
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  listCreatorScheduleBlocks(scheduleId: string) {
    return this.request<{ items: CreatorAvailabilityBlockRecord[] }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}/blocks`,
    );
  }

  createCreatorScheduleBlock(scheduleId: string, data: { date: string; label?: string }) {
    return this.request<CreatorAvailabilityBlockRecord & { warning?: string | null }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}/blocks`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  deleteCreatorScheduleBlock(scheduleId: string, blockId: string) {
    return this.request<{ ok: boolean }>(
      `/services/scheduling/schedules/${encodeURIComponent(scheduleId)}/blocks/${encodeURIComponent(blockId)}`,
      { method: 'DELETE' },
    );
  }

  updateServiceOneToOneConfig(offerId: string, data: Partial<ServiceOneToOneConfigRecord>) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/one-to-one`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  replaceOfferAvailability(
    offerId: string,
    windows: Array<{
      dayOfWeek: number;
      startMinute: number;
      endMinute: number;
      timezone: string;
    }>,
  ) {
    return this.request<{ items: ServiceAvailabilityWindowRecord[] }>(
      `/services/offers/${encodeURIComponent(offerId)}/availability`,
      { method: 'PUT', body: JSON.stringify({ windows }) },
    );
  }

  getOfferAvailability(offerId: string) {
    return this.request<{ items: ServiceAvailabilityWindowRecord[] }>(
      `/services/offers/${encodeURIComponent(offerId)}/availability`,
    );
  }

  getCreatorDefaultAvailability() {
    return this.request<{ timezone: string; items: ServiceAvailabilityWindowRecord[] }>(
      '/services/scheduling/default-availability',
    );
  }

  replaceCreatorDefaultAvailability(data: {
    timezone: string;
    windows: Array<{
      dayOfWeek: number;
      startMinute: number;
      endMinute: number;
      timezone: string;
    }>;
  }) {
    return this.request<{ timezone: string; items: ServiceAvailabilityWindowRecord[] }>(
      '/services/scheduling/default-availability',
      { method: 'PUT', body: JSON.stringify(data) },
    );
  }

  updateServicePriorityDmConfig(offerId: string, data: Partial<ServicePriorityDmConfigRecord>) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/priority-dm`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  getOfferPriorityDmCompose(slug: string) {
    return this.request<{
      id: string;
      status: string;
      offer: {
        id: string;
        slug: string;
        title: string;
        priorityDmConfig?: ServicePriorityDmConfigRecord | null;
      };
    }>(`/offers/${encodeURIComponent(slug)}/priority-dm/compose`);
  }

  submitPriorityDmRequest(
    requestId: string,
    data: { message: string; intakeAnswers?: Record<string, string> },
  ) {
    return this.request<{
      ok: boolean;
      requestId: string;
      conversationId: string;
      slaDueAt: string;
    }>(`/services/priority-dm/requests/${encodeURIComponent(requestId)}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  getAccountPriorityDmRequests() {
    return this.request<{ items: ServicePriorityDmRequestRecord[] }>('/account/priority-dm');
  }

  listPriorityDmQueue(params?: { status?: 'open' | 'answered' | 'all' }) {
    const qs = params?.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return this.request<{ items: ServicePriorityDmRequestRecord[] }>(
      `/services/priority-dm/queue${qs}`,
    );
  }

  fulfillPriorityDmRequest(requestId: string, data: { message: string }) {
    return this.request<{ ok: boolean; requestId: string }>(
      `/services/priority-dm/requests/${encodeURIComponent(requestId)}/fulfill`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }

  declinePriorityDmRequest(requestId: string) {
    return this.request<{ ok: boolean; requestId: string; refundPending: boolean }>(
      `/services/priority-dm/requests/${encodeURIComponent(requestId)}/decline`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  updateServiceWebinarConfig(offerId: string, data: Partial<ServiceWebinarConfigRecord>) {
    return this.request<ServiceWebinarConfigRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/webinar`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  listWebinarRegistrants(offerId: string) {
    return this.request<{
      items: Array<{
        id: string;
        registeredAt: string;
        buyer: { email: string; username?: string | null; displayName?: string | null };
      }>;
    }>(`/services/webinars/${encodeURIComponent(offerId)}/registrants`);
  }

  publishWebinarReplay(offerId: string, data?: { recordingUrl?: string }) {
    return this.request<ServiceWebinarConfigRecord>(
      `/services/webinars/${encodeURIComponent(offerId)}/replay/publish`,
      { method: 'POST', body: JSON.stringify(data ?? {}) },
    );
  }

  markWebinarAttendance(registrationId: string, status: 'attended' | 'no_show') {
    return this.request<{ id: string; status: string }>(
      `/services/webinars/registrations/${encodeURIComponent(registrationId)}/attendance`,
      { method: 'POST', body: JSON.stringify({ status }) },
    );
  }

  updateServiceCohortConfig(
    offerId: string,
    data: Partial<ServiceCohortConfigRecord> & {
      sessions?: Array<{
        sessionIndex: number;
        title?: string;
        startsAt: string;
        durationMinutes: number;
        timezone?: string;
        meetingProvider?: ServiceCohortSessionRecord['meetingProvider'];
        manualMeetingUrl?: string;
      }>;
    },
  ) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/cohort`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  listCohortEnrollees(offerId: string) {
    return this.request<{
      items: Array<{
        id: string;
        enrolledAt: string;
        buyer: { email: string; username?: string | null; displayName?: string | null };
      }>;
    }>(`/services/cohorts/${encodeURIComponent(offerId)}/enrollees`);
  }

  publishCohortSessionRecording(offerId: string, sessionId: string, recordingUrl: string) {
    return this.request<ServiceCohortSessionRecord>(
      `/services/cohorts/${encodeURIComponent(offerId)}/sessions/${encodeURIComponent(sessionId)}/replay/publish`,
      { method: 'POST', body: JSON.stringify({ recordingUrl }) },
    );
  }

  updateServicePackageConfig(
    offerId: string,
    data: {
      lineItems: Array<{
        includedOfferId?: string;
        includedCourseId?: string;
        includedMembershipTierId?: string;
        includedEventId?: string;
        quantity?: number;
        sortOrder?: number;
      }>;
    },
  ) {
    return this.request<ServiceOfferRecord>(
      `/services/offers/${encodeURIComponent(offerId)}/package`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  }

  listPackageCandidates() {
    return this.request<{ items: PackageCandidateItem[] }>('/services/packages/candidates');
  }

  listBookingCredits(offerId?: string) {
    const qs = offerId ? `?offerId=${encodeURIComponent(offerId)}` : '';
    return this.request<{
      items: Array<{
        id: string;
        targetOfferId: string;
        expiresAt: string;
        sourcePackageOfferId?: string | null;
        offer: { id: string; slug: string; title: string; type: string };
      }>;
    }>(`/account/booking-credits${qs}`);
  }

  redeemBookingCredit(slug: string, data: { bookingId: string; creditId: string }) {
    return this.request<{ ok: boolean; bookingId: string }>(
      `/offers/${encodeURIComponent(slug)}/redeem-credit`,
      { method: 'POST', body: JSON.stringify(data) },
    );
  }
}

export const api = new ApiClient();
